import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Copy, Check, Users, Shield, BarChart2, CheckCircle, Activity, ScrollText, ShieldCheck, ShieldOff, Key, Eye, EyeOff, X, Search, CreditCard, Lock, AlertTriangle, Trash2, RefreshCw, Loader2, FolderTree, BookOpen, Pencil, ChevronDown, ChevronRight, ToggleLeft, ToggleRight, Server, Upload, Play, ArrowDownCircle, UserPlus, Terminal, Cpu, Link2 } from 'lucide-react';
import { useAuth, API } from '../App';
import { useImpersonation } from '../contexts/ImpersonationContext';
import Header from '../components/layout/Header';
import ModelRosterAdmin from '../components/admin/ModelRosterAdmin';
// #740: reuse the polished AuditSection for the platform admin audit log tab
// (workspaceId={null} = platform scope) instead of maintaining a parallel
// renderer.
import AuditSection from '../components/enterprise/AuditSection';
// #1137 Task 4.1: Impersonate confirm modal — reusable across AdminPanel
// (platform scope) and EnterpriseAdmin (workspace scope, Task 4.2).
import ImpersonateConfirmModal from '../components/ImpersonateConfirmModal';

/**
 * #1239 — Collapsible group header for the OR-workspace grouping on
 * the customer Users tab. Click anywhere on the bar to toggle. The
 * chevron rotates to indicate state (▼ expanded, ▶ collapsed).
 *
 * #1242 polish: stronger visual separation from user rows. Tinted
 * Plasma Core background + left-side accent stripe + extra top
 * margin so groups read as discrete containers rather than another
 * row. Pre-fix the only difference was a one-step luminance bump
 * which blended at a glance.
 */
function OrWorkspaceGroupHeader({ label, count, collapsed, onToggle, hint, testIdSuffix }) {
  return (
    <div className="mt-3 first:mt-0">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 py-2.5 px-3 bg-[#FF4D00]/10 border border-[#FF4D00]/30 border-l-4 border-l-[#FF4D00] hover:bg-[#FF4D00]/15 hover:border-[#FF4D00]/50 transition-colors text-left"
        data-testid={testIdSuffix
          ? `or-ws-group-${testIdSuffix}`
          : `or-ws-group-${label.replace(/\s+/g, '-').toLowerCase()}`}
        aria-expanded={!collapsed}
      >
        <span
          className="text-[#FF4D00] font-mono text-xs w-3 inline-block transition-transform"
          style={{ transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)' }}
          aria-hidden="true"
        >
          ▶
        </span>
        <span className="text-xs font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#F5F1E8]">
          {label}
        </span>
        <span className="text-[10px] font-mono text-[#FF4D00]/70">({count})</span>
      </button>
      {/* #1248: optional hint text rendered just below the bar when
          expanded. Used by the 'Development & Testing' section to
          explain the scope of accounts that live there. */}
      {hint && !collapsed && (
        <p className="text-[10px] font-mono text-[#A0A0A0] italic mt-1 px-3">
          {hint}
        </p>
      )}
    </div>
  );
}

/**
 * #1220 — Section header used inside the Fray Users tab to split the
 * Fray Staff list from the Testing Users list. Visually distinct from
 * the row cards so the divide is obvious; styled in the Forge Tension
 * accent palette.
 */
function SectionDivider({ title, count, hint, testId }) {
  return (
    <div
      className="pt-3 pb-2 border-t border-[#2A2A2A] first:border-t-0 first:pt-0"
      data-testid={testId}
    >
      <div className="flex items-baseline gap-2">
        <span className="text-xs font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#F5F1E8]">
          {title}
        </span>
        <span className="text-[10px] font-mono text-[#A0A0A0]">({count})</span>
      </div>
      {hint && (
        <p className="text-[10px] font-mono text-[#A0A0A0] italic mt-0.5">
          {hint}
        </p>
      )}
    </div>
  );
}

export default function AdminPanel() {
  const { user } = useAuth();
  const [tab, setTab] = useState('invites');
  const [invites, setInvites] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [observatory, setObservatory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [emailHint, setEmailHint] = useState('');
  const [copied, setCopied] = useState('');
  const [inviteType, setInviteType] = useState('self_serve');
  const [inviteTypeDefaults, setInviteTypeDefaults] = useState({});
  const [inviteCredits, setInviteCredits] = useState(0);
  const [inviteDomain, setInviteDomain] = useState('');
  const [inviteExpiry, setInviteExpiry] = useState('');
  const [inviteMetrics, setInviteMetrics] = useState(null);
  const [inviteFilter, setInviteFilter] = useState('all');
  const [selectedInvites, setSelectedInvites] = useState(new Set());
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState(null); // { total, completed, failed, current, results }
  const [bulkPlanChanging, setBulkPlanChanging] = useState(false);
  const [bulkPlan, setBulkPlan] = useState('free');
  const [userFilter, setUserFilter] = useState('all');
  const [resetModal, setResetModal] = useState(null); // user object or null
  const [resetPassword, setResetPassword] = useState('');
  const [resetForceChange, setResetForceChange] = useState(true);
  const [resetAutoGen, setResetAutoGen] = useState(true);
  const [resetResult, setResetResult] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);

  // Add User modal state
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ email: '', display_name: '', password: '', plan: 'pro', role: 'user', is_investor: false });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState('');
  const [addUserResult, setAddUserResult] = useState(null);
  const [showAddUserPw, setShowAddUserPw] = useState(false);

  // User detail drawer state
  const [selectedUser, setSelectedUser] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerData, setDrawerData] = useState(null);

  // Test data counts (#367)
  const [testCounts, setTestCounts] = useState({ test_users: 0, test_invites: 0 });
  const [deletingTestData, setDeletingTestData] = useState(null);

  // User search state
  const [userSearch, setUserSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(userSearch), 300);
    return () => clearTimeout(timer);
  }, [userSearch]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invRes, userRes, statsRes] = await Promise.all([
        API.get('/api/admin/invites'),
        API.get('/api/admin/users'),
        API.get('/api/admin/stats'),
      ]);
      setInvites(invRes.data.invites || []);
      setUsers(userRes.data.users || []);
      setStats(statsRes.data);
      API.get('/api/admin/observatory').then(r => setObservatory(r.data)).catch(() => {});
      API.get('/api/admin/invite-types').then(r => setInviteTypeDefaults(r.data.types || {})).catch(() => {});
      API.get('/api/admin/invite-metrics').then(r => setInviteMetrics(r.data)).catch(() => {});
      API.get('/api/admin/test-counts').then(r => setTestCounts(r.data)).catch(() => {});
    } catch (err) {
      console.error('Admin load error:', err);
    }
    setLoading(false);
  };

  const createInvite = async () => {
    setCreating(true);
    const defaults = inviteTypeDefaults[inviteType] || {};
    try {
      await API.post('/api/admin/invites', {
        email_hint: emailHint,
        invite_type_slug: inviteType,
        plan_override: defaults.plan || 'pro',
        billing_mode: defaults.billing_mode || 'byok',
        max_uses: 1,
        credits_allocated: inviteCredits || defaults.credits || 0,
        domain_restriction: inviteDomain || undefined,
        expires_days: inviteExpiry ? parseInt(inviteExpiry) : (defaults.expiry_days || undefined),
      });
      setEmailHint('');
      setInviteCredits(0);
      setInviteDomain('');
      setInviteExpiry('');
      await loadData();
    } catch (err) {
      console.error(err);
    }
    setCreating(false);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(''), 2000);
  };

  const copyInviteLink = (code) => {
    const link = `${window.location.origin}/signup?invite=${code}`;
    navigator.clipboard.writeText(link);
    setCopied(`link:${code}`);
    setTimeout(() => setCopied(''), 2000);
  };

  const createUser = async () => {
    setAddUserLoading(true);
    setAddUserError('');
    try {
      const res = await API.post('/api/admin/users/create', addUserForm);
      setAddUserResult(res.data);
      await loadData();
    } catch (err) {
      setAddUserError(err.response?.data?.detail || 'Failed to create user');
    }
    setAddUserLoading(false);
  };

  const openUserDrawer = async (u) => {
    setSelectedUser(u);
    setDrawerLoading(true);
    setDrawerData(null);
    try {
      const res = await API.get(`/api/admin/users/${u.id}`);
      setDrawerData(res.data);
    } catch (err) {
      console.error('Load user detail error:', err);
      setDrawerData({ error: true });
    }
    setDrawerLoading(false);
  };

  const closeDrawer = useCallback(() => {
    setSelectedUser(null);
    setDrawerData(null);
  }, []);

  // Escape key handler for drawer
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape' && selectedUser) closeDrawer(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [selectedUser, closeDrawer]);

  // #661: Fray staff predicate — role is fray_*, plan is fray, or investor flag set
  const isFrayStaff = (u) =>
    ['fray_admin', 'fray_super_admin'].includes(u.role) ||
    u.plan === 'fray' ||
    u.is_investor === true;

  // #1220 / #1246: non-human-account predicate. Matches BOTH:
  //   - test accounts: Playwright bypass (#1160), security audits, MCP
  //     integration tests, anything *@fray.test (RFC 6761 reserved TLD)
  //   - automation/system accounts: Claude Code (cc@agents.fray),
  //     Emergent Deploy Pipeline (emergent@pipeline.fray), background
  //     workers (system@fray)
  //
  // Internal email convention: the made-up `.fray` TLD (or bare `@fray`
  // domain) is used exclusively for non-human principals in this codebase.
  // Function name kept as `isTestUser` for backward-compat with the
  // existing #1219 + #1220 call sites; the section it routes to is now
  // 'Development & Testing' (#1246) reflecting the broader scope.
  const isTestUser = (u) => {
    const email = (u.email || '').toLowerCase();
    if (email.endsWith('@fray.test')) return true;
    // #1246: internal-account email TLD (.fray) and bare @fray domain
    if (email.endsWith('.fray')) return true;
    if (email.endsWith('@fray')) return true;
    if (u.auth_2fa_bypass === true) return true;
    if (u.is_test_account === true) return true;
    // Pattern-match the prefixes our automation uses. Conservative — if a
    // real customer happens to email starting "playwright-" we'd mis-flag
    // them, but that's improbable in practice and the impact is cosmetic
    // (they show up in the wrong section, not deleted/blocked).
    const TEST_PREFIXES = [
      'playwright-', 'sec-audit-', 'cp-fix-', 'suspend-fix-',
      'int-test-', 'mcp-integration', 'test-', 'qa-',
    ];
    return TEST_PREFIXES.some((p) => email.startsWith(p));
  };

  // #1239: infer the OR workspace label for a user from their plan +
  // workspace context. Plan-derived (no OR API roundtrip) so this is
  // O(N) at render time and reflects the EXPECTED state after #1235 +
  // #1238 propagate. During the migration window (users who haven't
  // re-provisioned since #1238 deployed), the OR-side reality may
  // differ — that's a separate v2 enhancement (query OR API to mark
  // un-migrated users with a warning badge).
  //
  // Returns a stable group label string. Used as both display text
  // and grouping key, so two users with the same label group together.
  const inferOrWorkspaceLabel = (u) => {
    if (isFrayStaff(u)) return 'Fray Admin';
    const plan = (u.plan || 'free').toLowerCase();
    if (plan === 'enterprise') {
      // Enterprise users belong to a workspace; the per-customer Fray OR
      // workspace lives inside that customer's OR account. Label it by
      // the workspace name so multiple enterprise customers don't all
      // collapse under one heading.
      const wsLabel = u.workspace_name || u.workspace_id || 'Enterprise (unknown)';
      return `${wsLabel} → Fray workspace`;
    }
    // Fray-managed plans → tier workspaces (per #1238 mapping)
    const tierMap = {
      pro: 'Fray | tier | Pro',
      free: 'Fray | tier | Free',
      student: 'Fray | tier | Student',
      team: 'Fray | tier | Team',
    };
    return tierMap[plan] || `Fray | plan | ${plan}`;
  };

  // Filtered users for search. Tab-scoped pre-filter:
  //   - users tab           → self-serve tiers only (free/student/pro/team).
  //                           Excludes Fray staff (#1219), test users (#1220),
  //                           AND enterprise (#1244 — those go to enterprise-users tab)
  //   - enterprise-users tab → enterprise plan only, excluding Fray staff + test
  //   - fray-users tab      → Fray staff + test users; section split at render
  const filteredUsers = useMemo(() => {
    let result = users;
    if (tab === 'fray-users') {
      result = result.filter((u) => isFrayStaff(u) || isTestUser(u));
    } else if (tab === 'enterprise-users') {
      // #1244: enterprise customers only — excludes Fray staff (who may
      // happen to have enterprise plan) and test users
      result = result.filter(
        (u) => (u.plan || '').toLowerCase() === 'enterprise'
               && !isFrayStaff(u) && !isTestUser(u)
      );
    } else if (tab === 'users') {
      // #1219 + #1220 + #1244: self-serve tiers only on Users tab.
      // Enterprise customers live on the enterprise-users tab now.
      result = result.filter(
        (u) => !isFrayStaff(u) && !isTestUser(u)
               && (u.plan || '').toLowerCase() !== 'enterprise'
      );
    }
    if (userFilter === 'provisioned') result = result.filter(u => u.provisioned_plan || u.provisioned_by);
    else if (userFilter === 'byok') result = result.filter(u => u.billing_mode === 'byok');
    else if (userFilter === 'admin') result = result.filter(u => ['admin', 'fray_admin', 'super_admin', 'fray_super_admin'].includes(u.role));
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(u =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.id || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [users, userFilter, debouncedSearch, tab]);

  // #1220: split for the Fray Users tab two-section render. Stable refs
  // for the two halves derived from filteredUsers.
  const frayStaffUsers = useMemo(
    () => (tab === 'fray-users' ? filteredUsers.filter((u) => !isTestUser(u)) : []),
    [filteredUsers, tab]
  );
  const testUsersOnFrayTab = useMemo(
    () => (tab === 'fray-users' ? filteredUsers.filter((u) => isTestUser(u)) : []),
    [filteredUsers, tab]
  );

  // #1239 + #1244: Users tab grouped by self-serve tier. ALL tier
  // groups are always shown — empty tiers display "(0)" so admin sees
  // the complete subscription-tier structure (matches the
  // /admin → Tiers source-of-truth table). Order matches the Tiers
  // table: Free, Student, Pro, Team.
  const SELF_SERVE_TIERS = useMemo(() => [
    { plan: 'free',    label: 'Free' },
    { plan: 'student', label: 'Student' },
    { plan: 'pro',     label: 'Pro' },
    { plan: 'team',    label: 'Team' },
  ], []);

  const usersByTier = useMemo(() => {
    if (tab !== 'users') return [];
    // Initialize with empty arrays for ALL tiers so empty groups still
    // appear in the UI.
    const groups = Object.fromEntries(
      SELF_SERVE_TIERS.map(({ label }) => [label, []])
    );
    for (const u of filteredUsers) {
      const plan = (u.plan || 'free').toLowerCase();
      const tier = SELF_SERVE_TIERS.find((t) => t.plan === plan);
      if (tier) groups[tier.label].push(u);
      // Unknown plans (shouldn't happen post-filter) silently dropped
    }
    return SELF_SERVE_TIERS.map(({ label }) => [label, groups[label]]);
  }, [filteredUsers, tab, SELF_SERVE_TIERS]);

  // #1244: Enterprise Users tab grouped by customer workspace. Only
  // shows workspaces that have at least one user (don't enumerate
  // every workspace that exists). Label = '<Workspace Name> → Fray
  // workspace' matching the existing #1239 enterprise convention.
  const enterpriseUsersByWorkspace = useMemo(() => {
    if (tab !== 'enterprise-users') return [];
    const groups = {};
    for (const u of filteredUsers) {
      const wsName = u.workspace_name || u.workspace_id || 'Enterprise (no workspace)';
      const label = `${wsName} → Fray workspace`;
      if (!groups[label]) groups[label] = [];
      groups[label].push(u);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredUsers, tab]);

  // #1239: collapsed-group state. Default = all expanded. Toggle on
  // chevron click. Keyed by group label so adding/removing groups
  // (e.g. when filters change) doesn't lose unrelated state.
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const toggleGroup = useCallback((label) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  if (!['fray_admin', 'fray_super_admin'].includes(user?.role)) {
    return (
      <div className="min-h-screen bg-[#0F0F0F]">
        <Header />
        <div className="pt-24 text-center text-[#A0A0A0]">Admin access required</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-6 max-w-5xl mx-auto">
        {/* #1218: Sticky header — stats cards + tab nav stay pinned while
            the user list / tab content scrolls underneath. The Header
            component above is fixed-position and ~80px tall, so this
            sticky element sits below it. The bg fills the gap during
            scroll so list rows don't bleed through. */}
        <div
          className="sticky top-0 z-20 bg-[#0F0F0F] -mx-6 px-6 pb-2"
          data-testid="admin-sticky-header"
        >
          <h1 className="text-2xl font-bold font-['Barlow_Condensed'] mb-6 pt-2" data-testid="admin-title">Admin Panel</h1>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
              {[
                { label: 'Users', value: stats.total_users, icon: Users },
                { label: 'Total Frays', value: stats.total_frays, icon: BarChart2 },
                { label: 'Completed', value: stats.completed_frays, icon: CheckCircle },
                { label: 'Active Invites', value: stats.active_invites, icon: Shield },
              ].map((s, i) => (
                <div key={i} className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                  <s.icon className="w-4 h-4 text-[#FF4D00] mb-2" />
                  <span className="text-2xl font-bold font-['Barlow_Condensed'] block">{s.value}</span>
                  <span className="text-xs text-[#A0A0A0] font-mono">{s.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Tabs — #1244: split Users into self-serve tiers (Users) +
              enterprise customers (Enterprise Users) + Fray staff (Fray Users).
              #1258: 'jim' tab is fray_super_admin gated and pinned to the
              right end as a personal control surface for local Fray/SIT runs. */}
          <div className="flex gap-1 mb-2 border-b border-[#2A2A2A]">
            {[
              'invites', 'users', 'enterprise-users', 'fray-users', 'models',
              'mcp', 'observatory', 'audit-log', 'providers', 'platform-keys',
              'tiers', 'skills', 'topics', 'personas', 'persona-groups',
              'playbooks', 'moderation',
              ...(user?.role === 'fray_super_admin' ? ['jim'] : []),
            ].map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`px-4 py-2 text-xs font-mono border-b-2 -mb-px ${
                  tab === t ? 'text-[#FF4D00] border-[#FF4D00]' : 'text-[#A0A0A0] border-transparent'
                } ${t === 'jim' ? 'ml-auto' : ''}`}
                data-testid={`admin-tab-${t}`} aria-selected={tab === t} role="tab">{{'mcp': 'MCP', 'audit-log': 'Audit Log', 'platform-keys': 'Platform Infrastructure', 'persona-groups': 'Persona Groups', 'fray-users': 'Fray Users', 'enterprise-users': 'Enterprise Users', 'jim': 'Jim'}[t] || t.charAt(0).toUpperCase() + t.slice(1)}</button>
            ))}
          </div>
        </div>
        <div className="mb-4" />

        {tab === 'invites' && (
          <div data-testid="invites-tab">
            {/* Metrics bar */}
            {inviteMetrics && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="invite-metrics">
                {[
                  { label: 'Total', value: inviteMetrics.total, color: '#F5F1E8' },
                  { label: 'Acceptance', value: `${inviteMetrics.acceptance_rate}%`, color: '#22C55E' },
                  { label: 'Pending', value: inviteMetrics.pending, color: '#F2A900' },
                  { label: 'Expired', value: inviteMetrics.expired, color: '#A0A0A0' },
                  { label: 'Revoked', value: inviteMetrics.revoked, color: '#A61E17' },
                ].map(m => (
                  <div key={m.label} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] text-center">
                    <span className="block text-lg font-bold font-mono" style={{ color: m.color }}>{m.value}</span>
                    <span className="text-[9px] font-mono text-[#A0A0A0] uppercase">{m.label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Filter chips */}
            <div className="flex gap-1 mb-4 flex-wrap">
              {['all', 'active', 'claimed', 'expired', 'revoked', 'self_serve', 'beta_tester', 'enterprise_bulk'].map(f => (
                <button key={f} onClick={() => { setInviteFilter(f); setSelectedInvites(new Set()); }}
                  className={`px-2 py-1 text-[10px] font-mono border transition-colors ${
                    inviteFilter === f ? 'text-[#FF4D00] border-[#FF4D00]/40 bg-[#FF4D00]/5' : 'text-[#A0A0A0] border-[#2A2A2A]'
                  }`}>{f.replace(/_/g, ' ').toUpperCase()}</button>
              ))}
              {testCounts.test_invites > 0 && deletingTestData !== 'invites-confirm' && (
                <button onClick={() => setDeletingTestData('invites-confirm')}
                  className="px-2 py-1 text-[9px] font-mono bg-[#F2A900]/10 text-[#F2A900] border border-[#F2A900]/30 hover:bg-[#F2A900]/20">
                  Delete {testCounts.test_invites} Test Invites
                </button>
              )}
              {deletingTestData === 'invites-confirm' && (
                <span className="flex items-center gap-1.5">
                  <span className="text-[9px] text-[#F2A900] font-mono">Delete {testCounts.test_invites} test invites?</span>
                  <button onClick={async () => {
                    setDeletingTestData('invites');
                    try {
                      const res = await API.delete('/api/admin/invites/test');
                      alert(`Deleted ${res.data.deleted} test invites`);
                      await loadData();
                    } catch {}
                    setDeletingTestData(null);
                  }} className="px-2 py-0.5 text-[9px] font-mono bg-[#A61E17] text-white rounded">Yes</button>
                  <button onClick={() => setDeletingTestData(null)}
                    className="px-2 py-0.5 text-[9px] font-mono border border-[#2A2A2A] text-[#A0A0A0] rounded">No</button>
                </span>
              )}
              {deletingTestData === 'invites' && <span className="text-[9px] font-mono text-[#F2A900]">Deleting...</span>}
            </div>

            {/* Bulk actions */}
            {selectedInvites.size > 0 && (
              <div className="flex items-center gap-3 mb-4 p-3 border border-[#FF4D00]/20 bg-[#FF4D00]/5">
                <span className="text-xs font-mono text-[#FF4D00]">{selectedInvites.size} selected</span>
                <button onClick={async () => {
                  await API.post('/api/admin/invites/bulk-revoke', { ids: Array.from(selectedInvites) });
                  setSelectedInvites(new Set());
                  loadData();
                }}
                  className="px-3 py-1 text-[10px] font-mono bg-[#A61E17]/10 text-[#A61E17] border border-[#A61E17]/30 hover:bg-[#A61E17]/20"
                  data-testid="bulk-revoke-btn">
                  Delete Selected
                </button>
                <button onClick={() => setSelectedInvites(new Set())}
                  className="px-3 py-1 text-[10px] font-mono text-[#A0A0A0] border border-[#2A2A2A]">
                  Clear
                </button>
              </div>
            )}

            {/* Unified invite creation */}
            <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-6">
              <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-3">Create Invite</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                {Object.entries(inviteTypeDefaults).map(([slug, def]) => (
                  <button key={slug} onClick={() => {
                    setInviteType(slug);
                    setInviteCredits(def.credits || 0);
                    setInviteExpiry(def.expiry_days ? String(def.expiry_days) : '');
                  }}
                    className={`px-2 py-2 text-[10px] font-mono border transition-colors text-left ${
                      inviteType === slug
                        ? 'text-[#FF4D00] border-[#FF4D00]/40 bg-[#FF4D00]/5'
                        : 'text-[#A0A0A0] border-[#2A2A2A] hover:text-[#F5F5F5] hover:border-[#3A3A3A]'
                    }`}
                    data-testid={`invite-type-${slug}`}
                  >
                    <span className="block font-semibold uppercase">{slug.replace(/_/g, ' ')}</span>
                    <span className="block text-[9px] opacity-60">{def.plan} · {def.credits || 0} credits</span>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <input type="email" value={emailHint} onChange={e => setEmailHint(e.target.value)}
                  className="px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
                  placeholder="Email (required)" data-testid="invite-email" required />
                <input type="text" value={inviteDomain} onChange={e => setInviteDomain(e.target.value)}
                  className="px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
                  placeholder="Domain restriction (e.g. @company.com)" data-testid="invite-domain" />
                <div className="flex gap-2">
                  <input type="number" value={inviteCredits} onChange={e => setInviteCredits(Number(e.target.value))}
                    className="flex-1 px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
                    placeholder="Credits" data-testid="invite-credits" />
                  <input type="number" value={inviteExpiry} onChange={e => setInviteExpiry(e.target.value)}
                    className="flex-1 px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
                    placeholder="Expiry (days)" data-testid="invite-expiry" />
                </div>
                <div className="flex items-center gap-2">
                  {inviteTypeDefaults[inviteType] && (
                    <span className="text-[10px] font-mono text-[#A0A0A0]">
                      Plan: <span className="text-[#FF4D00]">{inviteTypeDefaults[inviteType].plan}</span>
                      {' · '}Mode: <span className="text-[#FF4D00]">{inviteTypeDefaults[inviteType].billing_mode}</span>
                      {' · '}Track: <span className="text-[#FF4D00]">{inviteTypeDefaults[inviteType].onboarding}</span>
                    </span>
                  )}
                </div>
              </div>
              <button onClick={createInvite} disabled={creating || !emailHint || !emailHint.includes('@')}
                className="px-4 py-2 min-h-[44px] bg-[#FF4D00] text-[#0A0A0A] text-sm font-bold font-display uppercase tracking-wider flex items-center gap-2 disabled:opacity-50 hover:bg-[#FF6B33] transition-colors"
                data-testid="create-invite-btn">
                <Plus className="w-4 h-4" /> {creating ? 'Creating...' : 'Create Invite'}
              </button>
            </div>

            {/* Invite list */}
            <div className="space-y-2">
              {invites.filter(inv => {
                if (inviteFilter === 'all') return true;
                if (inviteFilter === 'active') return inv.is_active && inv.uses_remaining > 0;
                if (inviteFilter === 'claimed') return inv.lifecycle_state === 'claimed' || (inv.max_uses > 0 && inv.uses_remaining <= 0);
                if (inviteFilter === 'expired') return inv.lifecycle_state === 'expired' || (!inv.is_active && inv.lifecycle_state !== 'revoked' && inv.lifecycle_state !== 'claimed');
                if (inviteFilter === 'revoked') return inv.lifecycle_state === 'revoked';
                return inv.invite_type_slug === inviteFilter;
              }).map((inv, i) => {
                // #481: Detect claimed status — uses_remaining exhausted or lifecycle_state === 'claimed'
                const isClaimed = inv.lifecycle_state === 'claimed' || (inv.max_uses > 0 && inv.uses_remaining <= 0);
                const state = isClaimed ? 'claimed' : (inv.lifecycle_state || (inv.is_active && inv.uses_remaining > 0 ? 'created' : 'expired'));
                const stateColor = { created: '#22C55E', sent: '#3B82F6', clicked: '#F2A900', claimed: '#00D4FF', accepted: '#22C55E', active: '#22C55E', revoked: '#A61E17', expired: '#A0A0A0' }[state] || '#A0A0A0';
                return (
                  <div key={i} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between" data-testid={`invite-${inv.code}`}>
                    <input type="checkbox" checked={selectedInvites.has(inv.id)}
                      onChange={e => {
                        const next = new Set(selectedInvites);
                        e.target.checked ? next.add(inv.id) : next.delete(inv.id);
                        setSelectedInvites(next);
                      }}
                      className="mr-3 shrink-0 accent-[#FF4D00]" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-mono text-[#FF4D00]">{inv.code}</span>
                        <span className="text-[9px] px-1.5 py-0.5 border font-mono uppercase" style={{ color: stateColor, borderColor: `${stateColor}40` }}>
                          {state}
                        </span>
                        {inv.invite_type_slug && inv.invite_type_slug !== 'self_serve' && (
                          <span className="text-[9px] px-1.5 py-0.5 border border-[#2A2A2A] font-mono text-[#A0A0A0] uppercase">
                            {inv.invite_type_slug.replace(/_/g, ' ')}
                          </span>
                        )}
                        {inv.is_test && (
                          <span className="text-[9px] px-1.5 py-0.5 border border-[#F2A900]/30 font-mono text-[#F2A900] uppercase">TEST</span>
                        )}
                      </div>
                      <div className="flex gap-3 mt-1 text-[10px] text-[#A0A0A0] font-mono flex-wrap">
                        {inv.email_hint && <span>For: {inv.email_hint}</span>}
                        <span>Plan: {inv.plan_override}</span>
                        <span>Mode: {inv.billing_mode}</span>
                        <span>Uses: {(inv.max_uses || 0) - (inv.uses_remaining || 0)}/{inv.max_uses}</span>
                        {inv.credits_allocated > 0 && <span>Credits: {inv.credits_allocated}</span>}
                        {inv.expires_at && <span>Expires: {new Date(inv.expires_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        <button onClick={async () => { await API.post(`/api/admin/invites/${inv.id}/revoke`); loadData(); }}
                          className="p-2 text-[#A0A0A0] hover:text-[#A61E17] transition-colors" title="Delete"
                          data-testid={`revoke-${inv.code}`}>
                          <X className="w-4 h-4" />
                        </button>
                      <button onClick={() => copyCode(inv.code)}
                        className="p-2 text-[#A0A0A0] hover:text-[#FF4D00] transition-colors" data-testid={`copy-${inv.code}`}
                        title={`Copy invite code: ${inv.code}`}
                        aria-label={`Copy invite code ${inv.code}`}>
                        {copied === inv.code ? <CheckCircle className="w-4 h-4 text-[#22C55E]" /> : <Copy className="w-4 h-4" />}
                      </button>
                      <button onClick={() => copyInviteLink(inv.code)}
                        className="p-2 text-[#A0A0A0] hover:text-[#FF4D00] transition-colors" data-testid={`copy-link-${inv.code}`}
                        title={`Copy share link: ${window.location.origin}/signup?invite=${inv.code}`}
                        aria-label={`Copy invite share link for ${inv.code}`}>
                        {copied === `link:${inv.code}` ? <CheckCircle className="w-4 h-4 text-[#22C55E]" /> : <Link2 className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                );
              })}
              {invites.length === 0 && <p className="text-[#A0A0A0] text-sm">No invites created yet.</p>}
            </div>
          </div>
        )}

        {(tab === 'users' || tab === 'fray-users' || tab === 'enterprise-users') && (
          <div data-testid={
            tab === 'fray-users' ? 'fray-users-tab'
              : tab === 'enterprise-users' ? 'enterprise-users-tab'
              : 'users-tab'
          }>
            {/* Add User + Search */}
            <div className="flex gap-3 mb-4">
              <button onClick={() => { setShowAddUser(true); setAddUserForm({ email: '', display_name: '', password: '', plan: 'pro', role: 'user', is_investor: false }); setAddUserError(''); setAddUserResult(null); setShowAddUserPw(false); }}
                className="px-4 py-2 bg-[#FF4D00] text-black text-sm font-medium flex items-center gap-2 shrink-0"
                data-testid="add-user-btn">
                <UserPlus className="w-4 h-4" /> Add User
              </button>
            </div>
            {/* Search box */}
            <div className="relative mb-4">
              <Search className="w-4 h-4 text-[#A0A0A0] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00] font-['Inter']"
                placeholder="Search by name, email, or ID..."
                data-testid="user-search"
              />
              {userSearch && (
                <button onClick={() => setUserSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#F5F5F5]" aria-label="Clear search">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="flex gap-2 mb-4">
              {['all', 'provisioned', 'byok', 'admin'].map(f => (
                <button key={f} onClick={() => setUserFilter(f)}
                  className={`px-3 py-1 text-[10px] font-mono border capitalize ${userFilter === f ? 'bg-[#FF4D00] text-black border-[#FF4D00]' : 'border-[#2A2A2A] text-[#A0A0A0]'}`}
                  data-testid={`filter-users-${f}`} aria-pressed={userFilter === f} aria-label={`Filter users: ${f}`}>{f}</button>
              ))}
              <span className="text-[10px] font-mono text-[#A0A0A0] self-center ml-auto">{filteredUsers.length} users</span>
              {testCounts.test_users > 0 && deletingTestData !== 'users-confirm' && (
                <button onClick={() => setDeletingTestData('users-confirm')}
                  className="px-2 py-1 text-[9px] font-mono bg-[#F2A900]/10 text-[#F2A900] border border-[#F2A900]/30 hover:bg-[#F2A900]/20">
                  Delete {testCounts.test_users} Test Users
                </button>
              )}
              {deletingTestData === 'users-confirm' && (
                <span className="flex items-center gap-1.5">
                  <span className="text-[9px] text-[#F2A900] font-mono">Delete {testCounts.test_users} test users?</span>
                  <button onClick={async () => {
                    setDeletingTestData('users');
                    try {
                      const res = await API.delete('/api/admin/users/test');
                      alert(`Deleted ${res.data.deleted} test users`);
                      await loadData();
                    } catch (e) { alert(e.response?.data?.detail || 'Delete failed: ' + (e.message || 'Unknown error')); }
                    setDeletingTestData(null);
                  }} className="px-2 py-0.5 text-[9px] font-mono bg-[#A61E17] text-white rounded">Yes</button>
                  <button onClick={() => setDeletingTestData(null)}
                    className="px-2 py-0.5 text-[9px] font-mono border border-[#2A2A2A] text-[#A0A0A0] rounded">No</button>
                </span>
              )}
              {deletingTestData === 'users' && <span className="text-[9px] font-mono text-[#F2A900]">Deleting...</span>}
            </div>
            {/* Bulk actions for users */}
            {selectedUsers.size > 0 && (
              <div className="flex items-center gap-3 mb-4 p-3 border border-[#FF4D00]/20 bg-[#FF4D00]/5 flex-wrap" data-testid="user-bulk-actions">
                <span className="text-xs font-mono text-[#FF4D00]">{selectedUsers.size} selected</span>
                <button onClick={async () => {
                  if (!window.confirm(`Delete ${selectedUsers.size} accounts? This cannot be undone.`)) return;
                  const ids = Array.from(selectedUsers);
                  setBulkDeleting(true);
                  setBulkDeleteProgress({ total: ids.length, completed: 0, failed: 0, current: 'Starting...', results: null });
                  try {
                    const res = await API.post('/api/admin/users/bulk-delete', { ids, confirm: 'DELETE' });
                    const data = res.data;
                    setBulkDeleteProgress({ total: ids.length, completed: data.deleted || 0, failed: data.failed || 0, current: null, results: data.results || [] });
                  } catch (err) {
                    setBulkDeleteProgress(prev => ({ ...prev, current: null, results: [{ status: 'failed', error: err.response?.data?.detail || 'Request failed' }] }));
                  }
                  setSelectedUsers(new Set());
                  setBulkDeleting(false);
                  loadData();
                }}
                  disabled={bulkDeleting}
                  className="px-3 py-1 text-[10px] font-mono bg-[#A61E17]/10 text-[#A61E17] border border-[#A61E17]/30 hover:bg-[#A61E17]/20 disabled:opacity-50"
                  data-testid="user-bulk-delete">
                  {bulkDeleting ? <><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Deleting...</> : 'Bulk Delete'}
                </button>
                <div className="flex items-center gap-1">
                  <select value={bulkPlan} onChange={e => setBulkPlan(e.target.value)}
                    className="px-2 py-1 text-[10px] font-mono bg-[#0A0A0A] border border-[#2A2A2A] text-[#F5F1E8]">
                    {['free', 'student', 'pro', 'team', 'enterprise'].map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <button onClick={async () => {
                    setBulkPlanChanging(true);
                    await API.post('/api/admin/users/bulk-plan-change', { ids: Array.from(selectedUsers), plan: bulkPlan });
                    setSelectedUsers(new Set());
                    setBulkPlanChanging(false);
                    loadData();
                  }}
                    disabled={bulkPlanChanging}
                    className="px-3 py-1 text-[10px] font-mono bg-[#FF4D00]/10 text-[#FF4D00] border border-[#FF4D00]/30 hover:bg-[#FF4D00]/20 disabled:opacity-50"
                    data-testid="user-bulk-plan">
                    {bulkPlanChanging ? '...' : 'Change Plan'}
                  </button>
                </div>
                <button onClick={() => setSelectedUsers(new Set())}
                  className="px-3 py-1 text-[10px] font-mono text-[#A0A0A0] border border-[#2A2A2A]">
                  Clear
                </button>
              </div>
            )}
            {/* Bulk Delete Progress/Results Modal */}
            {bulkDeleteProgress && bulkDeleteProgress.results && (
              <div className="mb-4 p-4 border border-[#2A2A2A] bg-[#1A1A1A]" data-testid="bulk-delete-results">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-['Barlow_Condensed'] font-bold">Bulk Delete Complete</h4>
                  <button onClick={() => setBulkDeleteProgress(null)} className="text-[#A0A0A0] hover:text-[#F5F5F5] text-xs">&times;</button>
                </div>
                <div className="flex gap-4 mb-3">
                  <div className="px-3 py-2 bg-[#22C55E]/10 border border-[#22C55E]/30">
                    <span className="text-lg font-bold text-[#22C55E]">{bulkDeleteProgress.completed}</span>
                    <span className="text-[9px] text-[#A0A0A0] ml-1">deleted</span>
                  </div>
                  {bulkDeleteProgress.failed > 0 && (
                    <div className="px-3 py-2 bg-[#EF4444]/10 border border-[#EF4444]/30">
                      <span className="text-lg font-bold text-[#EF4444]">{bulkDeleteProgress.failed}</span>
                      <span className="text-[9px] text-[#A0A0A0] ml-1">failed</span>
                    </div>
                  )}
                </div>
                {bulkDeleteProgress.results.filter(r => r.status === 'failed').length > 0 && (
                  <div className="text-[10px] font-mono text-[#EF4444] space-y-1">
                    {bulkDeleteProgress.results.filter(r => r.status === 'failed').map((r, i) => (
                      <div key={i}>{r.email || r.user_id}: {r.error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {bulkDeleting && (
              <div className="mb-4 p-4 border border-[#FF4D00]/30 bg-[#FF4D00]/5 flex items-center gap-3" data-testid="bulk-delete-progress">
                <Loader2 className="w-4 h-4 text-[#FF4D00] animate-spin" />
                <span className="text-xs font-mono text-[#FF4D00]">Deleting {bulkDeleteProgress?.total || '?'} accounts and cascading associated data...</span>
              </div>
            )}
            {/* #1220 / #1248: Fray Users tab section dividers now use the
                same OrWorkspaceGroupHeader treatment as Users +
                Enterprise Users tabs (Plasma Core accent + collapsible
                + chevron). Standalone SectionDivider call removed —
                the header now lives inside the iteration block below
                so collapse-state can hide its rows.

                #1239: row JSX is shared via the `renderUserRow`
                function defined inside the iteration IIFE below. */}
            <div className="space-y-2">
              {/* #1239: render-function for one user row. Closes over
                  selectedUsers, openUserDrawer, setResetModal, etc. so
                  it can be called from multiple iteration contexts
                  (flat list, group iteration, Fray-Staff section)
                  without duplicating the JSX. */}
              {(() => {
                const renderUserRow = (u, i) => (
                <div key={u.id || i} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between cursor-pointer hover:border-[#FF4D00]/40 transition-colors" onClick={() => openUserDrawer(u)}>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={selectedUsers.has(u.id)}
                      onClick={e => e.stopPropagation()}
                      onChange={e => {
                        const next = new Set(selectedUsers);
                        e.target.checked ? next.add(u.id) : next.delete(u.id);
                        setSelectedUsers(next);
                      }}
                      className="shrink-0 accent-[#FF4D00]" />
                  <div>
                    <span className="text-sm font-medium">{u.name || u.email}</span>
                    <div className="flex gap-3 mt-1 text-[10px] text-[#A0A0A0] font-mono">
                      <span>{u.email}</span>
                      <span className="capitalize">Plan: {u.plan || 'free'}</span>
                      <span>Mode: {u.billing_mode || 'managed'}</span>
                      <span className={u.role === 'fray_super_admin' ? 'text-[#FF4D00]' : u.role === 'super_admin' ? 'text-[#22C55E]' : ['admin', 'fray_admin'].includes(u.role) ? 'text-[#A855F7]' : ''}>Role: {u.role === 'fray_super_admin' ? 'Fray Super Admin' : u.role === 'super_admin' ? 'Super Admin' : u.role === 'fray_admin' ? 'Fray Admin' : u.role === 'admin' ? 'Admin' : u.role || 'user'}</span>
                      {u.totp_enabled && <span className="text-[#22C55E]">2FA</span>}
                      {u.is_disabled && <span className="text-[#EF4444]">DISABLED</span>}
                      {u.is_investor && <span className="text-[#00D4FF]">INVESTOR</span>}
                      {u.is_test && <span className="text-[#F2A900]">TEST</span>}
                      {u.provisioned_plan && <span className="text-[#A855F7]">Provisioned: {u.provisioned_plan}</span>}
                    </div>
                  </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    <span className="text-[10px] font-mono text-[#A0A0A0]">
                      {new Date(u.created_at).toLocaleDateString()}
                    </span>
                    {u.id !== user?.id && (
                      <>
                        <button onClick={() => {
                          setResetModal(u);
                          setResetPassword('');
                          setResetAutoGen(true);
                          setResetForceChange(true);
                          setResetResult(null);
                          setShowResetPw(false);
                        }} className="px-2 py-0.5 text-[8px] font-mono border text-[#FF4D00] border-[#FF4D00]/30 hover:bg-[#FF4D00]/10">
                          <Key className="w-3 h-3 inline mr-1" />Reset PW
                        </button>
                        {u.totp_enabled && (
                          <button onClick={async () => {
                            if (!window.confirm(`Reset 2FA for ${u.email}? They will need to re-enroll their authenticator app.`)) return;
                            await API.delete(`/api/admin/users/${u.id}/2fa`);
                            await loadData();
                          }} className="px-2 py-0.5 text-[8px] font-mono border text-[#3B82F6] border-[#3B82F6]/30 hover:bg-[#3B82F6]/10">
                            <Shield className="w-3 h-3 inline mr-1" />Reset 2FA
                          </button>
                        )}
                        <button onClick={async () => {
                          if (!window.confirm(`Force ${u.email} to log in again? All sessions and API keys will be invalidated.`)) return;
                          await API.post(`/api/admin/users/${u.id}/force-reauth`);
                          await loadData();
                        }} className="px-2 py-0.5 text-[8px] font-mono border text-[#A0A0A0] border-[#2A2A2A] hover:text-[#F5F5F5] hover:bg-[#2A2A2A]">
                          Force Reauth
                        </button>
                        <button onClick={async () => {
                          const newVal = !u.is_investor;
                          await API.put(`/api/admin/users/${u.id}/investor`, { is_investor: newVal });
                          await loadData();
                        }} className={`px-2 py-0.5 text-[8px] font-mono border ${u.is_investor ? 'text-[#00D4FF] border-[#00D4FF]/30 hover:bg-[#00D4FF]/10' : 'text-[#A0A0A0] border-[#2A2A2A] hover:text-[#00D4FF] hover:border-[#00D4FF]/30'}`}>
                          {u.is_investor ? 'Revoke Investor' : 'Grant Investor'}
                        </button>
                        {(u.plan === 'fray' || u.plan === 'enterprise') && (
                        <button onClick={async () => {
                          const isAdmin = ['admin', 'fray_admin'].includes(u.role);
                          const newRole = isAdmin ? 'user' : (u.plan === 'fray' ? 'fray_admin' : 'admin');
                          await API.put(`/api/admin/users/${u.id}/role`, { role: newRole });
                          await loadData();
                        }} className={`px-2 py-0.5 text-[8px] font-mono border ${['admin', 'fray_admin'].includes(u.role) ? 'text-[#EF4444] border-[#EF4444]/30 hover:bg-[#EF4444]/10' : 'text-[#A855F7] border-[#A855F7]/30 hover:bg-[#A855F7]/10'}`}>
                          {['admin', 'fray_admin'].includes(u.role) ? <><ShieldOff className="w-3 h-3 inline mr-1" />Revoke Admin</> : <><ShieldCheck className="w-3 h-3 inline mr-1" />Make Admin</>}
                        </button>
                        )}
                        <button onClick={async () => {
                          if (u.is_disabled) {
                            await API.put(`/api/admin/users/${u.id}/enable`);
                          } else {
                            if (!window.confirm(`Disable ${u.email}? They will be logged out and cannot log in until re-enabled.`)) return;
                            await API.put(`/api/admin/users/${u.id}/disable`);
                          }
                          await loadData();
                        }} className={`px-2 py-0.5 text-[8px] font-mono border ${u.is_disabled ? 'text-[#22C55E] border-[#22C55E]/30 hover:bg-[#22C55E]/10' : 'text-[#A0A0A0] border-[#2A2A2A] hover:text-[#EF4444] hover:border-[#EF4444]/30'}`}>
                          {u.is_disabled ? 'Enable' : 'Disable'}
                        </button>
                        <button onClick={async (e) => {
                          const confirm1 = window.prompt(`Type "${u.email}" to permanently delete this user and ALL their data:`);
                          if (confirm1 !== u.email) return;
                          const btn = e.currentTarget;
                          btn.disabled = true;
                          btn.textContent = 'Deleting...';
                          try {
                            const res = await API.delete(`/api/admin/users/${u.id}`, { data: { confirm_email: u.email } });
                            const d = res.data?.deleted || {};
                            const summary = Object.entries(d).filter(([,v]) => v > 0).map(([k,v]) => `${v} ${k}`).join(', ');
                            btn.textContent = summary ? `Done (${summary})` : 'Deleted';
                            setTimeout(() => loadData(), 1500);
                          } catch (err) {
                            btn.disabled = false;
                            btn.textContent = 'Delete';
                            alert(err.response?.data?.detail || 'Delete failed');
                          }
                        }} className="px-2 py-0.5 text-[8px] font-mono border text-[#EF4444]/50 border-[#EF4444]/20 hover:text-[#EF4444] hover:bg-[#EF4444]/10 disabled:opacity-50">
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
                );
                // ── Iteration ──
                // #1244: three render paths — Fray Users (existing
                // sections from #1220), Users (self-serve tier groups,
                // always-show all 4 even when empty), Enterprise Users
                // (one group per customer workspace).
                const renderGroupedRows = (groups, opts = {}) => (
                  groups.flatMap(([label, group]) => {
                    const collapsed = collapsedGroups.has(label);
                    const header = (
                      <OrWorkspaceGroupHeader
                        key={`hdr-${label}`}
                        label={label}
                        count={group.length}
                        collapsed={collapsed}
                        onToggle={() => toggleGroup(label)}
                      />
                    );
                    if (collapsed) return [header];
                    if (group.length === 0 && opts.showEmptyHint) {
                      return [
                        header,
                        <div
                          key={`empty-${label}`}
                          className="px-3 py-2 text-[10px] font-mono text-[#A0A0A0] italic"
                          data-testid={`empty-tier-${label.toLowerCase()}`}
                        >
                          no users on this tier yet
                        </div>,
                      ];
                    }
                    return [header, ...group.map(renderUserRow)];
                  })
                );
                if (tab === 'fray-users') {
                  // #1248: Fray Staff + Development & Testing now use
                  // collapsible OrWorkspaceGroupHeader (matching the
                  // Users + Enterprise Users tabs). Testing rows keep
                  // their compact custom JSX (TESTING / 2FA-bypass
                  // badges, no destructive actions) — that's why we
                  // emit them inline rather than reusing renderUserRow.
                  const items = [];
                  // ── Fray Staff group ──
                  const staffCollapsed = collapsedGroups.has('Fray Staff');
                  items.push(
                    <OrWorkspaceGroupHeader
                      key="hdr-fray-staff"
                      label="Fray Staff"
                      count={frayStaffUsers.length}
                      collapsed={staffCollapsed}
                      onToggle={() => toggleGroup('Fray Staff')}
                      testIdSuffix="fray-staff"
                    />
                  );
                  if (!staffCollapsed) {
                    for (const u of frayStaffUsers) items.push(renderUserRow(u));
                  }
                  // ── Development & Testing group ──
                  const testCollapsed = collapsedGroups.has('Development & Testing');
                  items.push(
                    <OrWorkspaceGroupHeader
                      key="hdr-dev-testing"
                      label="Development & Testing"
                      count={testUsersOnFrayTab.length}
                      collapsed={testCollapsed}
                      onToggle={() => toggleGroup('Development & Testing')}
                      hint="Automation accounts (Claude Code, Emergent Pipeline, background workers) + throwaway test accounts (Playwright, security audit, MCP integration)"
                      testIdSuffix="dev-testing"
                    />
                  );
                  if (!testCollapsed) {
                    if (testUsersOnFrayTab.length === 0) {
                      items.push(
                        <div
                          key="empty-dev-testing"
                          className="p-3 text-xs font-mono text-[#A0A0A0] italic"
                          data-testid="fray-users-testing-empty"
                        >
                          No automation or testing accounts.
                        </div>
                      );
                    } else {
                      // Custom compact row for testing users — TESTING /
                      // 2FA-bypass badges, no destructive action buttons.
                      for (let i = 0; i < testUsersOnFrayTab.length; i++) {
                        const u = testUsersOnFrayTab[i];
                        items.push(
                          <div
                            key={`test-${u.id || i}`}
                            className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between cursor-pointer hover:border-[#FF4D00]/40 transition-colors"
                            onClick={() => openUserDrawer(u)}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={selectedUsers.has(u.id)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  const next = new Set(selectedUsers);
                                  e.target.checked ? next.add(u.id) : next.delete(u.id);
                                  setSelectedUsers(next);
                                }}
                                className="shrink-0 accent-[#F2A900]"
                              />
                              <div>
                                <span className="text-sm font-medium">{u.name || u.email}</span>
                                <div className="flex gap-3 mt-1 text-[10px] text-[#A0A0A0] font-mono">
                                  <span>{u.email}</span>
                                  <span className="capitalize">Plan: {u.plan || 'free'}</span>
                                  <span className="text-[#F2A900]">TESTING</span>
                                  {u.auth_2fa_bypass && <span className="text-[#F2A900]">2FA-bypass</span>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <span className="text-[10px] font-mono text-[#A0A0A0]">
                                {new Date(u.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        );
                      }
                    }
                  }
                  return items;
                }
                if (tab === 'users') {
                  // #1244: always-show all self-serve tier groups
                  return renderGroupedRows(usersByTier, { showEmptyHint: true });
                }
                if (tab === 'enterprise-users') {
                  // #1244: enterprise customers grouped by workspace
                  return renderGroupedRows(enterpriseUsersByWorkspace);
                }
                // Fallback (defensive)
                return filteredUsers.map(renderUserRow);
              })()}
              {/* #1248: Fray Users tab section dividers + rows are now
                  emitted INSIDE the iteration block above so collapse-
                  state can hide rows. The standalone Testing Users
                  block that lived here is removed. */}
            </div>
          </div>
        )}

        {tab === 'observatory' && (
          <div data-testid="observatory-tab">
            {observatory ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: 'Total Frays', value: observatory.platform_totals.total_frays },
                    { label: 'Total Tokens', value: observatory.platform_totals.total_tokens?.toLocaleString() },
                    { label: 'Total Cost', value: `$${observatory.platform_totals.total_cost?.toFixed(4)}` },
                    { label: 'This Month Frays', value: observatory.this_month.frays },
                  ].map((m, i) => (
                    <div key={i} className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                      <Activity className="w-4 h-4 text-[#FF4D00] mb-2" />
                      <span className="text-2xl font-bold font-['Barlow_Condensed'] block">{m.value}</span>
                      <span className="text-[10px] text-[#A0A0A0] font-mono">{m.label}</span>
                    </div>
                  ))}
                </div>

                <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-2">Top Models</h3>
                <div className="border border-[#2A2A2A] mb-6 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#1A1A1A]">
                      <tr>
                        <th className="text-left p-2 font-mono text-[#A0A0A0]">Model</th>
                        <th className="text-right p-2 font-mono text-[#A0A0A0]">Turns</th>
                        <th className="text-right p-2 font-mono text-[#A0A0A0]">Tokens</th>
                        <th className="text-right p-2 font-mono text-[#A0A0A0]">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {observatory.top_models?.map((m, i) => (
                        <tr key={i} className="border-t border-[#2A2A2A]">
                          <td className="p-2 font-mono">{m.model}</td>
                          <td className="p-2 text-right">{m.turns}</td>
                          <td className="p-2 text-right font-mono">{m.tokens?.toLocaleString()}</td>
                          <td className="p-2 text-right font-mono text-[#22C55E]">${m.cost?.toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-2">Top Users</h3>
                <div className="space-y-2">
                  {observatory.top_users?.map((u, i) => (
                    <div key={i} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium">{u.name}</span>
                        <span className="text-xs text-[#A0A0A0] font-mono ml-2">{u.email}</span>
                      </div>
                      <div className="text-right text-xs font-mono">
                        <span>{u.frays} frays</span>
                        <span className="text-[#A0A0A0] mx-2">|</span>
                        <span>{u.tokens?.toLocaleString()} tokens</span>
                        {u.cost > 0 && <span className="text-[#22C55E] ml-2">${u.cost.toFixed(4)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-[#A0A0A0] text-sm">Loading observatory data...</p>
            )}
          </div>
        )}

        {tab === 'audit-log' && <AuditLogTab />}
        {tab === 'providers' && <ProvidersTab />}
        {tab === 'platform-keys' && <PlatformKeysTab />}
        {tab === 'tiers' && <TiersTab />}
        {tab === 'skills' && <SkillsTab />}
        {tab === 'topics' && <TopicsTab />}
        {tab === 'personas' && <PersonasTab />}
        {tab === 'persona-groups' && <PersonaGroupsTab />}
        {tab === 'playbooks' && <PlaybooksTab />}
        {tab === 'moderation' && <ModerationTab />}
        {tab === 'models' && <ModelRosterAdmin />}
        {tab === 'mcp' && <McpTab />}
        {tab === 'jim' && <JimMenuTab />}
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" role="dialog" aria-label="Add user">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg mx-4 bg-[#1A1A1A] border border-[#2A2A2A] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-[#2A2A2A]">
              <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Add User</h3>
              <button onClick={() => { setShowAddUser(false); setAddUserResult(null); }}
                className="text-[#A0A0A0] hover:text-[#F5F5F5]" aria-label="Close add user dialog">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {!addUserResult ? (
                <>
                  {addUserError && (
                    <div className="p-2 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-xs">{addUserError}</div>
                  )}
                  <div>
                    <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Email *</label>
                    <input type="email" value={addUserForm.email}
                      onChange={e => setAddUserForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
                      placeholder="user@example.com" data-testid="add-user-email" />
                  </div>
                  <div>
                    <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Display Name</label>
                    <input type="text" value={addUserForm.display_name}
                      onChange={e => setAddUserForm(f => ({ ...f, display_name: e.target.value }))}
                      className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
                      placeholder="John Doe" data-testid="add-user-name" />
                  </div>
                  <div>
                    <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Password *</label>
                    <div className="relative">
                      <input type={showAddUserPw ? 'text' : 'password'} value={addUserForm.password}
                        onChange={e => setAddUserForm(f => ({ ...f, password: e.target.value }))}
                        className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00] pr-10"
                        placeholder="Minimum 8 characters" minLength={8} data-testid="add-user-password" />
                      <button onClick={() => setShowAddUserPw(!showAddUserPw)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#FF4D00]">
                        {showAddUserPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Plan</label>
                      <select value={addUserForm.plan}
                        onChange={e => setAddUserForm(f => ({ ...f, plan: e.target.value }))}
                        className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
                        data-testid="add-user-plan">
                        {['free', 'student', 'pro', 'team', 'enterprise', 'fray'].map(p => (
                          <option key={p} value={p}>{p === 'fray' ? 'Fray (Platform)' : p.charAt(0).toUpperCase() + p.slice(1)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Role</label>
                      <select value={addUserForm.role}
                        onChange={e => setAddUserForm(f => ({ ...f, role: e.target.value }))}
                        className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
                        data-testid="add-user-role">
                        {(addUserForm.plan === 'fray'
                          ? [['user', 'User'], ['fray_admin', 'Fray Admin'], ['fray_super_admin', 'Fray Super Admin']]
                          : addUserForm.plan === 'enterprise'
                            ? [['user', 'User'], ['admin', 'Admin'], ['super_admin', 'Super Admin']]
                            : [['user', 'User']]
                        ).map(([v, label]) => (
                          <option key={v} value={v}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={addUserForm.is_investor}
                      onChange={e => setAddUserForm(f => ({ ...f, is_investor: e.target.checked }))}
                      className="w-4 h-4 accent-[#00D4FF]" data-testid="add-user-investor" />
                    <span className="text-xs text-[#A0A0A0]">Investor account</span>
                  </label>
                  <button onClick={createUser}
                    disabled={addUserLoading || !addUserForm.email || addUserForm.password.length < 8}
                    className="w-full px-4 py-2.5 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50"
                    data-testid="add-user-submit">
                    {addUserLoading ? 'Creating...' : 'Create User'}
                  </button>
                </>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-[#22C55E]/10 border border-[#22C55E]/30">
                    <CheckCircle className="w-4 h-4 text-[#22C55E] inline mr-2" />
                    <span className="text-sm text-[#22C55E]">User created successfully</span>
                  </div>
                  <div className="p-3 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono space-y-1">
                    <p><span className="text-[#A0A0A0]">Email:</span> {addUserResult.email}</p>
                    <p><span className="text-[#A0A0A0]">Name:</span> {addUserResult.name}</p>
                    <p><span className="text-[#A0A0A0]">Plan:</span> {addUserResult.plan}</p>
                    <p><span className="text-[#A0A0A0]">Role:</span> {addUserResult.role}</p>
                  </div>
                  {addUserResult.mcp_api_key && (
                    <div className="p-3 bg-[#FF4D00]/10 border border-[#FF4D00]/30">
                      <p className="text-[10px] text-[#A0A0A0] font-mono mb-1">MCP API Key (save now):</p>
                      <code className="text-xs text-[#FF4D00] font-mono break-all">{addUserResult.mcp_api_key}</code>
                    </div>
                  )}
                  <button onClick={() => { setShowAddUser(false); setAddUserResult(null); }}
                    className="w-full px-4 py-2 border border-[#2A2A2A] text-sm text-[#A0A0A0] hover:text-[#F5F5F5]">
                    Done
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" role="dialog" aria-label="Reset password">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md mx-4 bg-[#1A1A1A] border border-[#2A2A2A] overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-[#2A2A2A]">
              <div>
                <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Reset Password</h3>
                <p className="text-[10px] text-[#A0A0A0] font-mono mt-0.5">{resetModal.email}</p>
              </div>
              <button onClick={() => { setResetModal(null); setResetResult(null); }}
                className="text-[#A0A0A0] hover:text-[#F5F5F5]" aria-label="Close reset password dialog">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {!resetResult ? (
                <>
                  {/* Auto-generate vs Manual */}
                  <div className="flex gap-2">
                    <button onClick={() => { setResetAutoGen(true); setResetPassword(''); }}
                      className={`flex-1 px-3 py-2 text-xs font-mono border ${resetAutoGen ? 'bg-[#FF4D00]/10 border-[#FF4D00] text-[#FF4D00]' : 'border-[#2A2A2A] text-[#A0A0A0]'}`}>
                      Auto-generate
                    </button>
                    <button onClick={() => setResetAutoGen(false)}
                      className={`flex-1 px-3 py-2 text-xs font-mono border ${!resetAutoGen ? 'bg-[#FF4D00]/10 border-[#FF4D00] text-[#FF4D00]' : 'border-[#2A2A2A] text-[#A0A0A0]'}`}>
                      Set manually
                    </button>
                  </div>

                  {/* Manual password input */}
                  {!resetAutoGen && (
                    <div>
                      <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">New Password</label>
                      <div className="relative">
                        <input
                          type={showResetPw ? 'text' : 'password'}
                          value={resetPassword}
                          onChange={e => setResetPassword(e.target.value)}
                          className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00] pr-10"
                          placeholder="Minimum 8 characters"
                          minLength={8}
                        />
                        <button onClick={() => setShowResetPw(!showResetPw)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#FF4D00]"
                          aria-label={showResetPw ? 'Hide password' : 'Show password'}>
                          {showResetPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Force change toggle */}
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={resetForceChange}
                      onChange={e => setResetForceChange(e.target.checked)}
                      className="w-4 h-4 accent-[#FF4D00]"
                    />
                    <span className="text-xs text-[#A0A0A0]">
                      Require password change on next login
                    </span>
                  </label>

                  {/* Submit */}
                  <button
                    onClick={async () => {
                      setResetLoading(true);
                      try {
                        const body = { force_change_on_login: resetForceChange };
                        if (!resetAutoGen && resetPassword) body.password = resetPassword;
                        const res = await API.post(`/api/admin/users/${resetModal.id}/reset-password`, body);
                        setResetResult(res.data);
                      } catch (err) {
                        setResetResult({ error: err.response?.data?.detail || 'Reset failed' });
                      } finally {
                        setResetLoading(false);
                      }
                    }}
                    disabled={resetLoading || (!resetAutoGen && resetPassword.length < 8)}
                    className="w-full px-4 py-2.5 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50"
                  >
                    {resetLoading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </>
              ) : resetResult.error ? (
                <div className="p-3 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm">
                  {resetResult.error}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-[#22C55E]/10 border border-[#22C55E]/30">
                    <CheckCircle className="w-4 h-4 text-[#22C55E] inline mr-2" />
                    <span className="text-sm text-[#22C55E]">Password reset successfully</span>
                  </div>

                  {resetResult.temporary_password && (
                    <div className="p-3 bg-[#0F0F0F] border border-[#2A2A2A]">
                      <label className="text-[10px] text-[#A0A0A0] font-mono block mb-1">Temporary Password</label>
                      <div className="flex items-center gap-2">
                        <code className="text-sm text-[#FF4D00] font-mono flex-1">{resetResult.temporary_password}</code>
                        <button onClick={() => {
                          navigator.clipboard.writeText(resetResult.temporary_password);
                          setCopied('reset-pw');
                          setTimeout(() => setCopied(''), 2000);
                        }} className="px-2 py-1 text-[8px] font-mono border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#FF4D00]">
                          {copied === 'reset-pw' ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <p className="text-[9px] text-[#EF4444] mt-2">Save this now — it cannot be retrieved again.</p>
                    </div>
                  )}

                  {resetResult.force_change_on_login && (
                    <p className="text-[10px] text-[#A0A0A0]">
                      User will be prompted to change their password on next login.
                    </p>
                  )}

                  <button onClick={() => { setResetModal(null); setResetResult(null); }}
                    className="w-full px-4 py-2 border border-[#2A2A2A] text-sm text-[#A0A0A0] hover:text-[#F5F5F5]">
                    Done
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* User Detail Drawer */}
      <UserDetailDrawer
        user={selectedUser}
        drawerData={drawerData}
        drawerLoading={drawerLoading}
        onClose={closeDrawer}
        onRefresh={loadData}
        onRefreshDrawer={() => selectedUser && openUserDrawer(selectedUser)}
        currentUserId={user?.id}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   UserDetailDrawer — slide-out panel for UC-ADM-04
   ═══════════════════════════════════════════════════════════════ */
function UserDetailDrawer({ user: selectedUser, drawerData, drawerLoading, onClose, onRefresh, onRefreshDrawer, currentUserId }) {
  const [activeTab, setActiveTab] = useState('account');
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [creditDelta, setCreditDelta] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditAdjusting, setCreditAdjusting] = useState(false);
  const [showCreditForm, setShowCreditForm] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [changingRole, setChangingRole] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  // #1137 Task 4.1: Impersonation modal state. Confirm modal is rendered
  // alongside the drawer; opening it does not navigate — only the
  // post-confirm startImpersonation() does, via window.location.href.
  const { startImpersonation } = useImpersonation();
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const [impersonateError, setImpersonateError] = useState(null);
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  // #1137 final-review N1: Cancel-during-Confirm race guard. If the user
  // clicks Cancel while POST /api/admin/impersonate/grant is in flight,
  // the in-flight request will still resolve with a real grant_id —
  // without this ref the success branch would persist the grant to
  // sessionStorage and navigate even though the user said cancel. The
  // ref flips to true on Cancel; the success branch checks it and (a)
  // bails out early, (b) revokes the just-minted grant best-effort so
  // the audit row is closed instead of orphaned. Reset on every modal
  // open via the useEffect below.
  const impersonateCancelledRef = useRef(false);
  useEffect(() => {
    if (impersonateTarget) impersonateCancelledRef.current = false;
  }, [impersonateTarget]);

  // Key reveal state (#644) — plaintext is only held in-memory after explicit click
  const [revealedOpenRouterKey, setRevealedOpenRouterKey] = useState(null);
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealError, setRevealError] = useState(null);
  const [orKeyCopied, setOrKeyCopied] = useState(false);
  // Key regeneration state (#659) — destructive; gated behind confirmation
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenError, setRegenError] = useState(null);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const [regenSuccess, setRegenSuccess] = useState(null);

  // Reset tab when user changes
  useEffect(() => {
    setActiveTab('account');
    setApiKeys([]);
    setGeneratedKey(null);
    setShowCreditForm(false);
    setDeleteConfirmEmail('');
    // Clear any previously revealed plaintext to avoid leaking across users
    setRevealedOpenRouterKey(null);
    setRevealError(null);
    setOrKeyCopied(false);
    // #659: clear regeneration state
    setRegenError(null);
    setShowRegenConfirm(false);
    setRegenSuccess(null);
    // #662: clear any revealed BYOK keys so they don't leak across users
    setByokState({});
  }, [selectedUser?.id]);

  // #1129: target-user projects (admin drill-down). Loaded only when the
  // Projects tab is active so we don't pre-fetch on every drawer open.
  const [userProjects, setUserProjects] = useState([]);
  const [userProjectsLoading, setUserProjectsLoading] = useState(false);
  const [userProjectsError, setUserProjectsError] = useState(null);

  // Load API keys when switching to that tab
  useEffect(() => {
    if (activeTab === 'api-keys' && selectedUser?.id) {
      loadApiKeys();
    }
  }, [activeTab, selectedUser?.id]);

  // Load projects when switching to that tab (#1129).
  useEffect(() => {
    if (activeTab === 'projects' && selectedUser?.id) {
      loadUserProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedUser?.id]);

  // Reset projects view when user changes
  useEffect(() => {
    setUserProjects([]);
    setUserProjectsError(null);
  }, [selectedUser?.id]);

  const loadApiKeys = async () => {
    setApiKeysLoading(true);
    try {
      const res = await API.get(`/api/admin/users/${selectedUser.id}/api-keys`);
      setApiKeys(res.data.keys || res.data.api_keys || []);
    } catch (err) {
      console.error('Load API keys error:', err);
    }
    setApiKeysLoading(false);
  };

  const loadUserProjects = async () => {
    setUserProjectsLoading(true);
    setUserProjectsError(null);
    try {
      const res = await API.get(`/api/admin/users/${selectedUser.id}/projects`);
      setUserProjects(res.data?.projects || []);
    } catch (err) {
      setUserProjectsError(err.response?.data?.detail || 'Failed to load projects');
    }
    setUserProjectsLoading(false);
  };

  const generateApiKey = async () => {
    if (!newKeyName.trim()) return;
    try {
      const res = await API.post(`/api/admin/users/${selectedUser.id}/api-keys`, { name: newKeyName });
      setGeneratedKey(res.data.key || res.data.api_key);
      setNewKeyName('');
      await loadApiKeys();
    } catch (err) {
      console.error('Generate key error:', err);
    }
  };

  const revokeApiKey = async (keyId) => {
    if (!window.confirm('Revoke this API key? This cannot be undone.')) return;
    try {
      await API.delete(`/api/admin/users/${selectedUser.id}/api-keys/${keyId}`);
      await loadApiKeys();
    } catch (err) {
      console.error('Revoke key error:', err);
    }
  };

  // #644: Reveal OpenRouter key for admin key-value verification. Lazy —
  // plaintext is never pre-loaded; only fetched on explicit user click.
  const revealOpenRouterKey = async () => {
    setRevealLoading(true);
    setRevealError(null);
    try {
      const res = await API.get(`/api/admin/users/${selectedUser.id}/openrouter-key`);
      setRevealedOpenRouterKey(res.data.key);
    } catch (err) {
      if (err.response?.status === 404) {
        setRevealError('No OpenRouter key on this user, or you are not authorized to reveal it.');
      } else {
        setRevealError(err.response?.data?.detail || 'Failed to reveal key');
      }
    }
    setRevealLoading(false);
  };

  // #662: Non-OR BYOK key reveal state. One object keyed by provider name
  // so we don't blow up the component with 3 separate state triplets.
  const [byokState, setByokState] = useState({});  // { github: {key, loading, error, copied}, ... }
  const setByokField = (provider, patch) =>
    setByokState(prev => ({ ...prev, [provider]: { ...(prev[provider] || {}), ...patch } }));

  const revealByokKey = async (provider) => {
    setByokField(provider, { loading: true, error: null });
    try {
      const res = await API.get(`/api/admin/users/${selectedUser.id}/${provider}-key`);
      setByokField(provider, { key: res.data.key, loading: false });
    } catch (err) {
      const msg = err.response?.status === 404
        ? `No ${provider} key on this user, or you are not authorized to reveal it.`
        : (err.response?.data?.detail || `Failed to reveal ${provider} key`);
      setByokField(provider, { error: msg, loading: false });
    }
  };

  const copyByokKey = (provider) => {
    const key = byokState[provider]?.key;
    if (!key) return;
    navigator.clipboard.writeText(key);
    setByokField(provider, { copied: true });
    setTimeout(() => setByokField(provider, { copied: false }), 2000);
  };

  const hideByokKey = (provider) => {
    setByokField(provider, { key: null, copied: false });
  };

  // #659: admin-triggered OR key regeneration via platform management key.
  // Destructive — invalidates the current key on OpenRouter's side.
  const regenerateOpenRouterKey = async () => {
    setRegenLoading(true);
    setShowRegenConfirm(false);
    setRegenError(null);
    setRegenSuccess(null);
    try {
      const res = await API.post(`/api/admin/users/${selectedUser.id}/regenerate-openrouter-key`);
      // Hide any previously revealed plaintext — it's stale now
      setRevealedOpenRouterKey(null);
      // Refresh the drawer via the parent's loader so the new masked key shows
      if (onRefreshDrawer) {
        await onRefreshDrawer();
      }
      setRegenSuccess(`Key regenerated for ${res.data.target_email || 'user'}. Use Reveal to see the new plaintext.`);
      setTimeout(() => setRegenSuccess(null), 5000);
    } catch (err) {
      setRegenError(err.response?.data?.detail || err.message || 'Regeneration failed');
    }
    setRegenLoading(false);
  };

  const copyOpenRouterKey = () => {
    if (!revealedOpenRouterKey) return;
    navigator.clipboard.writeText(revealedOpenRouterKey);
    setOrKeyCopied(true);
    setTimeout(() => setOrKeyCopied(false), 2000);
  };

  const hideOpenRouterKey = () => {
    setRevealedOpenRouterKey(null);
    setOrKeyCopied(false);
  };

  const adjustCredits = async () => {
    const delta = parseInt(creditDelta);
    if (isNaN(delta) || !creditReason.trim()) return;
    setCreditAdjusting(true);
    try {
      await API.post(`/api/admin/users/${selectedUser.id}/credits/adjust`, {
        delta,
        reason: creditReason,
      });
      setCreditDelta('');
      setCreditReason('');
      setShowCreditForm(false);
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Credit adjust error:', err);
    }
    setCreditAdjusting(false);
  };

  const handleAction = async (action) => {
    setActionLoading(action);
    try {
      switch (action) {
        case 'change-plan': {
          const plan = changingPlan;
          await API.put(`/api/admin/users/${selectedUser.id}/plan`, { plan });
          setChangingPlan(false);
          break;
        }
        case 'change-role': {
          const role = changingRole;
          await API.put(`/api/admin/users/${selectedUser.id}/role`, { role });
          setChangingRole(false);
          break;
        }
        case 'disable':
          await API.put(`/api/admin/users/${selectedUser.id}/disable`);
          break;
        case 'enable':
          await API.put(`/api/admin/users/${selectedUser.id}/enable`);
          break;
        case 'reset-2fa':
          await API.delete(`/api/admin/users/${selectedUser.id}/2fa`);
          break;
        case 'force-reauth':
          await API.post(`/api/admin/users/${selectedUser.id}/force-reauth`);
          break;
        case 'force-password-change':
          await API.post(`/api/admin/users/${selectedUser.id}/reset-password`, { force_change_on_login: true });
          break;
        case 'delete': {
          if (deleteConfirmEmail !== selectedUser.email) return;
          const delRes = await API.delete(`/api/admin/users/${selectedUser.id}`, { data: { confirm_email: selectedUser.email } });
          const d = delRes.data?.deleted || {};
          const summary = Object.entries(d).filter(([,v]) => v > 0).map(([k,v]) => `${v} ${k}`).join(', ');
          if (summary) alert(`Deleted: ${summary}`);
          onClose();
          break;
        }
        default: break;
      }
      // #797: refresh list AND drawer. Without onRefreshDrawer the drawer
      // renders against stale drawerData and the change looks like a no-op.
      if (onRefresh) await onRefresh();
      if (onRefreshDrawer) await onRefreshDrawer();
    } catch (err) {
      console.error(`Action ${action} error:`, err);
      alert(err.response?.data?.detail || `${action} failed`);
    }
    setActionLoading(null);
  };

  const TABS = [
    { id: 'account', label: 'Account', icon: Users },
    { id: 'api-keys', label: 'API Keys', icon: Key },
    { id: 'projects', label: 'Projects', icon: FolderTree },
    { id: 'usage', label: 'Usage & Credits', icon: CreditCard },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'danger', label: 'Danger Zone', icon: AlertTriangle },
  ];

  const ROLE_COLORS = { fray_super_admin: '#FF4D00', super_admin: '#22C55E', fray_admin: '#A855F7', admin: '#A855F7', user: '#A0A0A0' };
  const PLAN_COLORS = { enterprise: '#22C55E', pro: '#FF4D00', student: '#3B82F6', free: '#A0A0A0' };
  const d = drawerData || {};
  // #797: drawerData is the fresh per-user fetch; selectedUser is the
  // list-row snapshot that doesn't update when we mutate the user (plan
  // change, role change, disable/enable). Prefer drawerData fields for
  // anything that can change in the drawer so the UI reflects the write
  // after onRefreshDrawer fires, instead of showing the stale list row.
  const u = { ...(selectedUser || {}), ...d };

  const LoadingSkeleton = () => (
    <div className="space-y-4 p-6">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="h-4 bg-[#2A2A2A] rounded animate-pulse" style={{ width: `${60 + Math.random() * 40}%` }} />
      ))}
    </div>
  );

  return (
    <AnimatePresence>
      {selectedUser && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40"
            onClick={onClose}
          />
          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-xl bg-[#0F0F0F] border-l border-[#2A2A2A] z-50 flex flex-col overflow-hidden"
            data-testid="user-detail-drawer"
            role="dialog"
            aria-label={`User details: ${u.name || u.email}`}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#2A2A2A] shrink-0 gap-2">
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-bold font-['Barlow_Condensed'] truncate">{u.name || u.email}</h2>
                <p className="text-xs text-[#A0A0A0] font-mono truncate">{u.email}</p>
              </div>
              {/* #1137 Task 4.1: Platform impersonation entry point. Hidden
                  when target == self (you can't impersonate yourself), and
                  hidden for disabled accounts since the backend will refuse
                  to mint a grant against an inactive target anyway. */}
              {u.id !== currentUserId && !u.is_disabled && (
                <button
                  data-testid={`impersonate-user-${u.id}`}
                  onClick={() => { setImpersonateError(null); setImpersonateTarget(u); }}
                  className="px-3 py-1.5 text-xs uppercase font-bold border border-[#A61E17] text-[#A61E17] hover:bg-[#A61E17] hover:text-[#F5F1E8] font-['Barlow_Condensed'] shrink-0"
                  title="Impersonate this user"
                >
                  Impersonate
                </button>
              )}
              <button onClick={onClose} className="p-2 text-[#A0A0A0] hover:text-[#F5F5F5] shrink-0" aria-label="Close user details">
                <X className="w-5 h-5" />
              </button>
            </div>
            {impersonateError && (
              <div
                className="px-4 py-2 bg-[#A61E17]/10 border-b border-[#A61E17]/30 text-xs text-[#A61E17] font-mono"
                data-testid="impersonate-error"
              >
                {impersonateError}
              </div>
            )}

            {/* Badges row */}
            <div className="flex items-center gap-2 px-4 py-2 border-b border-[#2A2A2A] shrink-0">
              <span className="text-[9px] px-2 py-0.5 border font-mono" style={{
                color: ROLE_COLORS[u.role] || '#A0A0A0',
                borderColor: (ROLE_COLORS[u.role] || '#A0A0A0') + '40',
              }}>{u.role === 'fray_super_admin' ? 'FRAY SUPER ADMIN' : u.role === 'super_admin' ? 'SUPER ADMIN' : u.role === 'fray_admin' ? 'FRAY ADMIN' : u.role === 'admin' ? 'ADMIN' : (u.role || 'user').toUpperCase()}</span>
              <span className="text-[9px] px-2 py-0.5 border font-mono" style={{
                color: PLAN_COLORS[u.plan] || '#A0A0A0',
                borderColor: (PLAN_COLORS[u.plan] || '#A0A0A0') + '40',
              }}>{(u.plan || 'free').toUpperCase()}</span>
              {u.is_disabled ? (
                <span className="text-[9px] px-2 py-0.5 border font-mono text-[#EF4444] border-[#EF4444]/40">DISABLED</span>
              ) : (
                <span className="text-[9px] px-2 py-0.5 border font-mono text-[#22C55E] border-[#22C55E]/40">ACTIVE</span>
              )}
              {u.totp_enabled && <span className="text-[9px] px-2 py-0.5 border font-mono text-[#22C55E] border-[#22C55E]/40">2FA</span>}
            </div>

            {/* Tab bar */}
            <div className="flex border-b border-[#2A2A2A] shrink-0 overflow-x-auto">
              {TABS.map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-mono whitespace-nowrap border-b-2 -mb-px transition-colors ${
                    activeTab === t.id ? 'text-[#FF4D00] border-[#FF4D00]' : 'text-[#A0A0A0] border-transparent hover:text-[#F5F5F5]'
                  }`} aria-selected={activeTab === t.id} role="tab">
                  <t.icon className="w-3 h-3" />
                  {t.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {drawerLoading ? <LoadingSkeleton /> : (
                <>
                  {/* ── Account Tab ── */}
                  {activeTab === 'account' && (
                    <div className="p-4 space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
                          <label className="text-[9px] font-mono text-[#A0A0A0] block mb-1">USER ID</label>
                          <div className="flex items-center gap-2">
                            <code className="text-xs font-mono text-[#F5F5F5] truncate">{u.id}</code>
                            <button onClick={() => { navigator.clipboard.writeText(u.id); }}
                              className="text-[#A0A0A0] hover:text-[#FF4D00] shrink-0" aria-label="Copy user ID">
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
                          <label className="text-[9px] font-mono text-[#A0A0A0] block mb-1">BILLING MODE</label>
                          <span className="text-xs font-mono text-[#F5F5F5]">{u.billing_mode || 'managed'}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
                          <label className="text-[9px] font-mono text-[#A0A0A0] block mb-1">CREATED</label>
                          <span className="text-xs font-['Inter'] text-[#F5F5F5]">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}</span>
                        </div>
                        <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
                          <label className="text-[9px] font-mono text-[#A0A0A0] block mb-1">LAST ACTIVE</label>
                          <span className="text-xs font-['Inter'] text-[#F5F5F5]">{d.last_active ? new Date(d.last_active).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}</span>
                        </div>
                      </div>

                      {/* Change Plan */}
                      <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
                        <label className="text-[9px] font-mono text-[#A0A0A0] block mb-2">CHANGE PLAN</label>
                        <div className="flex items-center gap-2">
                          <select
                            value={changingPlan || u.plan || 'free'}
                            onChange={e => setChangingPlan(e.target.value)}
                            className="flex-1 px-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]"
                          >
                            <option value="free">Free</option>
                            <option value="student">Student</option>
                            <option value="pro">Pro</option>
                            <option value="team">Team</option>
                            <option value="enterprise">Enterprise</option>
                            <option value="fray">Fray (Platform)</option>
                          </select>
                          <button onClick={() => handleAction('change-plan')}
                            disabled={actionLoading === 'change-plan' || !changingPlan || changingPlan === (u.plan || 'free')}
                            className="px-3 py-1.5 bg-[#FF4D00] text-black text-xs font-medium disabled:opacity-30">
                            {actionLoading === 'change-plan' ? '...' : 'Apply'}
                          </button>
                        </div>
                      </div>

                      {/* Change Role */}
                      {u.id !== currentUserId && (
                        <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
                          <label className="text-[9px] font-mono text-[#A0A0A0] block mb-2">CHANGE ROLE</label>
                          <div className="flex items-center gap-2">
                            <select
                              value={changingRole || u.role || 'user'}
                              onChange={e => setChangingRole(e.target.value)}
                              className="flex-1 px-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]"
                            >
                              {(u.plan === 'fray'
                                ? [['user', 'User'], ['fray_admin', 'Fray Admin'], ['fray_super_admin', 'Fray Super Admin']]
                                : u.plan === 'enterprise'
                                  ? [['user', 'User'], ['admin', 'Admin'], ['super_admin', 'Super Admin']]
                                  : u.plan === 'team'
                                    ? [['user', 'User'], ['admin', 'Admin']]
                                    : [['user', 'User']]
                              ).map(([v, label]) => (
                                <option key={v} value={v}>{label}</option>
                              ))}
                            </select>
                            <button onClick={() => handleAction('change-role')}
                              disabled={actionLoading === 'change-role' || !changingRole || changingRole === (u.role || 'user')}
                              className="px-3 py-1.5 bg-[#A855F7] text-white text-xs font-medium disabled:opacity-30">
                              {actionLoading === 'change-role' ? '...' : 'Apply'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Disable/Enable toggle */}
                      {u.id !== currentUserId && (
                        <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between">
                          <div>
                            <label className="text-[9px] font-mono text-[#A0A0A0] block">ACCOUNT STATUS</label>
                            <span className={`text-xs font-mono ${u.is_disabled ? 'text-[#EF4444]' : 'text-[#22C55E]'}`}>
                              {u.is_disabled ? 'Disabled' : 'Active'}
                            </span>
                          </div>
                          <button onClick={() => handleAction(u.is_disabled ? 'enable' : 'disable')}
                            disabled={actionLoading === 'disable' || actionLoading === 'enable'}
                            className={`px-3 py-1.5 text-xs font-medium border ${u.is_disabled
                              ? 'text-[#22C55E] border-[#22C55E]/30 hover:bg-[#22C55E]/10'
                              : 'text-[#EF4444] border-[#EF4444]/30 hover:bg-[#EF4444]/10'
                            }`}>
                            {u.is_disabled ? 'Enable' : 'Disable'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── API Keys Tab ── */}
                  {activeTab === 'api-keys' && (
                    <div className="p-4 space-y-4">
                      {/* Generate key form */}
                      <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
                        <label className="text-[9px] font-mono text-[#A0A0A0] block mb-2">GENERATE NEW KEY</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newKeyName}
                            onChange={e => setNewKeyName(e.target.value)}
                            className="flex-1 px-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]"
                            placeholder="Key name (e.g. production-api)"
                          />
                          <button onClick={generateApiKey} disabled={!newKeyName.trim()}
                            className="px-3 py-1.5 bg-[#FF4D00] text-black text-xs font-medium disabled:opacity-30 flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Generate
                          </button>
                        </div>
                      </div>

                      {/* Show generated key */}
                      {generatedKey && (
                        <div className="p-3 bg-[#22C55E]/10 border border-[#22C55E]/30">
                          <label className="text-[9px] font-mono text-[#22C55E] block mb-1">NEW API KEY — COPY NOW, SHOWN ONLY ONCE</label>
                          <div className="flex items-center gap-2">
                            <code className="text-xs font-mono text-[#22C55E] flex-1 break-all">{generatedKey}</code>
                            <button onClick={() => {
                              navigator.clipboard.writeText(generatedKey);
                              setKeyCopied(true);
                              setTimeout(() => setKeyCopied(false), 2000);
                            }} className="px-2 py-1 text-[9px] font-mono border border-[#22C55E]/30 text-[#22C55E] shrink-0" aria-label="Copy API key">
                              {keyCopied ? 'Copied!' : 'Copy'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* OpenRouter Key (#597, reveal added #644) */}
                      {drawerData?.openrouter_key && (
                        <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
                          <label className="text-[9px] font-mono text-[#A0A0A0] block mb-2">OPENROUTER KEY</label>
                          <div className="flex items-center gap-3 flex-wrap">
                            <code className="text-xs font-mono text-[#F5F5F5] break-all">
                              {revealedOpenRouterKey || drawerData.openrouter_key.masked}
                            </code>
                            <span className={`text-[9px] px-1.5 py-0.5 border font-mono ${
                              drawerData.openrouter_key.source === 'byok'
                                ? 'text-[#3B82F6] border-[#3B82F6]/30'
                                : 'text-[#22C55E] border-[#22C55E]/30'
                            }`}>
                              {drawerData.openrouter_key.source === 'byok' ? 'BYOK' : 'AUTO-PROVISIONED'}
                            </span>
                            {!revealedOpenRouterKey ? (
                              <button
                                onClick={revealOpenRouterKey}
                                disabled={revealLoading}
                                className="text-[9px] font-mono px-2 py-1 border border-[#FF4D00]/40 text-[#FF4D00] hover:bg-[#FF4D00]/10 disabled:opacity-40"
                                aria-label="Reveal OpenRouter key plaintext"
                              >
                                {revealLoading ? 'Revealing…' : 'Reveal'}
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={copyOpenRouterKey}
                                  className="text-[9px] font-mono px-2 py-1 border border-[#22C55E]/40 text-[#22C55E] hover:bg-[#22C55E]/10"
                                  aria-label="Copy OpenRouter key"
                                >
                                  {orKeyCopied ? 'Copied!' : 'Copy'}
                                </button>
                                <button
                                  onClick={hideOpenRouterKey}
                                  className="text-[9px] font-mono px-2 py-1 border border-[#2A2A2A] text-[#A0A0A0] hover:bg-[#2A2A2A]"
                                  aria-label="Hide OpenRouter key"
                                >
                                  Hide
                                </button>
                              </>
                            )}
                            {/* #659: Regenerate button — destructive, always visible, gated by confirmation */}
                            <button
                              onClick={() => setShowRegenConfirm(true)}
                              disabled={regenLoading}
                              className="text-[9px] font-mono px-2 py-1 border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 disabled:opacity-40"
                              aria-label="Regenerate OpenRouter key (destructive)"
                              data-testid="or-key-regen-btn"
                            >
                              {regenLoading ? 'Regenerating…' : 'Regenerate'}
                            </button>
                          </div>
                          {revealError && (
                            <div className="mt-2 text-[9px] font-mono text-[#EF4444]">{revealError}</div>
                          )}
                          {revealedOpenRouterKey && (
                            <div className="mt-2 text-[9px] font-mono text-[#A0A0A0]">
                              Plaintext shown. Reveal action has been audited.
                            </div>
                          )}
                          {/* #659: confirmation modal for destructive regeneration */}
                          {showRegenConfirm && (
                            <div className="mt-2 p-3 bg-[#EF4444]/5 border border-[#EF4444]/30 text-[10px] font-mono"
                              data-testid="or-key-regen-confirm-panel">
                              <div className="text-[#EF4444] mb-2">
                                Regenerating invalidates this user's current OpenRouter key immediately.
                                Any running fray using the old key will fail.
                                Proceed?
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={regenerateOpenRouterKey}
                                  className="px-3 py-1 min-h-[44px] bg-[#EF4444] text-white"
                                  data-testid="or-key-regen-confirm"
                                >
                                  Yes, regenerate
                                </button>
                                <button
                                  onClick={() => setShowRegenConfirm(false)}
                                  className="px-3 py-1 min-h-[44px] border border-[#2A2A2A] text-[#A0A0A0]"
                                  data-testid="or-key-regen-cancel"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                          {/* #659: surface regen errors inline (not console-only) */}
                          {regenError && (
                            <div className="mt-2 text-[9px] font-mono text-[#EF4444] p-2 border border-[#EF4444]/30 bg-[#EF4444]/5"
                              data-testid="or-key-regen-error">
                              Regeneration failed: {regenError}
                            </div>
                          )}
                          {regenSuccess && (
                            <div className="mt-2 text-[9px] font-mono text-[#22C55E] p-2 border border-[#22C55E]/30 bg-[#22C55E]/5"
                              data-testid="or-key-regen-success">
                              {regenSuccess}
                            </div>
                          )}
                        </div>
                      )}

                      {/* #662: BYOK provider key panels — GitHub, Perplexity, Exa. Customer-supplied keys
                           the admin may need to view for debugging. Regenerate is NOT available for these
                           (customer owns the key; they update via their Settings). */}
                      {[
                        { provider: 'github', label: 'GITHUB TOKEN', hasFlag: 'has_github_key' },
                        { provider: 'perplexity', label: 'PERPLEXITY KEY', hasFlag: 'has_perplexity_key' },
                        { provider: 'exa', label: 'EXA KEY', hasFlag: 'has_exa_key' },
                      ].map(({ provider, label, hasFlag }) => {
                        const st = byokState[provider] || {};
                        const hasKey = drawerData?.[hasFlag];
                        return (
                          <div key={provider} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]" data-testid={`byok-panel-${provider}`}>
                            <label className="text-[9px] font-mono text-[#A0A0A0] block mb-2">{label}</label>
                            {!hasKey ? (
                              <div className="text-[9px] font-mono text-[#A0A0A0] italic">(none configured)</div>
                            ) : (
                              <>
                                <div className="flex items-center gap-3 flex-wrap">
                                  <code className="text-xs font-mono text-[#F5F5F5] break-all">
                                    {st.key || '••••••••'}
                                  </code>
                                  <span className="text-[9px] px-1.5 py-0.5 border font-mono text-[#3B82F6] border-[#3B82F6]/30">
                                    BYOK
                                  </span>
                                  {!st.key ? (
                                    <button
                                      onClick={() => revealByokKey(provider)}
                                      disabled={st.loading}
                                      className="text-[9px] font-mono px-2 py-1 border border-[#FF4D00]/40 text-[#FF4D00] hover:bg-[#FF4D00]/10 disabled:opacity-40 min-h-[44px]"
                                      data-testid={`byok-reveal-${provider}`}
                                    >
                                      {st.loading ? 'Revealing…' : 'Reveal'}
                                    </button>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => copyByokKey(provider)}
                                        className="text-[9px] font-mono px-2 py-1 border border-[#22C55E]/40 text-[#22C55E] hover:bg-[#22C55E]/10 min-h-[44px]"
                                        data-testid={`byok-copy-${provider}`}
                                      >
                                        {st.copied ? 'Copied!' : 'Copy'}
                                      </button>
                                      <button
                                        onClick={() => hideByokKey(provider)}
                                        className="text-[9px] font-mono px-2 py-1 border border-[#2A2A2A] text-[#A0A0A0] hover:bg-[#2A2A2A] min-h-[44px]"
                                        data-testid={`byok-hide-${provider}`}
                                      >
                                        Hide
                                      </button>
                                    </>
                                  )}
                                </div>
                                {st.error && (
                                  <div className="mt-2 text-[9px] font-mono text-[#EF4444]">{st.error}</div>
                                )}
                                {st.key && (
                                  <div className="mt-2 text-[9px] font-mono text-[#A0A0A0]">
                                    Plaintext shown. Reveal action has been audited. Customer-supplied — no Regenerate (customer updates via Settings).
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}

                      {/* Fray API Keys table (#644: hashed-storage notice) */}
                      <div className="p-2 bg-[#0F0F0F] border border-[#2A2A2A] text-[9px] font-mono text-[#A0A0A0]">
                        Fray MCP tokens are stored as SHA256 hashes. Plaintext cannot be revealed — use Regenerate to issue a new token.
                      </div>
                      {apiKeysLoading ? <LoadingSkeleton /> : (
                        <div className="border border-[#2A2A2A] overflow-hidden">
                          <table className="w-full text-xs">
                            <thead className="bg-[#1A1A1A]">
                              <tr>
                                <th className="text-left p-2 font-mono text-[#A0A0A0]">Name</th>
                                <th className="text-left p-2 font-mono text-[#A0A0A0]">Prefix</th>
                                <th className="text-left p-2 font-mono text-[#A0A0A0]">Created</th>
                                <th className="text-right p-2 font-mono text-[#A0A0A0]">Uses</th>
                                <th className="text-right p-2 font-mono text-[#A0A0A0]">Status</th>
                                <th className="p-2"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {apiKeys.map((k, i) => (
                                <tr key={k.id || i} className="border-t border-[#2A2A2A]">
                                  <td className="p-2 font-mono text-[#F5F5F5]">{k.name}</td>
                                  <td className="p-2 font-mono text-[#A0A0A0]">{k.prefix || k.key_prefix || '***'}</td>
                                  <td className="p-2 font-mono text-[#A0A0A0]">{k.created_at ? new Date(k.created_at).toLocaleDateString() : '-'}</td>
                                  <td className="p-2 text-right font-mono">{k.use_count ?? k.uses ?? 0}</td>
                                  <td className="p-2 text-right">
                                    <span className={`text-[9px] px-1.5 py-0.5 border font-mono ${k.is_active !== false ? 'text-[#22C55E] border-[#22C55E]/30' : 'text-[#EF4444] border-[#EF4444]/30'}`}>
                                      {k.is_active !== false ? 'ACTIVE' : 'REVOKED'}
                                    </span>
                                  </td>
                                  <td className="p-2">
                                    {k.is_active !== false && (
                                      <button onClick={() => revokeApiKey(k.id || k._id)}
                                        className="text-[9px] font-mono text-[#EF4444] hover:text-[#EF4444]/80">Revoke</button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                              {apiKeys.length === 0 && (
                                <tr><td colSpan={6} className="p-4 text-center text-[#A0A0A0] text-xs">No API keys</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Projects Tab (#1129) ── */}
                  {activeTab === 'projects' && (
                    <div className="p-4 space-y-3" data-testid="user-projects-tab">
                      <div className="text-[10px] font-mono text-[#A0A0A0]">
                        Projects owned by this user. Visibility into another user's
                        projects is admin-only and audit-logged on every load.
                      </div>
                      {userProjectsLoading ? (
                        <div className="flex items-center gap-2 text-[#A0A0A0] py-4 text-xs font-mono">
                          <Loader2 className="w-3 h-3 animate-spin" /> Loading projects…
                        </div>
                      ) : userProjectsError ? (
                        <div className="p-3 bg-[#A61E17]/10 border border-[#A61E17]/40 text-xs font-mono text-[#F5F1E8]">
                          {userProjectsError}
                        </div>
                      ) : userProjects.length === 0 ? (
                        <div className="text-center py-6 text-[#A0A0A0] font-mono text-xs border border-dashed border-[#2A2A2A] rounded">
                          No projects.
                        </div>
                      ) : (
                        <ul className="space-y-1" data-testid="user-projects-list">
                          {userProjects.map((p) => (
                            <li
                              key={p.id}
                              className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#FF4D00]/40 rounded text-xs font-mono"
                              data-testid={`user-project-row-${p.id}`}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <span className="font-bold text-[#F5F1E8] truncate">{p.name}</span>
                                {p.created_at && (
                                  <span className="text-[10px] text-[#A0A0A0] shrink-0">
                                    {new Date(p.created_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                              {p.description && (
                                <p className="text-[10px] text-[#A0A0A0] mt-1 line-clamp-2">
                                  {p.description}
                                </p>
                              )}
                              {p.workspace_id && (
                                <p className="text-[9px] text-[#A0A0A0] mt-1">
                                  workspace: <code className="text-[#00D4FF]">{p.workspace_id.slice(0, 8)}…</code>
                                </p>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* ── Usage & Credits Tab ── */}
                  {activeTab === 'usage' && (
                    <div className="p-4 space-y-4">
                      {/* Credit balance */}
                      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] text-center">
                        <label className="text-[9px] font-mono text-[#A0A0A0] block mb-1">CREDIT BALANCE</label>
                        <span className="text-3xl font-bold font-['Barlow_Condensed'] text-[#FF4D00]">
                          {d.credits ?? d.credit_balance ?? u.credits ?? 0}
                        </span>
                      </div>

                      {/* Adjust credits */}
                      {!showCreditForm ? (
                        <button onClick={() => setShowCreditForm(true)}
                          className="w-full px-3 py-2 border border-[#FF4D00]/30 text-[#FF4D00] text-xs font-mono hover:bg-[#FF4D00]/10">
                          Adjust Credits
                        </button>
                      ) : (
                        <div className="p-3 bg-[#1A1A1A] border border-[#FF4D00]/30 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-[9px] font-mono text-[#A0A0A0] block mb-1">DELTA (+ or -)</label>
                              <input type="number" value={creditDelta} onChange={e => setCreditDelta(e.target.value)}
                                className="w-full px-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]"
                                placeholder="+100 or -50" />
                            </div>
                            <div>
                              <label className="text-[9px] font-mono text-[#A0A0A0] block mb-1">REASON</label>
                              <input type="text" value={creditReason} onChange={e => setCreditReason(e.target.value)}
                                className="w-full px-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]"
                                placeholder="Manual adjustment" />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={adjustCredits} disabled={creditAdjusting || !creditDelta || !creditReason.trim()}
                              className="px-3 py-1.5 bg-[#FF4D00] text-black text-xs font-medium disabled:opacity-30">
                              {creditAdjusting ? 'Adjusting...' : 'Apply'}
                            </button>
                            <button onClick={() => setShowCreditForm(false)}
                              className="px-3 py-1.5 border border-[#2A2A2A] text-xs text-[#A0A0A0]">Cancel</button>
                          </div>
                        </div>
                      )}

                      {/* Fray stats */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] text-center">
                          <label className="text-[9px] font-mono text-[#A0A0A0] block mb-1">TOTAL FRAYS</label>
                          <span className="text-xl font-bold font-['Barlow_Condensed']">{d.total_frays ?? d.fray_count ?? 0}</span>
                        </div>
                        <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] text-center">
                          <label className="text-[9px] font-mono text-[#A0A0A0] block mb-1">COMPLETED</label>
                          <span className="text-xl font-bold font-['Barlow_Condensed'] text-[#22C55E]">{d.completed_frays ?? 0}</span>
                        </div>
                      </div>

                      {/* Recent frays */}
                      {(d.recent_frays || []).length > 0 && (
                        <div>
                          <label className="text-[9px] font-mono text-[#A0A0A0] block mb-2">RECENT FRAYS</label>
                          <div className="space-y-1">
                            {d.recent_frays.slice(0, 10).map((f, i) => (
                              <div key={f.id || i} className="p-2 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between">
                                <span className="text-xs font-['Inter'] text-[#F5F5F5] truncate flex-1 mr-2">{f.scenario || f.topic || f.title || 'Untitled'}</span>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className={`text-[9px] px-1.5 py-0.5 border font-mono ${f.status === 'completed' ? 'text-[#22C55E] border-[#22C55E]/30' : 'text-[#A0A0A0] border-[#2A2A2A]'}`}>
                                    {f.status || 'unknown'}
                                  </span>
                                  <span className="text-[9px] font-mono text-[#A0A0A0]">{f.created_at ? new Date(f.created_at).toLocaleDateString() : ''}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Security Tab ── */}
                  {activeTab === 'security' && (
                    <div className="p-4 space-y-4">
                      {/* 2FA Status */}
                      <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between">
                        <div>
                          <label className="text-[9px] font-mono text-[#A0A0A0] block">TWO-FACTOR AUTH</label>
                          <span className={`text-xs font-mono ${u.totp_enabled ? 'text-[#22C55E]' : 'text-[#A0A0A0]'}`}>
                            {u.totp_enabled ? 'Enabled' : 'Not enabled'}
                          </span>
                        </div>
                        {u.totp_enabled && u.id !== currentUserId && (
                          <button onClick={() => handleAction('reset-2fa')}
                            disabled={actionLoading === 'reset-2fa'}
                            className="px-3 py-1.5 text-xs font-mono border text-[#3B82F6] border-[#3B82F6]/30 hover:bg-[#3B82F6]/10">
                            {actionLoading === 'reset-2fa' ? '...' : 'Reset 2FA'}
                          </button>
                        )}
                      </div>

                      {/* Force Password Change */}
                      {u.id !== currentUserId && (
                        <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between">
                          <div>
                            <label className="text-[9px] font-mono text-[#A0A0A0] block">PASSWORD</label>
                            <span className="text-xs font-['Inter'] text-[#F5F5F5]">Force user to change password on next login</span>
                          </div>
                          <button onClick={() => handleAction('force-password-change')}
                            disabled={actionLoading === 'force-password-change'}
                            className="px-3 py-1.5 text-xs font-mono border text-[#FF4D00] border-[#FF4D00]/30 hover:bg-[#FF4D00]/10">
                            {actionLoading === 'force-password-change' ? '...' : 'Force Change'}
                          </button>
                        </div>
                      )}

                      {/* Force Reauth */}
                      {u.id !== currentUserId && (
                        <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between">
                          <div>
                            <label className="text-[9px] font-mono text-[#A0A0A0] block">SESSIONS</label>
                            <span className="text-xs font-['Inter'] text-[#F5F5F5]">Invalidate all sessions and API keys</span>
                          </div>
                          <button onClick={() => handleAction('force-reauth')}
                            disabled={actionLoading === 'force-reauth'}
                            className="px-3 py-1.5 text-xs font-mono border text-[#A0A0A0] border-[#2A2A2A] hover:text-[#F5F5F5] hover:bg-[#2A2A2A]">
                            {actionLoading === 'force-reauth' ? '...' : 'Force Reauth'}
                          </button>
                        </div>
                      )}

                      {/* Permissions placeholder */}
                      <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] border-dashed">
                        <label className="text-[9px] font-mono text-[#A0A0A0] block mb-1">PERMISSIONS</label>
                        <span className="text-xs text-[#A0A0A0] font-['Inter']">Granular permissions coming soon.</span>
                      </div>
                    </div>
                  )}

                  {/* ── Danger Zone Tab ── */}
                  {activeTab === 'danger' && (
                    <div className="p-4 space-y-4">
                      <div className="p-4 border-2 border-[#EF4444]/30 bg-[#EF4444]/5 space-y-4">
                        <h3 className="text-sm font-bold font-['Barlow_Condensed'] text-[#EF4444] flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" /> Danger Zone
                        </h3>

                        {/* Disable/Enable */}
                        {u.id !== currentUserId && (
                          <div className="flex items-center justify-between p-3 bg-[#0F0F0F] border border-[#EF4444]/20">
                            <div>
                              <span className="text-xs font-medium text-[#F5F5F5] block">{u.is_disabled ? 'Enable Account' : 'Disable Account'}</span>
                              <span className="text-[9px] text-[#A0A0A0] font-mono">{u.is_disabled ? 'Allow user to log in again' : 'Block login, invalidate sessions'}</span>
                            </div>
                            <button onClick={() => handleAction(u.is_disabled ? 'enable' : 'disable')}
                              disabled={actionLoading === 'disable' || actionLoading === 'enable'}
                              className={`px-3 py-1.5 text-xs font-medium border ${u.is_disabled
                                ? 'text-[#22C55E] border-[#22C55E]/30 hover:bg-[#22C55E]/10'
                                : 'text-[#EF4444] border-[#EF4444]/30 hover:bg-[#EF4444]/10'
                              }`}>
                              {u.is_disabled ? 'Enable' : 'Disable'}
                            </button>
                          </div>
                        )}

                        {/* Delete Account */}
                        {u.id !== currentUserId && (
                          <div className="p-3 bg-[#0F0F0F] border border-[#EF4444]/20 space-y-2">
                            <div>
                              <span className="text-xs font-medium text-[#EF4444] block">Delete Account Permanently</span>
                              <span className="text-[9px] text-[#A0A0A0] font-mono">All user data, frays, and API keys will be destroyed. This cannot be undone.</span>
                            </div>
                            <div>
                              <label className="text-[9px] font-mono text-[#A0A0A0] block mb-1">Type "{u.email}" to confirm</label>
                              <input type="text" value={deleteConfirmEmail} onChange={e => setDeleteConfirmEmail(e.target.value)}
                                className="w-full px-3 py-1.5 bg-[#0F0F0F] border border-[#EF4444]/20 text-xs font-mono focus:outline-none focus:border-[#EF4444] text-[#EF4444]"
                                placeholder={u.email} />
                            </div>
                            <button onClick={() => handleAction('delete')}
                              disabled={actionLoading === 'delete' || deleteConfirmEmail !== u.email}
                              className="w-full px-3 py-2 bg-[#EF4444] text-white text-xs font-medium disabled:opacity-30 flex items-center justify-center gap-2">
                              <Trash2 className="w-3 h-3" />
                              {actionLoading === 'delete' ? 'Deleting...' : 'Delete Account'}
                            </button>
                          </div>
                        )}

                        {u.id === currentUserId && (
                          <p className="text-xs text-[#A0A0A0] font-['Inter']">You cannot perform destructive actions on your own account.</p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
          {/* #1137 Task 4.1: confirm modal for platform impersonation. Rendered
              outside the drawer's overflow container so the backdrop covers the
              whole viewport. The 200ms enable delay lives inside the modal. */}
          {impersonateTarget && (
            <ImpersonateConfirmModal
              target={impersonateTarget}
              scope="platform"
              onCancel={() => {
                // N1: arm the cancellation ref so an in-flight POST
                // success branch will revoke + bail instead of persisting.
                impersonateCancelledRef.current = true;
                setImpersonateTarget(null);
                setImpersonateError(null);
              }}
              onConfirm={async () => {
                if (impersonateLoading) return;
                setImpersonateLoading(true);
                setImpersonateError(null);
                try {
                  const resp = await API.post('/api/admin/impersonate/grant', {
                    target_user_id: impersonateTarget.id,
                    reason: 'platform admin debug',
                  });
                  // N1: if the user clicked Cancel while we were in flight,
                  // close the just-created grant best-effort and bail —
                  // never leak an active impersonation past Cancel.
                  if (impersonateCancelledRef.current) {
                    if (resp?.data?.grant_id) {
                      API.post(
                        `/api/impersonate/grant/${resp.data.grant_id}/revoke`,
                        { reason: 'cancelled_in_flight' },
                      ).catch(() => {});
                    }
                    return;
                  }
                  setImpersonateTarget(null);
                  // startImpersonation persists to sessionStorage and
                  // navigates to '/' so role-gated routes re-evaluate.
                  startImpersonation(resp.data);
                } catch (err) {
                  if (impersonateCancelledRef.current) return;
                  setImpersonateError(
                    err?.response?.data?.detail || err?.message || 'Impersonation failed'
                  );
                  setImpersonateTarget(null);
                } finally {
                  setImpersonateLoading(false);
                }
              }}
            />
          )}
        </>
      )}
    </AnimatePresence>
  );
}

// #740: the broken character-by-character renderer that lived here is gone.
// The Platform Admin → Audit Log tab now mounts the polished AuditSection
// component used by the Enterprise Audit tab — same filters, flyout,
// export, defensive JSONB parsing — but with `workspaceId={null}` so the
// query covers every workspace's events instead of one. The /api/admin/
// audit-log endpoint that this tab used to hit was deleted in the same PR;
// the consolidated /api/enterprise/audit/events now serves both scopes.
function AuditLogTab() {
  return (
    <div data-testid="audit-log-tab">
      <AuditSection workspaceId={null} />
    </div>
  );
}

// Providers tab (#669) — unified platform-level LLM/tool provider management.
// Mirrors the Enterprise Providers UX: one block per provider, with UI
// sections (runtime key, management key, auto-provision toggle, model sync)
// rendered conditionally based on capabilities returned by the server.
function ProvidersTab() {
  const [providers, setProviders] = useState([]);
  const [addable, setAddable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAdd, setSelectedAdd] = useState('');

  const fetchProviders = async () => {
    try {
      const r = await API.get('/api/admin/providers');
      setProviders(r.data.providers || []);
      setAddable(r.data.addable || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load providers');
    }
    setLoading(false);
  };

  useEffect(() => { fetchProviders(); }, []);

  const updateProvider = async (slug, payload) => {
    await API.put(`/api/admin/providers/${slug}`, payload);
    await fetchProviders();
  };

  const deleteProvider = async (slug) => {
    if (!window.confirm(`Remove ${slug} and clear its stored keys?`)) return;
    await API.delete(`/api/admin/providers/${slug}`);
    await fetchProviders();
  };

  const addProvider = async () => {
    if (!selectedAdd) return;
    // Surface the new provider in the list by saving an empty placeholder —
    // since runtime_key is required, we instead seed by simply re-fetching.
    // The user then fills and saves the key. We fake "add" by optimistically
    // filtering it into the list.
    const row = addable.find(p => p.slug === selectedAdd);
    if (row) {
      setProviders(prev => [...prev, {
        slug: row.slug,
        name: row.name,
        capabilities: {
          supports_runtime_key: true, supports_management_key: false,
          supports_auto_provision: false, supports_model_sync: false,
          removable: true,
        },
        runtime_configured: false, runtime_preview: null,
        management_configured: false, management_preview: null,
        auto_provision: null, warnings: {},
      }]);
      setAddable(prev => prev.filter(p => p.slug !== selectedAdd));
    }
    setSelectedAdd('');
  };

  if (loading) return <div className="animate-shimmer h-40" />;

  return (
    <div data-testid="providers-tab">
      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-4">
        <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-1 text-[#FF4D00]">⚙ Providers</h3>
        <p className="text-xs text-[#A0A0A0]">
          Configure LLM and tool provider API keys at the platform level. OpenRouter is the primary provider for fray execution.
          The <span className="text-[#00D4FF]">Runtime key</span> is the fallback key for system-shared operations (Fray of the Day, model roster sync, health checks) and <span className="text-[#F2A900]">plan=fray</span> users without a per-user key.
          The <span className="text-[#F2A900]">Management key</span> is used to mint per-user OpenRouter sub-keys via auto-provisioning.
        </p>
        {error && <p className="text-xs text-[#EF4444] mt-2">{error}</p>}
      </div>

      <div className="space-y-4">
        {providers.map(p => (
          <ProviderCard key={p.slug} provider={p}
            onUpdate={payload => updateProvider(p.slug, payload)}
            onDelete={() => deleteProvider(p.slug)} />
        ))}
      </div>

      {addable.length > 0 && (
        <div className="mt-6 p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold font-['Barlow_Condensed']">OTHER PROVIDERS</h4>
          </div>
          <p className="text-[11px] text-[#A0A0A0] mb-3">
            Add API keys for direct provider access. Keys are stored securely; runtime fallback routing will be wired in future updates.
          </p>
          <div className="flex gap-2">
            <select value={selectedAdd} onChange={e => setSelectedAdd(e.target.value)}
              className="flex-1 bg-[#0F0F0F] border border-[#2A2A2A] px-3 py-2 text-xs font-mono focus:border-[#FF4D00] focus:outline-none">
              <option value="">Select a provider...</option>
              {addable.map(p => <option key={p.slug} value={p.slug}>{p.name}</option>)}
            </select>
            <button onClick={addProvider} disabled={!selectedAdd}
              className="px-4 py-2 bg-[#FF4D00] text-black text-xs font-bold disabled:opacity-50">
              + Add Provider
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProviderCard({ provider, onUpdate, onDelete }) {
  const caps = provider.capabilities || {};
  const [runtimeInput, setRuntimeInput] = useState('');
  const [managementInput, setManagementInput] = useState('');
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (caps.supports_model_sync) {
      API.get('/api/models/sync-status').then(r => setSyncStatus(r.data)).catch(() => {});
    }
  }, [caps.supports_model_sync]);

  const save = async (field, value) => {
    setSaving(field);
    setError(null);
    try {
      await onUpdate({ [field === 'runtime' ? 'runtime_key' : 'management_key']: value });
      setSaved(field);
      if (field === 'runtime') setRuntimeInput('');
      else setManagementInput('');
      setTimeout(() => setSaved(null), 2000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    }
    setSaving(null);
  };

  const toggleAutoProvision = async () => {
    setSaving('auto');
    setError(null);
    try {
      await onUpdate({ auto_provision: !provider.auto_provision });
    } catch (e) {
      setError(e.response?.data?.detail || 'Toggle failed');
    }
    setSaving(null);
  };

  const syncModels = async () => {
    setSyncing(true);
    try {
      await API.post('/api/admin/models/sync');
      const r = await API.get('/api/models/sync-status');
      setSyncStatus(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Sync failed');
    }
    setSyncing(false);
  };

  const runtimeStatus = provider.runtime_configured ? provider.runtime_preview : 'Not configured';
  const mgmtStatus = provider.management_configured ? provider.management_preview : 'Not configured';

  return (
    <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-4" data-testid={`provider-${provider.slug}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-[#FF4D00]">🔑</span>
          <span className="text-sm font-semibold font-['Barlow_Condensed']">{provider.name}</span>
          {provider.slug === 'openrouter' && (
            <span className="text-[10px] text-[#A0A0A0] ml-1">Primary provider for fray execution</span>
          )}
        </div>
        {caps.removable && (
          <button onClick={onDelete}
            className="text-[10px] text-[#A0A0A0] hover:text-[#EF4444] font-mono">
            Remove
          </button>
        )}
      </div>

      {/* Runtime key */}
      {caps.supports_runtime_key && (
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-2">
            <span className={`w-2 h-2 rounded-full ${provider.runtime_configured ? 'bg-[#22C55E]' : 'bg-[#EF4444]'}`} />
            <span className="text-xs font-mono text-[#A0A0A0]">
              Runtime key: {provider.runtime_configured
                ? <span className="text-[#22C55E]">{runtimeStatus}</span>
                : <span className="text-[#EF4444]">{runtimeStatus}</span>}
            </span>
          </div>
          {provider.warnings?.runtime && (
            <p className="text-[10px] text-[#EF4444] mb-1">⚠ {provider.warnings.runtime}</p>
          )}
          <div className="flex gap-2">
            <input type="password" value={runtimeInput} onChange={e => setRuntimeInput(e.target.value)}
              placeholder={`${provider.slug === 'openrouter' ? 'sk-or-v1-...' : provider.slug === 'perplexity' ? 'pplx-...' : provider.slug === 'exa' ? 'exa-...' : 'api-key-...'}`}
              className="flex-1 bg-[#0F0F0F] border border-[#2A2A2A] px-3 py-2 text-xs font-mono focus:border-[#FF4D00] focus:outline-none" />
            <button onClick={() => save('runtime', runtimeInput)} disabled={saving === 'runtime' || !runtimeInput.trim()}
              className="px-4 py-2 bg-[#FF4D00] text-black text-xs font-medium disabled:opacity-50">
              {saved === 'runtime' ? 'Saved' : saving === 'runtime' ? '...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Management key + auto-provision */}
      {caps.supports_management_key && (
        <div className="mt-4 pt-4 border-t border-[#2A2A2A]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-[#F2A900]">Auto-provisioning</span>
            {caps.supports_auto_provision && (
              <button onClick={toggleAutoProvision} disabled={saving === 'auto'}
                className="text-xs font-mono" title="Toggle auto-provisioning">
                {provider.auto_provision
                  ? <span className="text-[#00D4FF]">● On</span>
                  : <span className="text-[#A0A0A0]">○ Off</span>}
              </button>
            )}
          </div>
          <p className="text-[10px] text-[#A0A0A0] mb-2">
            Each user gets their own {provider.name} sub-key. Individual usage tracked separately.
          </p>
          <div className="flex items-center gap-2 mb-2">
            <span className={`w-2 h-2 rounded-full ${provider.management_configured ? 'bg-[#22C55E]' : 'bg-[#EF4444]'}`} />
            <span className="text-xs font-mono text-[#A0A0A0]">
              Management key: {provider.management_configured
                ? <span className="text-[#22C55E]">{mgmtStatus}</span>
                : <span className="text-[#EF4444]">{mgmtStatus}</span>}
            </span>
          </div>
          {provider.warnings?.management && (
            <p className="text-[10px] text-[#EF4444] mb-1">⚠ {provider.warnings.management}</p>
          )}
          <div className="flex gap-2">
            <input type="password" value={managementInput} onChange={e => setManagementInput(e.target.value)}
              placeholder="sk-or-v1-... (Management API Key)"
              className="flex-1 bg-[#0F0F0F] border border-[#2A2A2A] px-3 py-2 text-xs font-mono focus:border-[#FF4D00] focus:outline-none" />
            <button onClick={() => save('management', managementInput)} disabled={saving === 'management' || !managementInput.trim()}
              className="px-4 py-2 bg-[#FF4D00] text-black text-xs font-medium disabled:opacity-50">
              {saved === 'management' ? 'Saved' : saving === 'management' ? '...' : 'Save'}
            </button>
          </div>
          {provider.slug === 'openrouter' && (
            <p className="text-[10px] text-[#A0A0A0] mt-1">
              Get your management key from{' '}
              <a href="https://openrouter.ai/settings/keys" target="_blank" rel="noopener noreferrer" className="text-[#00D4FF] underline">
                openrouter.ai/settings/keys
              </a>
            </p>
          )}
        </div>
      )}

      {/* Model sync */}
      {caps.supports_model_sync && (
        <div className="mt-4 pt-4 border-t border-[#2A2A2A] flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold">Model Sync</div>
            <div className="text-[10px] text-[#A0A0A0]">
              {syncStatus?.last_synced_at
                ? `Last synced ${new Date(syncStatus.last_synced_at).toLocaleString()}`
                : 'Models have not been synced yet'}
            </div>
          </div>
          <button onClick={syncModels} disabled={syncing}
            className="px-3 py-2 border border-[#2A2A2A] text-[#A0A0A0] text-xs hover:border-[#FF4D00] hover:text-[#F5F1E8] disabled:opacity-50">
            {syncing ? 'Syncing...' : '↻ Sync Available Models'}
          </button>
        </div>
      )}

      {error && <p className="text-[11px] text-[#EF4444] mt-2">{error}</p>}
    </div>
  );
}

// Provider-style keys (OpenRouter, Perplexity, Exa) moved to the Providers
// tab (#669). This tab now only manages infrastructure concerns.
const INFRA_KEY_DEFS = [
  { id: 'platform_github_token', name: 'GitHub', description: 'OAuth login and webhook processing', placeholder: 'ghp_...' },
  { id: 'stripe_secret_key', name: 'Stripe Secret Key', description: 'Billing backend (test or live)', placeholder: 'sk_test_... or sk_live_...' },
  { id: 'stripe_publishable_key', name: 'Stripe Publishable Key', description: 'Frontend checkout (test or live)', placeholder: 'pk_test_... or pk_live_...' },
  { id: 'aa_api_key', name: 'Artificial Analysis API Key', description: 'Model intelligence scores (IQ sync in Models tab)', placeholder: 'aa_...' },
  // #734 / #725: AES-256-GCM master key for client-side encryption of every
  // fray-contexts / fray-renders binary. If lost, every encrypted Storage
  // object becomes permanently unrecoverable — stash the generated value in
  // a password manager BEFORE pasting. Rotation is not yet supported.
  {
    id: 'fray_storage_master_key_v1',
    name: 'Storage Master Key (v1)',
    description: (
      "32 bytes base64url-encoded. Generate locally with: "
      + "python -c 'import secrets, base64; print(base64.urlsafe_b64encode(secrets.token_bytes(32)).decode())'. "
      + "Store the plaintext in your password manager before pasting — if lost, encrypted uploads cannot be recovered."
    ),
    placeholder: 'e.g. k8xQz2pF_N4vLm9sR7bY5tH6jW3eKd1cAhBgTfUeOqA=',
    sensitive: true,
  },
];

function PlatformKeysTab() {
  const [keys, setKeys] = useState({});
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    API.get('/api/admin/platform-keys').then(r => {
      setKeys(r.data.keys || {});
      setKeyWarnings(r.data.warnings || {});
    }).finally(() => setLoading(false));
  }, []);

  const [keyWarnings, setKeyWarnings] = useState({});
  const [saveError, setSaveError] = useState(null);

  const saveKey = async (keyId) => {
    const val = values[keyId];
    if (!val?.trim()) return;
    setSaving(keyId);
    setSaveError(null);
    try {
      await API.put('/api/admin/platform-keys', { [keyId]: val });
      setSaved(keyId);
      setValues(prev => ({ ...prev, [keyId]: '' }));
      const res = await API.get('/api/admin/platform-keys');
      setKeys(res.data.keys || {});
      setTimeout(() => setSaved(null), 2000);
    } catch (e) {
      setSaveError(e.response?.data?.detail || 'Save failed');
    }
    setSaving(null);
  };

  if (loading) return <div className="animate-shimmer h-40" />;

  const defs = INFRA_KEY_DEFS;
  const configured = defs.filter(d => keys[d.id] && !keyWarnings[d.id]).length;

  return (
    <div data-testid="platform-keys-tab">
      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-4">
        <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-1">Platform Infrastructure</h3>
        <p className="text-xs text-[#A0A0A0]">Keys that power platform services (billing, OAuth). LLM and tool provider keys are managed under the Providers tab.</p>
        {saveError && <p className="text-xs text-[#EF4444] mt-2">{saveError}</p>}
      </div>

      {/* Status summary */}
      <div className="flex items-center gap-3 mb-4 px-4 py-2 bg-[#1A1A1A] border border-[#2A2A2A]">
        <span className={`text-xs font-mono ${configured === defs.length ? 'text-[#22C55E]' : 'text-[#F2A900]'}`}>
          {configured} of {defs.length} keys configured
        </span>
        <div className="flex-1 h-1 bg-[#2A2A2A] rounded overflow-hidden">
          <div className="h-full rounded transition-all" style={{ width: `${(configured / defs.length) * 100}%`, background: configured === defs.length ? '#22C55E' : '#F2A900' }} />
        </div>
      </div>

      <div className="space-y-3">
        {defs.map(def => (
          <div key={def.id} className={`p-4 bg-[#1A1A1A] border ${keyWarnings[def.id] ? 'border-[#EF4444]/50' : 'border-[#2A2A2A]'}`} data-testid={`platform-key-${def.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${keyWarnings[def.id] ? 'bg-[#EF4444]' : keys[def.id] ? 'bg-[#22C55E]' : 'bg-[#EF4444]'}`} />
                <span className="text-sm font-medium font-['Barlow_Condensed']">{def.name}</span>
              </div>
              {keys[def.id]
                ? <span className="text-[9px] font-mono text-[#A0A0A0]">{keys[def.id]}</span>
                : <span className="text-[9px] font-mono text-[#EF4444]">Missing</span>
              }
            </div>
            {keyWarnings[def.id] && (
              <p className="text-[10px] text-[#EF4444] font-mono mb-2 p-1.5 bg-[#EF4444]/10 border border-[#EF4444]/20 rounded">
                ⚠ {keyWarnings[def.id]}
              </p>
            )}
            <p className="text-[10px] text-[#A0A0A0] mb-3">{def.description}</p>
            <div className="flex gap-2">
              <input type="password" value={values[def.id] || ''} onChange={e => setValues(prev => ({ ...prev, [def.id]: e.target.value }))}
                className="flex-1 px-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]"
                placeholder={def.placeholder} />
              <button onClick={() => saveKey(def.id)} disabled={saving === def.id || !values[def.id]?.trim()}
                className="px-4 py-1.5 bg-[#FF4D00] text-black text-xs font-medium disabled:opacity-50">
                {saved === def.id ? 'Saved' : saving === def.id ? '...' : 'Save'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* #1255: Preview Access password rotation. Lives in Platform
          Infrastructure because the gate fronts the whole site, same
          tier as billing/OAuth keys. Hashes stored bcrypt; admin only
          ever sees metadata (configured / source / fallback present),
          never the hash itself. */}
      <PreviewAccessCard />
    </div>
  );
}

/**
 * #1255: Preview Access password card.
 * Two gates (site, investor); operator can rotate either one without
 * a frontend redeploy. Backend stores bcrypt hashes in `platform_secrets`.
 * Pre-rotation, the gate falls back to the legacy SITE_PASSWORD /
 * INVESTOR_PASSWORD env vars; the metadata GET surfaces which source
 * is currently authoritative.
 */
function PreviewAccessCard() {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState({ site: '', investor: '' });
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await API.get('/api/admin/platform/preview-passwords');
      setMeta(r.data || {});
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load preview passwords');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const savePassword = async (kind) => {
    const val = values[kind];
    if (!val || val.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setSaving(kind);
    setError(null);
    try {
      await API.put('/api/admin/platform/preview-passwords', { kind, password: val });
      setSaved(kind);
      setValues(prev => ({ ...prev, [kind]: '' }));
      await reload();
      setTimeout(() => setSaved(null), 2000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(null);
  };

  const KINDS = [
    { id: 'site',     label: 'Site Preview Password',
      description: 'Gates getfray.ai for the public preview. Required to enter the site at all.' },
    { id: 'investor', label: 'Investor Section Password',
      description: 'Additional gate on /investors. Required on top of the site password to access investor materials.' },
  ];

  return (
    <div className="mt-6" data-testid="preview-access-card">
      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-4">
        <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-1">Preview Access</h3>
        <p className="text-xs text-[#A0A0A0]">
          Site-wide gate password for getfray.ai. Stored as bcrypt hash; rotation takes effect immediately, no redeploy.
        </p>
        {error && <p className="text-xs text-[#EF4444] mt-2">{error}</p>}
      </div>

      {loading ? (
        <div className="animate-shimmer h-32" />
      ) : (
        <div className="space-y-3">
          {KINDS.map(k => {
            const m = (meta || {})[k.id] || {};
            const ok = m.configured;
            const source = m.source || 'unconfigured';
            const sourceColor = source === 'db' ? '#22C55E' : source === 'env' ? '#F2A900' : '#EF4444';
            return (
              <div key={k.id} className={`p-4 bg-[#1A1A1A] border ${ok ? 'border-[#2A2A2A]' : 'border-[#F2A900]/40'}`} data-testid={`preview-pw-${k.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: sourceColor }} />
                    <span className="text-sm font-medium font-['Barlow_Condensed']">{k.label}</span>
                  </div>
                  <span className="text-[9px] font-mono text-[#A0A0A0]">
                    source: <span style={{ color: sourceColor }}>{source}</span>
                    {source === 'env' && m.env_fallback_present && ' (legacy env)'}
                  </span>
                </div>
                <p className="text-[10px] text-[#A0A0A0] mb-3">{k.description}</p>
                {!ok && source !== 'db' && (
                  <p className="text-[10px] text-[#F2A900] font-mono mb-2 p-1.5 bg-[#F2A900]/10 border border-[#F2A900]/20 rounded">
                    {source === 'env'
                      ? '⚠ Currently using legacy build-time env var. Set a new password here to migrate to DB-stored bcrypt hash.'
                      : '⚠ No password configured AND no env fallback present — the gate denies all submissions until you set one.'}
                  </p>
                )}
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={values[k.id] || ''}
                    onChange={e => setValues(prev => ({ ...prev, [k.id]: e.target.value }))}
                    className="flex-1 px-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]"
                    placeholder="New password (≥6 chars)"
                  />
                  <button
                    onClick={() => savePassword(k.id)}
                    disabled={saving === k.id || !values[k.id] || values[k.id].length < 6}
                    className="px-4 py-1.5 bg-[#FF4D00] text-black text-xs font-medium disabled:opacity-50"
                    data-testid={`preview-pw-save-${k.id}`}
                  >
                    {saved === k.id ? 'Saved' : saving === k.id ? '...' : 'Set'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/**
 * #1258 + #1260: Jim Menu — fray_super_admin-only inline control
 * surface for managing OR Keychain accounts and launching Fray + SIT
 * debate sessions with CC routed through OpenRouter (so the
 * orchestrator runs on OR credit, not the Max plan — unblocks long
 * sessions when Max usage is exhausted).
 *
 * Data plane: local Python daemon at http://127.0.0.1:8765
 * (scripts/launcher/jim_menu_server.py).
 * UI plane: this React component, calling daemon API directly via fetch.
 *
 * Cross-origin from https://getfray.ai → http://127.0.0.1:8765 works
 * via Chrome's Private Network Access protocol — daemon's CORSMiddleware
 * opts in via `allow_private_network=True`. Browsers without PNA fall
 * back to the "Open in new tab" link automatically (fetch fails →
 * unreachable state → fallback panel renders).
 */
const JIM_DAEMON_URL = 'http://127.0.0.1:8765';

async function jimFetch(path, opts) {
  const r = await fetch(`${JIM_DAEMON_URL}${path}`, {
    mode: 'cors',
    credentials: 'omit',
    ...(opts || {}),
    headers: {
      'Content-Type': 'application/json',
      ...((opts || {}).headers || {}),
    },
  });
  const text = await r.text();
  let data;
  try { data = JSON.parse(text); } catch (_) { data = text; }
  if (!r.ok) {
    const detail = (data && data.detail) ? data.detail : `HTTP ${r.status}`;
    const err = new Error(detail);
    err.status = r.status;
    err.data = data;
    throw err;
  }
  return data;
}

function JimMenuTab() {
  const [daemonState, setDaemonState] = useState({ status: 'checking', info: null });
  const [subTab, setSubTab] = useState('keys');

  const probe = useCallback(async () => {
    try {
      const data = await jimFetch('/api/health');
      setDaemonState({ status: 'up', info: data });
    } catch (e) {
      setDaemonState({ status: 'down', info: { error: String(e?.message || e) } });
    }
  }, []);

  useEffect(() => {
    probe();
    const id = setInterval(probe, 15000);
    return () => clearInterval(id);
  }, [probe]);

  const isUp = daemonState.status === 'up';
  const statusColor = isUp ? '#22C55E' : daemonState.status === 'down' ? '#A61E17' : '#F2A900';
  const statusLabel = isUp ? 'reachable' : daemonState.status === 'down' ? 'not running' : 'checking…';

  if (!isUp) {
    return (
      <div data-testid="jim-menu-tab">
        <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-4">
          <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-1">Jim Menu — Local Control Surface</h3>
          <p className="text-xs text-[#A0A0A0] leading-relaxed">
            Local daemon currently unreachable. Start it via your Desktop launcher and this tab populates with the OR Keys, Fray Run, and SIT Run sub-tabs.
          </p>
        </div>
        <div className="p-4 bg-[#1A1A1A] border border-[#F2A900]/40">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium font-['Barlow_Condensed']">Daemon Status</span>
            <span className="text-xs font-mono" style={{ color: statusColor }}>● {statusLabel}</span>
          </div>
          <p className="text-[10px] font-mono text-[#A0A0A0] mb-3">
            Start it via: <code className="text-[#00D4FF]">~/Desktop/Open Jim Menu.command</code>
          </p>
          <a
            href={JIM_DAEMON_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-4 py-2 bg-[#2A2A2A] text-[#A0A0A0] text-xs font-medium uppercase tracking-wider"
            data-testid="jim-menu-launch"
          >
            Try opening daemon URL →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="jim-menu-tab">
      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-4 flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-1">Jim Menu</h3>
          <p className="text-xs text-[#A0A0A0]">OR Keychain manager + Fray/SIT run launcher (CC routed through OpenRouter).</p>
        </div>
        <span className="text-xs font-mono" style={{ color: statusColor }}>● daemon v{daemonState.info?.version}</span>
      </div>

      <div className="flex gap-1 mb-4 border-b border-[#2A2A2A]">
        {[
          { id: 'keys', label: 'OR Keys' },
          { id: 'fray', label: 'Fray Run' },
          { id: 'sit',  label: 'SIT Run' },
        ].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 text-xs font-mono border-b-2 -mb-px ${subTab === t.id ? 'text-[#FF4D00] border-[#FF4D00]' : 'text-[#A0A0A0] border-transparent'}`}
            data-testid={`jim-subtab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'keys' && <JimKeysSubTab />}
      {subTab === 'fray' && <JimFraySubTab />}
      {subTab === 'sit'  && <JimSitSubTab />}
    </div>
  );
}

function JimKeysSubTab() {
  const [keys, setKeys] = useState([]);
  const [balances, setBalances] = useState({});
  const [account, setAccount] = useState('');
  const [keyVal, setKeyVal] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const reload = useCallback(async () => {
    try { setKeys(await jimFetch('/api/keys')); }
    catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const probe = async (acct) => {
    setBalances(prev => ({ ...prev, [acct]: { loading: true } }));
    try {
      const r = await jimFetch(`/api/keys/${encodeURIComponent(acct)}/balance`);
      setBalances(prev => ({ ...prev, [acct]: r }));
    } catch (e) {
      setBalances(prev => ({ ...prev, [acct]: { ok: false, error: e.message } }));
    }
  };

  const save = async () => {
    setError(null);
    if (!account || !keyVal) { setError('Account + key both required'); return; }
    setBusy('save');
    try {
      await jimFetch('/api/keys', { method: 'POST', body: JSON.stringify({ account, key: keyVal }) });
      setAccount(''); setKeyVal('');
      await reload();
    } catch (e) { setError(e.message); }
    setBusy(null);
  };

  const del = async (acct) => {
    if (!window.confirm(`Delete Keychain account "${acct}"?`)) return;
    setBusy(`del-${acct}`);
    try {
      await jimFetch(`/api/keys/${encodeURIComponent(acct)}`, { method: 'DELETE' });
      await reload();
    } catch (e) { setError(e.message); }
    setBusy(null);
  };

  return (
    <div className="space-y-4">
      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
        <h4 className="text-sm font-semibold font-['Barlow_Condensed'] mb-2">Keychain accounts (service: fray-openrouter)</h4>
        {error && <p className="text-[11px] text-[#EF4444] mb-2">{error}</p>}
        <table className="w-full text-[11px] font-mono">
          <thead>
            <tr className="text-[#A0A0A0] text-left">
              <th className="py-1">Account</th><th>Key (masked)</th><th>Balance</th><th></th>
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 && (
              <tr><td colSpan={4} className="py-3 text-[#A0A0A0]">No keys yet. Add one below.</td></tr>
            )}
            {keys.map(k => {
              const b = balances[k.account] || {};
              const balLabel = b.loading ? '…'
                : b.ok ? `$${(b.remaining || 0).toFixed(2)} of $${(b.total_credits || 0).toFixed(2)}`
                : b.error ? b.error : '—';
              const balColor = b.loading ? '#A0A0A0'
                : b.ok && (b.remaining || 0) > 10 ? '#22C55E'
                : b.ok && (b.remaining || 0) > 0  ? '#F2A900'
                : (b.ok || b.error) ? '#EF4444' : '#A0A0A0';
              return (
                <tr key={k.account} className="border-t border-[#2A2A2A]">
                  <td className="py-2"><strong className="text-[#F5F1E8]">{k.account}</strong></td>
                  <td className="text-[#A0A0A0]">{k.prefix}…{k.suffix} ({k.length} chars)</td>
                  <td style={{ color: balColor }}>{balLabel}</td>
                  <td className="text-right">
                    <button onClick={() => probe(k.account)} className="px-2 py-1 bg-[#2A2A2A] text-[#F5F1E8] text-[10px] mr-1">Test</button>
                    <button onClick={() => del(k.account)} disabled={busy === `del-${k.account}`} className="px-2 py-1 bg-[#A61E17] text-[#F5F1E8] text-[10px]">Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
        <h4 className="text-sm font-semibold font-['Barlow_Condensed'] mb-2">Add or Update Key</h4>
        <p className="text-[10px] text-[#A0A0A0] mb-3">
          Account name should match the Fray <code className="text-[#00D4FF]">key_profile</code> convention. Mirrored to <code className="text-[#00D4FF]">shared-conf/api_key_profiles.json</code> so debate-mcp picks new keys up per-call.
        </p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <input value={account} onChange={e => setAccount(e.target.value)} placeholder="account (e.g. psa)"
            className="px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] text-xs font-mono text-[#F5F1E8] focus:outline-none focus:border-[#FF4D00]" />
          <input type="password" value={keyVal} onChange={e => setKeyVal(e.target.value)} placeholder="sk-or-v1-..."
            className="px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] text-xs font-mono text-[#F5F1E8] focus:outline-none focus:border-[#FF4D00]" />
        </div>
        <button onClick={save} disabled={busy === 'save'} className="px-4 py-2 bg-[#FF4D00] text-black text-xs font-medium font-['Barlow_Condensed'] uppercase tracking-wider disabled:opacity-50">
          {busy === 'save' ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function JimFraySubTab() {
  const [sessions, setSessions] = useState([]);
  const [keys, setKeys] = useState([]);
  const [models, setModels] = useState([]);
  const [sessionName, setSessionName] = useState('');
  const [sessionData, setSessionData] = useState(null);
  const [picks, setPicks] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    Promise.all([
      jimFetch('/api/sessions/fray').then(r => setSessions(r.sessions || [])),
      jimFetch('/api/keys').then(setKeys),
      jimFetch('/api/models').then(r => setModels(r.models || [])),
    ]).catch(e => setError(e.message));
  }, []);

  useEffect(() => {
    if (!sessionName) { setSessionData(null); return; }
    jimFetch(`/api/sessions/fray/${encodeURIComponent(sessionName)}`)
      .then(d => {
        setSessionData(d);
        const rc = d.run_config || {};
        const members = (d.roles?.members || []).map((r, i) => ({
          name: r.name,
          role_description: r.role_description || '',
          model: (rc.committee || [])[i]?.model || '',
        }));
        setPicks({
          team_lead: rc.team_lead || '',
          team_agent: rc.team_agent || '',
          moderator: rc.moderator || '',
          key_profile: rc.key_profile || (keys[0]?.account || 'debate_internal'),
          consensus_threshold: rc.consensus_threshold || 0.75,
          max_rounds: rc.max_rounds || 5,
          member_max_tokens: rc.member_max_tokens || 2048,
          members,
        });
      })
      .catch(e => setError(e.message));
  }, [sessionName, keys]);

  const setMemberModel = (i, model) => {
    setPicks(prev => ({ ...prev, members: prev.members.map((m, idx) => idx === i ? { ...m, model } : m) }));
  };

  const buildPayload = () => ({
    session_kind: 'fray',
    team_lead: picks.team_lead,
    team_agent: picks.team_agent,
    committee: picks.members,
    moderator: picks.moderator,
    key_profile: picks.key_profile,
    consensus_threshold: parseFloat(picks.consensus_threshold),
    max_rounds: parseInt(picks.max_rounds, 10),
    member_max_tokens: parseInt(picks.member_max_tokens, 10),
  });

  const save = async () => {
    setError(null); setInfo(null); setBusy('save');
    try {
      await jimFetch(`/api/sessions/fray/${encodeURIComponent(sessionName)}/run-config`, { method: 'POST', body: JSON.stringify(buildPayload()) });
      setInfo(`Saved run-config for ${sessionName}`);
    } catch (e) { setError(e.message); }
    setBusy(null);
  };

  const launch = async () => {
    setError(null); setInfo(null); setBusy('launch');
    try {
      await jimFetch(`/api/sessions/fray/${encodeURIComponent(sessionName)}/run-config`, { method: 'POST', body: JSON.stringify(buildPayload()) });
      const r = await jimFetch('/api/launch/fray', { method: 'POST', body: JSON.stringify({ session: sessionName }) });
      setInfo(`Launched ${sessionName} — iTerm window open. ${r.members?.length || 0} member(s).`);
    } catch (e) { setError(e.message); }
    setBusy(null);
  };

  const modelOption = (m) => `${m.model_name} [${m.tier || '?'}] (${m.intelligence_score || 0})`;
  const noRoles = sessionData && !sessionData.roles;
  const ready = sessionData && sessionData.roles && picks.members?.length > 0;

  return (
    <div className="space-y-4">
      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
        <h4 className="text-sm font-semibold font-['Barlow_Condensed'] mb-2">Fray Run Builder</h4>
        <p className="text-[10px] text-[#A0A0A0] mb-3">
          Pick a session, assign models per role, save run-config, launch in iTerm. Sets <code className="text-[#00D4FF]">ANTHROPIC_API_KEY</code> from the chosen Keychain account so Claude Code routes through OpenRouter.
        </p>
        {error && <p className="text-[11px] text-[#EF4444] mb-2">{error}</p>}
        {info && <p className="text-[11px] text-[#22C55E] mb-2">{info}</p>}

        <label className="text-[10px] font-mono text-[#A0A0A0] uppercase">Session</label>
        <select value={sessionName} onChange={e => setSessionName(e.target.value)}
          className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] text-xs font-mono text-[#F5F1E8] focus:outline-none focus:border-[#FF4D00]"
          data-testid="jim-fray-session">
          <option value="">— select session —</option>
          {sessions.map(s => (
            <option key={s.name} value={s.name}>
              {s.name}{s.has_roles ? ' ✓' : ' (no roles)'}{s.has_run_config ? ' [saved]' : ''}
            </option>
          ))}
        </select>

        {noRoles && (
          <p className="text-[10px] text-[#F2A900] mt-3">
            This session has no <code>committee-roles.json</code>. Add one to enable role-aware launching.
          </p>
        )}
      </div>

      {ready && (
        <>
          <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
            <h4 className="text-sm font-semibold font-['Barlow_Condensed'] mb-3">Orchestrator (CC routing)</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-mono text-[#A0A0A0] uppercase">Team lead</label>
                <select value={picks.team_lead} onChange={e => setPicks(p => ({ ...p, team_lead: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] text-xs font-mono text-[#F5F1E8] focus:outline-none focus:border-[#FF4D00]">
                  <option value="">—</option>
                  {models.map(m => <option key={m.model_name} value={m.model_name}>{modelOption(m)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-mono text-[#A0A0A0] uppercase">Team agent</label>
                <select value={picks.team_agent} onChange={e => setPicks(p => ({ ...p, team_agent: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] text-xs font-mono text-[#F5F1E8] focus:outline-none focus:border-[#FF4D00]">
                  <option value="">—</option>
                  {models.map(m => <option key={m.model_name} value={m.model_name}>{modelOption(m)}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
            <h4 className="text-sm font-semibold font-['Barlow_Condensed'] mb-3">Committee members ({picks.members.length})</h4>
            <div className="space-y-3">
              {picks.members.map((m, i) => (
                <div key={i} className="p-3 bg-[#0A0A0A] border border-[#2A2A2A]">
                  <div className="text-xs font-bold text-[#FF4D00] uppercase font-['Barlow_Condensed'] mb-1">{m.name}</div>
                  <p className="text-[10px] text-[#A0A0A0] mb-2 leading-relaxed">{m.role_description}</p>
                  <select value={m.model} onChange={e => setMemberModel(i, e.target.value)}
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] text-xs font-mono text-[#F5F1E8] focus:outline-none focus:border-[#FF4D00]"
                    data-testid={`jim-member-${i}`}>
                    <option value="">— select model —</option>
                    {models.map(mm => <option key={mm.model_name} value={mm.model_name}>{modelOption(mm)}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[10px] font-mono text-[#A0A0A0] uppercase">Moderator</label>
                <select value={picks.moderator} onChange={e => setPicks(p => ({ ...p, moderator: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] text-xs font-mono text-[#F5F1E8] focus:outline-none focus:border-[#FF4D00]">
                  <option value="">—</option>
                  {models.map(m => <option key={m.model_name} value={m.model_name}>{modelOption(m)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-mono text-[#A0A0A0] uppercase">Key profile (Keychain account)</label>
                <select value={picks.key_profile} onChange={e => setPicks(p => ({ ...p, key_profile: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] text-xs font-mono text-[#F5F1E8] focus:outline-none focus:border-[#FF4D00]">
                  {keys.map(k => <option key={k.account} value={k.account}>{k.account}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-mono text-[#A0A0A0] uppercase">Consensus</label>
                <input type="number" step="0.05" min="0" max="1" value={picks.consensus_threshold} onChange={e => setPicks(p => ({ ...p, consensus_threshold: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] text-xs font-mono text-[#F5F1E8] focus:outline-none focus:border-[#FF4D00]" />
              </div>
              <div>
                <label className="text-[10px] font-mono text-[#A0A0A0] uppercase">Max rounds</label>
                <input type="number" min="1" max="10" value={picks.max_rounds} onChange={e => setPicks(p => ({ ...p, max_rounds: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] text-xs font-mono text-[#F5F1E8] focus:outline-none focus:border-[#FF4D00]" />
              </div>
              <div>
                <label className="text-[10px] font-mono text-[#A0A0A0] uppercase">Member tokens</label>
                <input type="number" min="256" max="8192" value={picks.member_max_tokens} onChange={e => setPicks(p => ({ ...p, member_max_tokens: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] text-xs font-mono text-[#F5F1E8] focus:outline-none focus:border-[#FF4D00]" />
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={save} disabled={busy !== null} className="px-4 py-2 bg-[#2A2A2A] text-[#F5F1E8] text-xs font-medium font-['Barlow_Condensed'] uppercase tracking-wider disabled:opacity-50">
              {busy === 'save' ? 'Saving…' : 'Save Run Config'}
            </button>
            <button onClick={launch} disabled={busy !== null} className="px-4 py-2 bg-[#00D4FF] text-black text-xs font-medium font-['Barlow_Condensed'] uppercase tracking-wider disabled:opacity-50"
              data-testid="jim-fray-launch">
              {busy === 'launch' ? 'Launching…' : 'Save + Launch in iTerm'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function JimSitSubTab() {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    jimFetch('/api/sessions/sit').then(setInfo).catch(e => setError(e.message));
  }, []);
  return (
    <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
      <h4 className="text-sm font-semibold font-['Barlow_Condensed'] mb-2">SIT Run Builder</h4>
      {error && <p className="text-[11px] text-[#EF4444]">{error}</p>}
      {info?.available === false && (
        <p className="text-xs text-[#A0A0A0]">{info.note || 'SIT repo not found.'}</p>
      )}
      {info?.available && (
        <div>
          <p className="text-[11px] text-[#A0A0A0] mb-2">Found {info.sessions.length} SIT sessions in <code className="text-[#00D4FF]">{info.repo}</code>.</p>
          <p className="text-[11px] text-[#A0A0A0]">Full SIT launcher in this UI is the next iteration. For now, use <code className="text-[#00D4FF]">~/Desktop/Start SIT OR.command</code>.</p>
        </div>
      )}
    </div>
  );
}


// model_tiers_allowed is cumulative in DB: ['free','economy','standard','premium']
// 'economy' is displayed as 'Budget' to match OpenRouter vocabulary.
// UI collapses the array to a single max-tier select for simplicity.
const MODEL_TIER_OPTIONS = [
  { value: 'free',     label: 'Free only', arr: ['free'] },
  { value: 'budget',   label: 'Budget',    arr: ['free', 'economy'] },
  { value: 'standard', label: 'Standard',  arr: ['free', 'economy', 'standard'] },
  { value: 'any',      label: 'Any',       arr: ['free', 'economy', 'standard', 'premium'] },
];

function toMaxTier(arr) {
  if (!arr || arr.length === 0) return 'free';
  if (arr.includes('premium')) return 'any';
  if (arr.includes('standard')) return 'standard';
  if (arr.includes('economy') || arr.includes('budget')) return 'budget';
  return 'free';
}

function fromMaxTier(tier) {
  return MODEL_TIER_OPTIONS.find(o => o.value === tier)?.arr || ['free', 'economy', 'standard', 'premium'];
}

function slugify(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

const BLANK_NEW_TIER = {
  name: '', slug: '', price_monthly: '', price_annual: '',
  max_voices: '', max_phases: '', max_rounds_per_phase: '', max_frays_per_month: '',
  model_tier: 'any',
};

function TiersTab() {
  const [tiers, setTiers] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTier, setEditingTier] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [addingTier, setAddingTier] = useState(false);
  const [newTier, setNewTier] = useState(BLANK_NEW_TIER);
  const [addError, setAddError] = useState(null);

  const reloadTiers = () => API.get('/api/tiers').then(r => setTiers(r.data.tiers || []));

  useEffect(() => {
    Promise.all([
      API.get('/api/tiers').then(r => setTiers(r.data.tiers || [])),
      API.get('/api/features').then(r => setFeatures(r.data.features || [])),
    ]).finally(() => setLoading(false));
  }, []);

  const startEdit = (tier) => {
    setEditingTier(tier.id);
    setEditValues({
      price_monthly: tier.price_monthly ?? '',
      price_annual: tier.price_annual ?? '',
      max_voices: tier.max_voices ?? '',
      max_phases: tier.max_phases ?? '',
      max_rounds_per_phase: tier.max_rounds_per_phase ?? '',
      max_frays_per_month: tier.max_frays_per_month ?? '',
      model_tier: toMaxTier(tier.model_tiers_allowed),
    });
    setSaveError(null);
  };

  const cancelEdit = () => { setEditingTier(null); setEditValues({}); setSaveError(null); };

  const saveTier = async (tierId) => {
    setSaving(true);
    setSaveError(null);
    const NUM_FIELDS = ['price_monthly', 'price_annual', 'max_voices', 'max_phases', 'max_rounds_per_phase', 'max_frays_per_month'];
    const payload = {};
    for (const [k, v] of Object.entries(editValues)) {
      if (k === 'model_tier') {
        payload.model_tiers_allowed = fromMaxTier(v);
      } else if (NUM_FIELDS.includes(k)) {
        payload[k] = v === '' || v === null ? null : parseFloat(v);
      } else {
        payload[k] = v;
      }
    }
    try {
      await API.put(`/api/admin/tiers/${tierId}`, payload);
      setEditingTier(null);
      setEditValues({});
      await reloadTiers();
    } catch (e) {
      setSaveError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveNewTier = async () => {
    setSaving(true);
    setAddError(null);
    const NUM_FIELDS = ['price_monthly', 'price_annual', 'max_voices', 'max_phases', 'max_rounds_per_phase', 'max_frays_per_month'];
    const payload = { slug: newTier.slug || slugify(newTier.name), name: newTier.name, model_tiers_allowed: fromMaxTier(newTier.model_tier) };
    for (const k of NUM_FIELDS) {
      payload[k] = newTier[k] === '' || newTier[k] == null ? null : parseFloat(newTier[k]);
    }
    try {
      await API.post('/api/admin/tiers', payload);
      setAddingTier(false);
      setNewTier(BLANK_NEW_TIER);
      await reloadTiers();
    } catch (e) {
      setAddError(e?.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = async (featureKey, tierId, currentValue) => {
    const tier = tiers.find(t => t.id === tierId);
    if (!tier) return;
    const updated = { ...tier.features, [featureKey]: !currentValue };
    try {
      await API.put(`/api/admin/tiers/${tierId}`, { features: updated });
      await reloadTiers();
    } catch {}
  };

  // #1088 Phase 1 of #1081 — per-tier OR Workspace toggle
  const [orWorkspaceBusy, setOrWorkspaceBusy] = useState(null);
  const [orWorkspaceError, setOrWorkspaceError] = useState(null);

  const provisionOrWorkspace = async (tier) => {
    setOrWorkspaceBusy(tier.slug);
    setOrWorkspaceError(null);
    try {
      const r = await API.post(`/api/admin/tiers/${tier.slug}/provision-or-workspace`);
      if (r?.data?.or_workspace_id) {
        await reloadTiers();
      }
    } catch (e) {
      setOrWorkspaceError(`${tier.slug}: ${e?.response?.data?.detail || 'Provision failed'}`);
    } finally {
      setOrWorkspaceBusy(null);
    }
  };

  const toggleOrWorkspaceEnabled = async (tier) => {
    setOrWorkspaceBusy(tier.slug);
    setOrWorkspaceError(null);
    try {
      await API.put(`/api/admin/tiers/${tier.id}`, {
        or_workspace_enabled: !tier.or_workspace_enabled,
      });
      await reloadTiers();
    } catch (e) {
      setOrWorkspaceError(`${tier.slug}: ${e?.response?.data?.detail || 'Toggle failed'}`);
    } finally {
      setOrWorkspaceBusy(null);
    }
  };

  if (loading) return <div className="animate-shimmer h-40" />;

  const TIER_COLORS = { free: '#A0A0A0', student: '#3B82F6', pro: '#FF4D00', team: '#A855F7', enterprise: '#22C55E' };

  const inputCls = "bg-[#0A0A0A] border border-[#FF4D00] text-[#F5F1E8] font-mono text-xs px-1 py-0.5 rounded focus:outline-none";
  const selectCls = "bg-[#0A0A0A] border border-[#FF4D00] text-[#F5F1E8] font-mono text-xs px-1 py-0.5 rounded focus:outline-none";

  const LimitCell = ({ field, tier }) => {
    const isEditing = editingTier === tier.id;
    const val = tier[field];
    if (!isEditing) return (
      <td className="p-2 text-right font-mono text-[#F5F1E8]">
        {val != null ? val : <span className="text-[#4A4A4A]">∞</span>}
      </td>
    );
    return (
      <td className="p-1 text-right">
        <input type="number" min="1" placeholder="∞"
          value={editValues[field] ?? ''}
          onChange={e => setEditValues(p => ({ ...p, [field]: e.target.value }))}
          className={`w-14 text-right ${inputCls}`} />
      </td>
    );
  };

  const PriceCell = ({ field, tier, prefix = '$' }) => {
    const isEditing = editingTier === tier.id;
    const val = tier[field];
    if (!isEditing) return (
      <td className="p-2 text-right font-mono text-[#F5F1E8]">
        {val != null && val > 0 ? `$${val}` : <span className="text-[#4A4A4A]">—</span>}
      </td>
    );
    return (
      <td className="p-1 text-right">
        <div className="flex items-center justify-end gap-0.5">
          <span className="text-[#4A4A4A] font-mono text-xs">{prefix}</span>
          <input type="number" min="0" step="0.01" placeholder="0"
            value={editValues[field] ?? ''}
            onChange={e => setEditValues(p => ({ ...p, [field]: e.target.value }))}
            className={`w-14 text-right ${inputCls}`} />
        </div>
      </td>
    );
  };

  const ModelTierCell = ({ tier }) => {
    const isEditing = editingTier === tier.id;
    const label = MODEL_TIER_OPTIONS.find(o => o.value === toMaxTier(tier.model_tiers_allowed))?.label || '—';
    if (!isEditing) return <td className="p-2 text-right font-mono text-[#F5F1E8] text-[10px]">{label}</td>;
    return (
      <td className="p-1 text-right">
        <select value={editValues.model_tier || 'any'}
          onChange={e => setEditValues(p => ({ ...p, model_tier: e.target.value }))}
          className={selectCls}>
          {MODEL_TIER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </td>
    );
  };

  return (
    <div data-testid="tiers-tab">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Subscription Tiers</h3>
        <span className="text-[10px] text-[#4A4A4A] font-mono">blank = unlimited · model tier = max allowed</span>
      </div>
      {saveError && <div className="text-xs text-[#A61E17] mb-2 font-mono">{saveError}</div>}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#1A1A1A]">
            <tr>
              <th className="text-left p-2 font-mono text-[#A0A0A0]">Tier</th>
              <th className="text-right p-2 font-mono text-[#A0A0A0]">$/mo</th>
              <th className="text-right p-2 font-mono text-[#A0A0A0]">$/yr</th>
              <th className="text-right p-2 font-mono text-[#A0A0A0]">Voices</th>
              <th className="text-right p-2 font-mono text-[#A0A0A0]">Phases</th>
              <th className="text-right p-2 font-mono text-[#A0A0A0]">Rounds</th>
              <th className="text-right p-2 font-mono text-[#A0A0A0]">Frays/mo</th>
              <th className="text-right p-2 font-mono text-[#A0A0A0]">Models</th>
              <th className="p-2 font-mono text-[#A0A0A0]"></th>
            </tr>
          </thead>
          <tbody>
            {tiers.map(tier => (
              <tr key={tier.id} className={`border-t border-[#2A2A2A] ${editingTier === tier.id ? 'bg-[#111]' : ''}`}>
                <td className="p-2 font-medium" style={{ color: TIER_COLORS[tier.slug] || '#A0A0A0' }}>{tier.name}</td>
                <PriceCell field="price_monthly" tier={tier} />
                <PriceCell field="price_annual" tier={tier} />
                <LimitCell field="max_voices" tier={tier} />
                <LimitCell field="max_phases" tier={tier} />
                <LimitCell field="max_rounds_per_phase" tier={tier} />
                <LimitCell field="max_frays_per_month" tier={tier} />
                <ModelTierCell tier={tier} />
                <td className="p-1 text-right whitespace-nowrap">
                  {editingTier === tier.id ? (
                    <span className="flex gap-1 justify-end">
                      <button onClick={() => saveTier(tier.id)} disabled={saving}
                        className="px-2 py-0.5 text-[10px] font-mono bg-[#FF4D00] text-black hover:bg-[#FF6A20] disabled:opacity-50">
                        {saving ? '…' : 'Save'}
                      </button>
                      <button onClick={cancelEdit} disabled={saving}
                        className="px-2 py-0.5 text-[10px] font-mono border border-[#2A2A2A] text-[#A0A0A0] hover:border-[#4A4A4A]">
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button onClick={() => startEdit(tier)}
                      className="px-2 py-0.5 text-[10px] font-mono border border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00] hover:text-[#FF4D00]">
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {addingTier && (
              <tr className="border-t border-[#FF4D00] bg-[#0D0D0D]">
                <td className="p-1">
                  <input placeholder="Name" value={newTier.name}
                    onChange={e => setNewTier(p => ({ ...p, name: e.target.value, slug: p.slug || slugify(e.target.value) }))}
                    className={`w-20 ${inputCls}`} />
                  <input placeholder="slug" value={newTier.slug}
                    onChange={e => setNewTier(p => ({ ...p, slug: e.target.value }))}
                    className={`w-16 mt-0.5 ${inputCls}`} />
                </td>
                {['price_monthly','price_annual','max_voices','max_phases','max_rounds_per_phase','max_frays_per_month'].map(f => (
                  <td key={f} className="p-1 text-right">
                    <input type="number" min="0" placeholder="∞"
                      value={newTier[f]}
                      onChange={e => setNewTier(p => ({ ...p, [f]: e.target.value }))}
                      className={`w-14 text-right ${inputCls}`} />
                  </td>
                ))}
                <td className="p-1 text-right">
                  <select value={newTier.model_tier}
                    onChange={e => setNewTier(p => ({ ...p, model_tier: e.target.value }))}
                    className={selectCls}>
                    {MODEL_TIER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </td>
                <td className="p-1 text-right whitespace-nowrap">
                  <span className="flex gap-1 justify-end flex-col">
                    <span className="flex gap-1">
                      <button onClick={saveNewTier} disabled={saving || !newTier.name}
                        className="px-2 py-0.5 text-[10px] font-mono bg-[#FF4D00] text-black hover:bg-[#FF6A20] disabled:opacity-50">
                        {saving ? '…' : 'Add'}
                      </button>
                      <button onClick={() => { setAddingTier(false); setNewTier(BLANK_NEW_TIER); setAddError(null); }} disabled={saving}
                        className="px-2 py-0.5 text-[10px] font-mono border border-[#2A2A2A] text-[#A0A0A0]">
                        ✕
                      </button>
                    </span>
                    {addError && <span className="text-[10px] text-[#A61E17] font-mono">{addError}</span>}
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!addingTier && (
        <button onClick={() => { setAddingTier(true); setEditingTier(null); }}
          className="mt-2 px-3 py-1 text-[10px] font-mono border border-dashed border-[#2A2A2A] text-[#4A4A4A] hover:border-[#FF4D00] hover:text-[#FF4D00] transition-colors">
          + Add Tier
        </button>
      )}

      {/* #1088 Phase 1 of #1081 — per-tier OpenRouter Workspace */}
      <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mt-6 mb-1">OpenRouter Workspaces</h3>
      <div className="text-[10px] text-[#4A4A4A] font-mono mb-2">
        Each tier can route its provisioned OR keys into a dedicated OR Workspace inside Fray's account
        (per-tier governance + cleaner billing). Provision once, then enable when ready.
      </div>
      {orWorkspaceError && (
        <div className="text-xs text-[#A61E17] mb-2 font-mono">{orWorkspaceError}</div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead className="bg-[#1A1A1A]">
            <tr>
              <th className="text-left p-1.5 font-mono text-[#A0A0A0]">Tier</th>
              <th className="text-left p-1.5 font-mono text-[#A0A0A0]">OR Workspace ID</th>
              <th className="text-center p-1.5 font-mono text-[#A0A0A0]">Enabled</th>
              <th className="text-right p-1.5 font-mono text-[#A0A0A0]"></th>
            </tr>
          </thead>
          <tbody>
            {tiers.map(tier => {
              // #1098: enterprise customers provision OR keys via their own
              // workspace's mgmt key (the #1053 / #1056 / #1077 path). The
              // hybrid plan in #1081 excludes them from tier-OR-Workspace
              // routing — render as informational, no Provision button.
              const isEnterprise = (tier.slug || '').toLowerCase() === 'enterprise';
              if (isEnterprise) {
                return (
                  <tr key={`orws-${tier.id}`} className="border-t border-[#2A2A2A] opacity-60">
                    <td className="p-1.5 font-medium" style={{ color: TIER_COLORS[tier.slug] || '#A0A0A0' }}>
                      {tier.name}
                    </td>
                    <td className="p-1.5 font-mono text-[#A0A0A0] text-[10px]" colSpan={3}>
                      n/a — Enterprise users provision per-workspace via the workspace admin's <em>OR Provisioning</em> panel
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={`orws-${tier.id}`} className="border-t border-[#2A2A2A]">
                  <td className="p-1.5 font-medium" style={{ color: TIER_COLORS[tier.slug] || '#A0A0A0' }}>
                    {tier.name}
                  </td>
                  <td className="p-1.5 font-mono text-[#F5F1E8]">
                    {tier.or_workspace_id ? (
                      <span className="text-[10px]">{tier.or_workspace_id}</span>
                    ) : (
                      <span className="text-[#4A4A4A]">— not provisioned</span>
                    )}
                  </td>
                  <td className="text-center p-1.5">
                    <button
                      onClick={() => toggleOrWorkspaceEnabled(tier)}
                      disabled={!tier.or_workspace_id || orWorkspaceBusy === tier.slug}
                      title={tier.or_workspace_id ? 'Toggle workspace routing for this tier' : 'Provision the workspace first'}
                      className={`w-5 h-5 border inline-flex items-center justify-center transition-colors ${
                        tier.or_workspace_enabled
                          ? 'bg-[#22C55E] border-[#22C55E] text-black'
                          : tier.or_workspace_id
                            ? 'border-[#FF4D00] text-[#4A4A4A] hover:text-[#FF4D00]'
                            : 'border-[#2A2A2A] text-[#2A2A2A] cursor-not-allowed'
                      }`}>
                      {tier.or_workspace_enabled ? '✓' : ''}
                    </button>
                  </td>
                  <td className="p-1.5 text-right">
                    {tier.or_workspace_id ? (
                      <span className="text-[#4A4A4A] font-mono text-[10px]">provisioned</span>
                    ) : (
                      <button
                        onClick={() => provisionOrWorkspace(tier)}
                        disabled={orWorkspaceBusy === tier.slug}
                        className="px-2 py-0.5 text-[10px] font-mono border border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00] hover:text-[#FF4D00] disabled:opacity-50">
                        {orWorkspaceBusy === tier.slug ? '…' : 'Provision'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mt-6 mb-3">Feature Matrix</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead className="bg-[#1A1A1A]">
            <tr>
              <th className="text-left p-1.5 font-mono text-[#A0A0A0]">Feature</th>
              {tiers.map(t => <th key={t.id} className="text-center p-1.5 font-mono" style={{ color: TIER_COLORS[t.slug] }}>{t.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {features.map(feat => (
              <tr key={feat._id || feat.key} className="border-t border-[#2A2A2A]">
                <td className="p-1.5 font-mono text-[#A0A0A0]">{feat.name || feat.key}</td>
                {tiers.map(tier => {
                  const val = tier.features?.[feat.key];
                  return (
                    <td key={tier.id} className="text-center p-1.5">
                      <button onClick={() => toggleFeature(feat.key, tier.id, val)}
                        className={`w-5 h-5 border inline-flex items-center justify-center transition-colors ${val ? 'bg-[#22C55E] border-[#22C55E] text-black' : 'border-[#2A2A2A] text-[#2A2A2A]'}`}>
                        {val ? '✓' : ''}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SkillsTab() {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);  // #657: surface save failures inline

  useEffect(() => {
    loadSkills();
  }, []);

  const loadSkills = async () => {
    try {
      const res = await API.get('/api/skills');
      setSkills(res.data.skills || []);
    } catch (err) {
      console.error('Load skills error:', err);
    }
    setLoading(false);
  };

  const startEdit = async (skill) => {
    try {
      const res = await API.get(`/api/skills/${skill.slug}`);
      setEditing(skill.slug);
      setEditForm({
        slug: res.data.slug,
        name: res.data.name || '',
        description: res.data.description || '',
        content: res.data.content || '',
        category: res.data.category || 'general',
        bind_points: (res.data.bind_points || []).join(', '),
        usage_context: res.data.usage_context || 'in_app',
        playbook_slugs: (res.data.bind_points || []).join(', '),
        change_note: '',
      });
    } catch (err) {
      console.error('Load skill error:', err);
    }
  };

  const startNew = () => {
    setEditing('__new__');
    setEditForm({ slug: '', name: '', description: '', content: '', category: 'general', bind_points: '', usage_context: 'both', change_note: '' });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      // Parse YAML frontmatter (between --- delimiters)
      const fmMatch = text.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
      if (fmMatch) {
        const yaml = fmMatch[1];
        const body = fmMatch[2];
        const parsed = {};
        yaml.split('\n').forEach(line => {
          const [k, ...rest] = line.split(':');
          if (k && rest.length) parsed[k.trim()] = rest.join(':').trim();
        });
        setEditForm(f => ({
          ...f,
          content: text,
          name: parsed.name || f.name,
          description: parsed.description || f.description,
          slug: parsed.slug || f.slug || (parsed.name || '').toLowerCase().replace(/\s+/g, '-'),
          category: parsed.category || f.category,
          bind_points: parsed.bind_points || f.bind_points,
          usage_context: parsed.usage_context || 'both',
        }));
      } else {
        setEditForm(f => ({ ...f, content: text }));
      }
    };
    reader.readAsText(file);
  };

  const saveSkill = async () => {
    setSaving(true);
    setSaveError(null);  // #657: clear any prior error
    try {
      await API.post('/api/admin/skills', {
        slug: editForm.slug,
        name: editForm.name,
        description: editForm.description,
        content: editForm.content,
        category: editForm.category,
        bind_points: editForm.bind_points.split(',').map(s => s.trim()).filter(Boolean),
        usage_context: editForm.usage_context,
        change_note: editForm.change_note,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      setEditing(null);
      await loadSkills();
    } catch (err) {
      // #657: don't swallow — surface the backend's detail or JS error message inline
      console.error('Save skill error:', err);
      const backendDetail = err?.response?.data?.detail;
      setSaveError(
        typeof backendDetail === 'string' ? backendDetail
          : err?.message || 'Save failed — check browser console for details'
      );
    }
    setSaving(false);
  };

  const toggleSkill = async (slug) => {
    try {
      await API.put(`/api/admin/skills/${slug}/toggle`);
      await loadSkills();
    } catch (err) {
      console.error('Toggle skill error:', err);
    }
  };

  const loadVersions = async (slug) => {
    if (showVersions === slug) { setShowVersions(null); return; }
    try {
      const res = await API.get(`/api/admin/skills/${slug}/versions`);
      setVersions(res.data.versions || []);
      setShowVersions(slug);
    } catch (err) {
      console.error('Load versions error:', err);
    }
  };

  if (loading) return <div className="animate-shimmer h-40" />;

  if (editing) {
    return (
      <div data-testid="skills-editor">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold font-['Barlow_Condensed']">
            {editing === '__new__' ? 'New Skill' : `Edit: ${editForm.name}`}
          </h3>
          <button onClick={() => setEditing(null)} className="text-xs text-[#A0A0A0] hover:text-white font-mono">Cancel</button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Slug</label>
              <input value={editForm.slug} onChange={e => setEditForm(f => ({...f, slug: e.target.value}))}
                disabled={editing !== '__new__'}
                className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00] disabled:opacity-50"
                placeholder="committee-design" />
            </div>
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Name</label>
              <input value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))}
                className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]"
                placeholder="Committee Design Expert" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Description</label>
            <input value={editForm.description} onChange={e => setEditForm(f => ({...f, description: e.target.value}))}
              className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]"
              placeholder="What this skill does..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Category</label>
              <select value={editForm.category} onChange={e => setEditForm(f => ({...f, category: e.target.value}))}
                className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]">
                <option value="builder">Builder</option>
                <option value="output">Output</option>
                <option value="domain">Domain</option>
                <option value="research">Research</option>
                <option value="general">General</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Bind Points (comma-separated)</label>
              <input value={editForm.bind_points} onChange={e => setEditForm(f => ({...f, bind_points: e.target.value}))}
                className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]"
                placeholder="prompt-builder-chat, prompt-builder-extract" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Usage Context</label>
            <select value={editForm.usage_context} onChange={e => setEditForm(f => ({...f, usage_context: e.target.value}))}
              className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]">
              <option value="in_app">In-App Only (powers AI features on getfray.ai)</option>
              <option value="mcp_customer">MCP Customer Download (local agent skill)</option>
              <option value="both">Both (in-app + customer download)</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Associated Playbooks (comma-separated slugs)</label>
            <input value={editForm.playbook_slugs || ''} onChange={e => setEditForm(f => ({...f, playbook_slugs: e.target.value}))}
              className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]"
              placeholder="committee-design, risk-assessment, due-diligence" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-mono text-[#A0A0A0]">
                Skill Content <span className="text-[#A0A0A0]">({editForm.content.length} chars)</span>
              </label>
              <label className="px-3 py-1 bg-[#FF4D00] text-black text-[10px] font-bold cursor-pointer hover:bg-[#FF4D00]/80">
                Upload SKILL.md
                <input type="file" accept=".md,.txt" onChange={handleFileUpload} className="hidden" />
              </label>
            </div>
            <textarea value={editForm.content} onChange={e => setEditForm(f => ({...f, content: e.target.value}))}
              className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00] min-h-[400px] resize-y"
              placeholder="The skill prompt content... Upload a .md file or paste content here." />
          </div>
          {editing !== '__new__' && (
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Change Note</label>
              <input value={editForm.change_note} onChange={e => setEditForm(f => ({...f, change_note: e.target.value}))}
                className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]"
                placeholder="What changed and why..." />
            </div>
          )}
          <button onClick={saveSkill} disabled={saving || !editForm.slug || !editForm.content}
            className="px-6 py-2 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50 min-h-[44px]"
            data-testid="save-skill-btn">
            {saved ? 'Saved!' : saving ? 'Saving...' : 'Save Skill'}
          </button>
          {/* #657: inline hint when disabled so the user knows WHY the button won't fire */}
          {(!editForm.slug || !editForm.content) && !saving && !saved && (
            <div className="text-xs font-mono text-[#A0A0A0] mt-2" data-testid="save-skill-disabled-hint">
              {!editForm.slug && (
                <div>
                  Slug is required — populate it above, or upload a SKILL.md with <code>slug:</code> in its frontmatter.
                </div>
              )}
              {!editForm.content && (
                <div>
                  Content is required — paste the skill content or click "Upload SKILL.md".
                </div>
              )}
            </div>
          )}
          {/* #657: surface save failures inline (previously only logged to console) */}
          {saveError && (
            <div className="text-xs font-mono text-[#EF4444] mt-2 p-2 border border-[#EF4444]/30 bg-[#EF4444]/5"
              data-testid="save-skill-error">
              Save failed: {saveError}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="skills-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Skills ({skills.length})</h3>
        <button onClick={startNew}
          className="px-3 py-1.5 bg-[#FF4D00] text-black text-xs font-medium flex items-center gap-1">
          <Plus className="w-3 h-3" /> New Skill
        </button>
      </div>
      <div className="space-y-2">
        {skills.map(skill => (
          <div key={skill.slug || skill.id}>
            <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium font-['Barlow_Condensed']">{skill.name}</span>
                  <span className="text-[9px] px-1.5 py-0.5 border border-[#2A2A2A] text-[#A0A0A0] font-mono">{skill.category}</span>
                  <span className="text-[9px] px-1.5 py-0.5 border font-mono" style={{
                    color: skill.is_active !== false ? '#22C55E' : '#EF4444',
                    borderColor: skill.is_active !== false ? '#22C55E40' : '#EF444440'
                  }}>{skill.is_active !== false ? 'ACTIVE' : 'INACTIVE'}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 border font-mono ${
                    skill.usage_context === 'both' ? 'text-[#FF4D00] border-[#FF4D00]/30' :
                    skill.usage_context === 'mcp_customer' ? 'text-[#00D4FF] border-[#00D4FF]/30' :
                    'text-[#A0A0A0] border-[#2A2A2A]'
                  }`}>{skill.usage_context === 'both' ? 'APP+MCP' : skill.usage_context === 'mcp_customer' ? 'MCP' : 'APP'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-mono text-[#A0A0A0]">v{skill.version || 1}</span>
                  <button onClick={() => loadVersions(skill.slug)} className="text-[9px] font-mono text-[#A0A0A0] hover:text-[#FF4D00]">History</button>
                  <button onClick={() => toggleSkill(skill.slug)}
                    className={`text-[9px] font-mono px-2 py-0.5 border ${skill.is_active !== false ? 'text-[#EF4444] border-[#EF4444]/30' : 'text-[#22C55E] border-[#22C55E]/30'}`}>
                    {skill.is_active !== false ? 'Deactivate' : 'Activate'}
                  </button>
                  <button onClick={() => startEdit(skill)} className="text-[9px] font-mono text-[#FF4D00] hover:text-white px-2 py-0.5 border border-[#FF4D00]/30">Edit</button>
                </div>
              </div>
              <p className="text-[10px] text-[#A0A0A0] font-mono">{skill.description}</p>
              {skill.bind_points && skill.bind_points.length > 0 && (
                <div className="flex gap-1 mt-2">
                  {skill.bind_points.map(bp => (
                    <span key={bp} className="text-[8px] px-1.5 py-0.5 bg-[#FF4D00]/10 text-[#FF4D00] font-mono">{bp}</span>
                  ))}
                </div>
              )}
            </div>
            {showVersions === skill.slug && versions.length > 0 && (
              <div className="ml-4 border-l-2 border-[#2A2A2A] pl-4 py-2 space-y-1">
                {versions.map(v => (
                  <div key={v.id} className="p-2 bg-[#0F0F0F] border border-[#2A2A2A] text-[10px] font-mono text-[#A0A0A0]">
                    <span className="text-white">v{v.version}</span> — {v.change_note || 'No note'} — {new Date(v.created_at).toLocaleString()}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {skills.length === 0 && <p className="text-[#A0A0A0] text-sm">No skills created yet. Skills are seeded on server startup.</p>}
      </div>
    </div>
  );
}

function TopicsTab() {
  const [topics, setTopics] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('all');
  const [adding, setAdding] = useState(false);
  const [newTopic, setNewTopic] = useState({ topic: '', category: 'Tech & AI', heat_level: 'hot' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadTopics(); }, []);

  const loadTopics = async () => {
    try {
      const [topRes, catRes] = await Promise.all([
        API.get('/api/cat-fight/random-topic').catch(() => null),
        API.get('/api/cat-fight/categories'),
      ]);
      setCategories(catRes.data.categories || []);
      // Load all topics by fetching multiple times or use admin endpoint
      // For now, load categories and show counts
    } catch {}
    setLoading(false);
  };

  const addTopic = async () => {
    if (!newTopic.topic.trim()) return;
    setSaving(true);
    try {
      await API.post('/api/admin/cat-fight-topics', newTopic);
      setNewTopic({ topic: '', category: 'Tech & AI', heat_level: 'hot' });
      setAdding(false);
      await loadTopics();
    } catch (err) {
      console.error('Add topic error:', err);
    }
    setSaving(false);
  };

  const HEAT_COLORS = { warm: '#F59E0B', hot: '#FF6B00', nuclear: '#EF4444' };
  const ALL_CATEGORIES = ['Tech & AI', 'Culture Wars', 'Business', 'Philosophy', 'Sports', 'Pop Culture', 'Food & Lifestyle', 'Politics', 'Science', 'Education'];

  if (loading) return <div className="animate-shimmer h-40" />;

  return (
    <div data-testid="topics-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Cat Fight Topics</h3>
        <button onClick={() => setAdding(!adding)}
          className="px-3 py-1.5 bg-[#EF4444] text-white text-xs font-medium flex items-center gap-1">
          <Plus className="w-3 h-3" /> Add Topic
        </button>
      </div>

      {adding && (
        <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-4 space-y-3">
          <input value={newTopic.topic} onChange={e => setNewTopic(t => ({...t, topic: e.target.value}))}
            className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#EF4444]"
            placeholder="Is AI going to replace software engineers?" />
          <div className="grid grid-cols-2 gap-3">
            <select value={newTopic.category} onChange={e => setNewTopic(t => ({...t, category: e.target.value}))}
              className="px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#EF4444]">
              {ALL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={newTopic.heat_level} onChange={e => setNewTopic(t => ({...t, heat_level: e.target.value}))}
              className="px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#EF4444]">
              <option value="warm">Warm</option>
              <option value="hot">Hot</option>
              <option value="nuclear">Nuclear</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={addTopic} disabled={saving || !newTopic.topic.trim()}
              className="px-4 py-1.5 bg-[#EF4444] text-white text-xs font-medium disabled:opacity-50">
              {saving ? 'Saving...' : 'Add'}
            </button>
            <button onClick={() => setAdding(false)} className="px-4 py-1.5 border border-[#2A2A2A] text-xs text-[#A0A0A0]">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        {categories.map(c => (
          <div key={c.category} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] text-center">
            <span className="text-lg font-bold font-['Barlow_Condensed'] block">{c.count}</span>
            <span className="text-[9px] text-[#A0A0A0] font-mono">{c.category}</span>
          </div>
        ))}
      </div>

      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-4">
        <p className="text-xs text-[#A0A0A0]">
          Topics are seeded on server startup ({categories.reduce((sum, c) => sum + c.count, 0)} total across {categories.length} categories).
          Use the "Add Topic" button to expand the bank. Topics are served via the Random Topic and Topic of the Day APIs.
        </p>
      </div>

      {/* Trending Topic Generation */}
      <TrendingTopicGenerator onRefresh={loadTopics} />
    </div>
  );
}

function PersonasTab() {
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [pendingCount, setPendingCount] = useState(0);
  const [reviewing, setReviewing] = useState({});
  const LIMIT = 20;

  useEffect(() => { loadPersonas(); }, [page, search, visibilityFilter]);

  // Load pending count on mount
  useEffect(() => {
    API.get('/api/admin/personas/pending?limit=1')
      .then(r => setPendingCount(r.data.total || 0))
      .catch(() => {});
  }, []);

  const handleReview = async (slug, action) => {
    setReviewing(prev => ({ ...prev, [slug]: action }));
    try {
      await API.put(`/api/admin/personas/${slug}/review`, { action });
      await loadPersonas();
      // Refresh pending count
      API.get('/api/admin/personas/pending?limit=1')
        .then(r => setPendingCount(r.data.total || 0))
        .catch(() => {});
    } catch (err) {
      alert(err.response?.data?.detail || `${action} failed`);
    }
    setReviewing(prev => ({ ...prev, [slug]: null }));
  };

  const loadPersonas = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (search) params.append('search', search);
      if (visibilityFilter !== 'all') params.append('visibility', visibilityFilter);
      const res = await API.get(`/api/admin/personas?${params}`);
      setPersonas(res.data.personas || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error('Load personas error:', err); }
    setLoading(false);
  };

  const openCreate = () => {
    setForm({ name: '', slug: '', description: '', system_prompt: '', default_model: '', model_tier: 'standard', color: '#FF4D00', domain_tags: '', visibility: 'platform', featured_category: '' });
    setCreating(true); setEditing(null);
  };

  const openEdit = (p) => {
    setForm({ name: p.name || '', slug: p.slug || '', description: p.description || '', system_prompt: p.system_prompt || '', default_model: p.default_model || '', model_tier: p.model_tier || 'standard', color: p.color || '#FF4D00', domain_tags: (p.domain_tags || []).join(', '), visibility: p.visibility || 'platform', featured_category: p.featured_category || '' });
    setEditing(p); setCreating(false);
  };

  const savePersona = async () => {
    setSaving(true);
    try {
      const body = { ...form, domain_tags: form.domain_tags.split(',').map(s => s.trim()).filter(Boolean) };
      if (creating) await API.post('/api/personas', body);
      else await API.put(`/api/personas/${editing.slug}`, body);
      setCreating(false); setEditing(null); await loadPersonas();
    } catch (err) { alert(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const deletePersona = async (slug) => {
    try { await API.delete(`/api/personas/${slug}`); setDeleteConfirm(null); await loadPersonas(); }
    catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  if (loading && personas.length === 0) return <div className="animate-shimmer h-40" />;

  if (creating || editing) {
    return (
      <div data-testid="personas-editor">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold font-['Barlow_Condensed']">{creating ? 'New Persona' : `Edit: ${form.name}`}</h3>
          <button onClick={() => { setCreating(false); setEditing(null); }} className="text-xs text-[#A0A0A0] hover:text-white font-mono">Cancel</button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Name</label>
              <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" placeholder="Devil's Advocate" />
            </div>
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Slug</label>
              <input value={form.slug} onChange={e => setForm(f => ({...f, slug: e.target.value}))} disabled={!!editing} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00] disabled:opacity-50" placeholder="devils-advocate" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Description</label>
            <input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" placeholder="Challenges assumptions..." />
          </div>
          <div>
            <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">System Prompt <span className="text-[#A0A0A0]">({(form.system_prompt || '').length} chars)</span></label>
            <textarea value={form.system_prompt} onChange={e => setForm(f => ({...f, system_prompt: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00] min-h-[200px] resize-y" placeholder="You are a..." />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Default Model</label>
              <input value={form.default_model} onChange={e => setForm(f => ({...f, default_model: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" placeholder="gpt-4o" />
            </div>
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Model Tier</label>
              <select value={form.model_tier} onChange={e => setForm(f => ({...f, model_tier: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]">
                <option value="budget">Budget</option><option value="standard">Standard</option><option value="premium">Premium</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Color</label>
              <div className="flex gap-2">
                <input type="color" value={form.color} onChange={e => setForm(f => ({...f, color: e.target.value}))} className="w-10 h-9 bg-transparent border border-[#2A2A2A] cursor-pointer" />
                <input value={form.color} onChange={e => setForm(f => ({...f, color: e.target.value}))} className="flex-1 px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Visibility</label>
              <select value={form.visibility} onChange={e => setForm(f => ({...f, visibility: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]">
                <option value="platform">Platform</option><option value="public">Public</option><option value="private">Private</option><option value="pending_review">Pending Review</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Domain Tags (comma-sep)</label>
              <input value={form.domain_tags} onChange={e => setForm(f => ({...f, domain_tags: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" placeholder="legal, business" />
            </div>
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Featured Category</label>
              <input value={form.featured_category} onChange={e => setForm(f => ({...f, featured_category: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" placeholder="strategy" />
            </div>
          </div>
          <button onClick={savePersona} disabled={saving || !form.name || !form.slug} className="px-6 py-2 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving...' : creating ? 'Create Persona' : 'Save Changes'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="personas-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Personas ({total})</h3>
          {pendingCount > 0 && (
            <button
              onClick={() => { setVisibilityFilter('pending_review'); setPage(1); }}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-mono border transition-colors ${
                visibilityFilter === 'pending_review'
                  ? 'border-[#F59E0B] text-[#F59E0B] bg-[#F59E0B]/10'
                  : 'border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/10'
              }`}
              data-testid="review-queue-btn"
            >
              <AlertTriangle className="w-3 h-3" />
              Review Queue ({pendingCount})
            </button>
          )}
        </div>
        <button onClick={openCreate} className="px-3 py-1.5 bg-[#FF4D00] text-black text-xs font-medium flex items-center gap-1"><Plus className="w-3 h-3" /> Create Persona</button>
      </div>
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-[#A0A0A0] absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="w-full pl-10 pr-4 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" placeholder="Search personas..." />
        </div>
        <select value={visibilityFilter} onChange={e => { setVisibilityFilter(e.target.value); setPage(1); }} className="px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]">
          <option value="all">All Visibility</option><option value="platform">Platform</option><option value="public">Public</option><option value="private">Private</option><option value="pending_review">Pending Review</option>
        </select>
      </div>
      <div className="border border-[#2A2A2A] overflow-x-auto mb-4">
        <table className="w-full text-xs">
          <thead className="bg-[#1A1A1A]">
            <tr>
              <th className="text-left p-2 font-mono text-[#A0A0A0]">Name</th>
              <th className="text-left p-2 font-mono text-[#A0A0A0]">Slug</th>
              <th className="text-left p-2 font-mono text-[#A0A0A0]">Category</th>
              <th className="text-left p-2 font-mono text-[#A0A0A0]">Visibility</th>
              <th className="text-right p-2 font-mono text-[#A0A0A0]">Uses</th>
              <th className="text-left p-2 font-mono text-[#A0A0A0]">Created</th>
              <th className="text-right p-2 font-mono text-[#A0A0A0]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {personas.map(p => (
              <tr key={p.slug || p.id} className="border-t border-[#2A2A2A] hover:bg-[#1A1A1A]/50">
                <td className="p-2 font-medium"><div className="flex items-center gap-2">{p.color && <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.color }} />}{p.name}</div></td>
                <td className="p-2 font-mono text-[#A0A0A0]">{p.slug}</td>
                <td className="p-2"><span className="text-[8px] px-1.5 py-0.5 border border-[#2A2A2A] text-[#A0A0A0] font-mono">{p.featured_category || '-'}</span></td>
                <td className="p-2"><span className={`text-[8px] px-1.5 py-0.5 border font-mono ${p.visibility === 'platform' ? 'text-[#FF4D00] border-[#FF4D00]/30' : p.visibility === 'public' ? 'text-[#22C55E] border-[#22C55E]/30' : p.visibility === 'pending_review' ? 'text-[#F59E0B] border-[#F59E0B]/30' : 'text-[#A0A0A0] border-[#2A2A2A]'}`}>{(p.visibility || 'private').toUpperCase()}</span></td>
                <td className="p-2 text-right font-mono">{p.use_count || 0}</td>
                <td className="p-2 font-mono text-[#A0A0A0]">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}</td>
                <td className="p-2 text-right"><div className="flex items-center justify-end gap-1">{p.visibility === 'pending_review' && (<><button onClick={() => handleReview(p.slug, 'approve')} disabled={reviewing[p.slug]} className="px-2 py-1 text-[9px] font-mono bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/30 hover:bg-[#22C55E]/20 disabled:opacity-50" data-testid={`approve-${p.slug}`}>{reviewing[p.slug] === 'approve' ? '...' : 'Approve'}</button><button onClick={() => handleReview(p.slug, 'reject')} disabled={reviewing[p.slug]} className="px-2 py-1 text-[9px] font-mono bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30 hover:bg-[#EF4444]/20 disabled:opacity-50" data-testid={`reject-${p.slug}`}>{reviewing[p.slug] === 'reject' ? '...' : 'Reject'}</button></>)}<button onClick={() => openEdit(p)} className="p-1.5 text-[#A0A0A0] hover:text-[#FF4D00]" aria-label={`Edit persona ${p.name}`}><Pencil className="w-3 h-3" /></button><button onClick={() => setDeleteConfirm(p.slug)} className="p-1.5 text-[#A0A0A0] hover:text-[#EF4444]" aria-label={`Delete persona ${p.name}`}><Trash2 className="w-3 h-3" /></button></div></td>
              </tr>
            ))}
            {personas.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-[#A0A0A0]">No personas found.</td></tr>}
          </tbody>
        </table>
      </div>
      {total > LIMIT && (
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-mono text-[#A0A0A0]">Page {page} of {Math.ceil(total / LIMIT)}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 text-xs border border-[#2A2A2A] text-[#A0A0A0] disabled:opacity-30">Prev</button>
            <button onClick={() => setPage(p => p + 1)} disabled={page * LIMIT >= total} className="px-3 py-1 text-xs border border-[#2A2A2A] text-[#A0A0A0] disabled:opacity-30">Next</button>
          </div>
        </div>
      )}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" role="dialog" aria-label="Delete persona confirmation">
          <div className="w-full max-w-sm mx-4 bg-[#1A1A1A] border border-[#2A2A2A] p-6">
            <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-2">Delete Persona</h3>
            <p className="text-xs text-[#A0A0A0] mb-4">Are you sure you want to delete <span className="text-white font-mono">{deleteConfirm}</span>? This cannot be undone.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-1.5 border border-[#2A2A2A] text-xs text-[#A0A0A0]">Cancel</button>
              <button onClick={() => deletePersona(deleteConfirm)} className="px-4 py-1.5 bg-[#EF4444] text-white text-xs font-medium">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PersonaGroupsTab() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [personaSearch, setPersonaSearch] = useState('');
  const [personaResults, setPersonaResults] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { loadGroups(); }, []);

  const loadGroups = async () => {
    try {
      const res = await API.get('/api/admin/persona-groups?page=1&limit=50');
      setGroups(res.data.groups || []);
    } catch (err) { console.error('Load groups error:', err); }
    setLoading(false);
  };

  const openCreate = () => {
    setForm({ name: '', slug: '', description: '', category: '', icon: '', display_order: 0 });
    setCreating(true); setEditing(null);
  };

  const openEdit = (g) => {
    setForm({ name: g.name || '', slug: g.slug || '', description: g.description || '', category: g.category || '', icon: g.icon || '', display_order: g.display_order || 0 });
    setEditing(g); setCreating(false);
  };

  const saveGroup = async () => {
    setSaving(true);
    try {
      const body = { ...form, display_order: parseInt(form.display_order) || 0 };
      if (creating) await API.post('/api/admin/persona-groups', body);
      else await API.put(`/api/admin/persona-groups/${editing.slug}`, body);
      setCreating(false); setEditing(null); await loadGroups();
    } catch (err) { alert(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const deleteGroup = async (slug) => {
    try { await API.delete(`/api/admin/persona-groups/${slug}`); setDeleteConfirm(null); await loadGroups(); }
    catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  const toggleExpand = async (g) => {
    if (expandedGroup === g.slug) { setExpandedGroup(null); return; }
    setExpandedGroup(g.slug); setMembersLoading(true);
    try {
      const res = await API.get(`/api/admin/persona-groups/${g.slug}`);
      setMembers(res.data.members || []);
    } catch { setMembers([]); }
    setMembersLoading(false);
  };

  const searchPersonas = async (q) => {
    setPersonaSearch(q);
    if (q.length < 2) { setPersonaResults([]); return; }
    try {
      const res = await API.get(`/api/admin/personas?search=${encodeURIComponent(q)}&limit=10`);
      setPersonaResults(res.data.personas || []);
    } catch { setPersonaResults([]); }
  };

  const addMember = async (personaId) => {
    try {
      await API.post(`/api/admin/persona-groups/${expandedGroup}/members`, { persona_id: personaId });
      const res = await API.get(`/api/admin/persona-groups/${expandedGroup}`);
      setMembers(res.data.members || []); setPersonaSearch(''); setPersonaResults([]);
    } catch (err) { alert(err.response?.data?.detail || 'Add failed'); }
  };

  const removeMember = async (personaId) => {
    try {
      await API.delete(`/api/admin/persona-groups/${expandedGroup}/members/${personaId}`);
      setMembers(m => m.filter(x => x.id !== personaId && x.persona_id !== personaId));
    } catch (err) { alert(err.response?.data?.detail || 'Remove failed'); }
  };

  if (loading) return <div className="animate-shimmer h-40" />;

  if (creating || editing) {
    return (
      <div data-testid="persona-groups-editor">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold font-['Barlow_Condensed']">{creating ? 'New Group' : `Edit: ${form.name}`}</h3>
          <button onClick={() => { setCreating(false); setEditing(null); }} className="text-xs text-[#A0A0A0] hover:text-white font-mono">Cancel</button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Name</label>
              <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" placeholder="Strategy Team" />
            </div>
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Slug</label>
              <input value={form.slug} onChange={e => setForm(f => ({...f, slug: e.target.value}))} disabled={!!editing} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00] disabled:opacity-50" placeholder="strategy-team" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Description</label>
            <input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" placeholder="A group of strategy-focused personas" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Category</label>
              <input value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" placeholder="business" />
            </div>
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Icon</label>
              <input value={form.icon} onChange={e => setForm(f => ({...f, icon: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" placeholder="users" />
            </div>
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Display Order</label>
              <input type="number" value={form.display_order} onChange={e => setForm(f => ({...f, display_order: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" />
            </div>
          </div>
          <button onClick={saveGroup} disabled={saving || !form.name || !form.slug} className="px-6 py-2 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving...' : creating ? 'Create Group' : 'Save Changes'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="persona-groups-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Persona Groups ({groups.length})</h3>
        <button onClick={openCreate} className="px-3 py-1.5 bg-[#FF4D00] text-black text-xs font-medium flex items-center gap-1"><Plus className="w-3 h-3" /> New Group</button>
      </div>
      <div className="space-y-2">
        {groups.map(g => (
          <div key={g.slug || g.id}>
            <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] cursor-pointer hover:border-[#FF4D00]/40 transition-colors" onClick={() => toggleExpand(g)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {expandedGroup === g.slug ? <ChevronDown className="w-4 h-4 text-[#FF4D00]" /> : <ChevronRight className="w-4 h-4 text-[#A0A0A0]" />}
                  <div>
                    <span className="text-sm font-medium font-['Barlow_Condensed']">{g.name}</span>
                    <div className="flex gap-2 mt-1 text-[9px] font-mono text-[#A0A0A0]">
                      <span>{g.slug}</span>
                      {g.category && <span className="text-[8px] px-1.5 py-0.5 border border-[#2A2A2A]">{g.category}</span>}
                      <span>{g.member_count || 0} members</span>
                      <span>Order: {g.display_order || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <button onClick={() => openEdit(g)} className="p-1.5 text-[#A0A0A0] hover:text-[#FF4D00]" aria-label={`Edit group ${g.name}`}><Pencil className="w-3 h-3" /></button>
                  <button onClick={() => setDeleteConfirm(g.slug)} className="p-1.5 text-[#A0A0A0] hover:text-[#EF4444]" aria-label={`Delete group ${g.name}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
            {expandedGroup === g.slug && (
              <div className="ml-6 border-l-2 border-[#2A2A2A] pl-4 py-3 space-y-2">
                {membersLoading ? <p className="text-[10px] text-[#A0A0A0] font-mono">Loading members...</p> : (
                  <>
                    {members.map(m => (
                      <div key={m.id || m.persona_id} className="flex items-center justify-between p-2 bg-[#0F0F0F] border border-[#2A2A2A]">
                        <div className="flex items-center gap-2">
                          {m.color && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />}
                          <span className="text-xs">{m.name || m.persona_name}</span>
                          <span className="text-[9px] font-mono text-[#A0A0A0]">{m.slug || m.persona_slug}</span>
                        </div>
                        <button onClick={() => removeMember(m.id || m.persona_id)} className="text-[#A0A0A0] hover:text-[#EF4444]" aria-label={`Remove ${m.name || m.persona_name} from group`}><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    {members.length === 0 && <p className="text-[10px] text-[#A0A0A0] font-mono">No members yet.</p>}
                    <div className="relative mt-2">
                      <Search className="w-3 h-3 text-[#A0A0A0] absolute left-2 top-1/2 -translate-y-1/2" />
                      <input type="text" value={personaSearch} onChange={e => searchPersonas(e.target.value)} className="w-full pl-7 pr-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-xs focus:outline-none focus:border-[#FF4D00]" placeholder="Search personas to add..." />
                    </div>
                    {personaResults.length > 0 && (
                      <div className="border border-[#2A2A2A] max-h-40 overflow-y-auto">
                        {personaResults.map(p => (
                          <button key={p.id || p.slug} onClick={() => addMember(p.id)} className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#FF4D00]/10 flex items-center gap-2 border-b border-[#2A2A2A] last:border-0">
                            <Plus className="w-3 h-3 text-[#FF4D00]" /><span>{p.name}</span><span className="text-[9px] font-mono text-[#A0A0A0]">{p.slug}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ))}
        {groups.length === 0 && <p className="text-[#A0A0A0] text-sm">No persona groups created yet.</p>}
      </div>
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" role="dialog" aria-label="Delete group confirmation">
          <div className="w-full max-w-sm mx-4 bg-[#1A1A1A] border border-[#2A2A2A] p-6">
            <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-2">Delete Group</h3>
            <p className="text-xs text-[#A0A0A0] mb-4">Are you sure you want to delete <span className="text-white font-mono">{deleteConfirm}</span>?</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-1.5 border border-[#2A2A2A] text-xs text-[#A0A0A0]">Cancel</button>
              <button onClick={() => deleteGroup(deleteConfirm)} className="px-4 py-1.5 bg-[#EF4444] text-white text-xs font-medium">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlaybooksTab() {
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [voices, setVoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { loadPlaybooks(); }, [search, statusFilter]);

  const loadPlaybooks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, limit: 50 });
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('is_active', statusFilter === 'active');
      const res = await API.get(`/api/admin/playbooks?${params}`);
      setPlaybooks(res.data.playbooks || []);
    } catch (err) { console.error('Load playbooks error:', err); }
    setLoading(false);
  };

  const openCreate = () => {
    setForm({ name: '', slug: '', description: '', category: '', domain: '', complexity_tier: 'standard', credit_cost: 1, is_active: true });
    setVoices([]); setCreating(true); setEditing(null);
  };

  const openEdit = (pb) => {
    setForm({ name: pb.name || '', slug: pb.slug || '', description: pb.description || '', category: pb.category || '', domain: pb.domain || '', complexity_tier: pb.complexity_tier || 'standard', credit_cost: pb.credit_cost ?? 1, is_active: pb.is_active !== false });
    setVoices(pb.voices || []); setEditing(pb); setCreating(false);
  };

  const savePlaybook = async () => {
    setSaving(true);
    try {
      const body = { ...form, credit_cost: parseInt(form.credit_cost) || 1, voices };
      if (creating) await API.post('/api/admin/playbooks', body);
      else await API.put(`/api/admin/playbooks/${editing.slug}`, body);
      setCreating(false); setEditing(null); await loadPlaybooks();
    } catch (err) { alert(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const toggleActive = async (slug) => {
    try { await API.put(`/api/admin/playbooks/${slug}/toggle`); await loadPlaybooks(); }
    catch (err) { alert(err.response?.data?.detail || 'Toggle failed'); }
  };

  const duplicatePlaybook = async (slug) => {
    try { await API.post(`/api/admin/playbooks/${slug}/duplicate`); await loadPlaybooks(); }
    catch (err) { alert(err.response?.data?.detail || 'Duplicate failed'); }
  };

  const deletePlaybook = async (slug) => {
    try { await API.delete(`/api/admin/playbooks/${slug}`); setDeleteConfirm(null); await loadPlaybooks(); }
    catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  const addVoice = () => setVoices(v => [...v, { persona_slug: '', model: '', role_label: '' }]);
  const updateVoice = (idx, field, value) => setVoices(v => v.map((voice, i) => i === idx ? { ...voice, [field]: value } : voice));
  const removeVoice = (idx) => setVoices(v => v.filter((_, i) => i !== idx));

  if (loading && playbooks.length === 0) return <div className="animate-shimmer h-40" />;

  if (creating || editing) {
    return (
      <div data-testid="playbooks-editor">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold font-['Barlow_Condensed']">{creating ? 'New Playbook' : `Edit: ${form.name}`}</h3>
          <button onClick={() => { setCreating(false); setEditing(null); }} className="text-xs text-[#A0A0A0] hover:text-white font-mono">Cancel</button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Name</label>
              <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" placeholder="Strategic Analysis" />
            </div>
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Slug</label>
              <input value={form.slug} onChange={e => setForm(f => ({...f, slug: e.target.value}))} disabled={!!editing} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00] disabled:opacity-50" placeholder="strategic-analysis" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00] min-h-[80px] resize-y" placeholder="What this playbook does..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Category</label>
              <input value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" placeholder="business" />
            </div>
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Domain</label>
              <input value={form.domain} onChange={e => setForm(f => ({...f, domain: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" placeholder="strategy" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Complexity Tier</label>
              <select value={form.complexity_tier} onChange={e => setForm(f => ({...f, complexity_tier: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]">
                <option value="simple">Simple</option><option value="standard">Standard</option><option value="complex">Complex</option><option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Credit Cost</label>
              <input type="number" value={form.credit_cost} onChange={e => setForm(f => ({...f, credit_cost: e.target.value}))} className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" min={0} />
            </div>
            <div>
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">Status</label>
              <button onClick={() => setForm(f => ({...f, is_active: !f.is_active}))} className={`w-full px-3 py-2 border text-xs font-mono flex items-center justify-center gap-2 ${form.is_active ? 'bg-[#22C55E]/10 border-[#22C55E]/30 text-[#22C55E]' : 'bg-[#0F0F0F] border-[#2A2A2A] text-[#A0A0A0]'}`}>
                {form.is_active ? <><ToggleRight className="w-4 h-4" /> Active</> : <><ToggleLeft className="w-4 h-4" /> Inactive</>}
              </button>
            </div>
          </div>
          <div className="border-t border-[#2A2A2A] pt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-[10px] font-mono text-[#A0A0A0]">VOICES ({voices.length})</label>
              <button onClick={addVoice} className="px-2 py-1 text-[9px] font-mono text-[#FF4D00] border border-[#FF4D00]/30 hover:bg-[#FF4D00]/10 flex items-center gap-1"><Plus className="w-3 h-3" /> Add Voice</button>
            </div>
            <div className="space-y-2">
              {voices.map((v, idx) => (
                <div key={idx} className="p-3 bg-[#0F0F0F] border border-[#2A2A2A] grid grid-cols-4 gap-2 items-end">
                  <div>
                    <label className="text-[9px] font-mono text-[#A0A0A0] block mb-1">Persona Slug</label>
                    <input value={v.persona_slug} onChange={e => updateVoice(idx, 'persona_slug', e.target.value)} className="w-full px-2 py-1.5 bg-[#1A1A1A] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" placeholder="devils-advocate" />
                  </div>
                  <div>
                    <label className="text-[9px] font-mono text-[#A0A0A0] block mb-1">Model</label>
                    <input value={v.model} onChange={e => updateVoice(idx, 'model', e.target.value)} className="w-full px-2 py-1.5 bg-[#1A1A1A] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" placeholder="gpt-4o" />
                  </div>
                  <div>
                    <label className="text-[9px] font-mono text-[#A0A0A0] block mb-1">Role Label</label>
                    <input value={v.role_label} onChange={e => updateVoice(idx, 'role_label', e.target.value)} className="w-full px-2 py-1.5 bg-[#1A1A1A] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#FF4D00]" placeholder="Challenger" />
                  </div>
                  <button onClick={() => removeVoice(idx)} className="p-1.5 text-[#A0A0A0] hover:text-[#EF4444] self-end mb-1" aria-label={`Remove voice ${idx + 1}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          </div>
          <button onClick={savePlaybook} disabled={saving || !form.name || !form.slug} className="px-6 py-2 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving...' : creating ? 'Create Playbook' : 'Save Changes'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="playbooks-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Playbooks ({playbooks.length})</h3>
        <button onClick={openCreate} className="px-3 py-1.5 bg-[#FF4D00] text-black text-xs font-medium flex items-center gap-1"><Plus className="w-3 h-3" /> New Playbook</button>
      </div>
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-[#A0A0A0] absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" placeholder="Search playbooks..." />
        </div>
        <div className="flex gap-1">
          {['all', 'active', 'inactive'].map(f => (
            <button key={f} onClick={() => setStatusFilter(f)} className={`px-3 py-1 text-[10px] font-mono border capitalize ${statusFilter === f ? 'bg-[#FF4D00] text-black border-[#FF4D00]' : 'border-[#2A2A2A] text-[#A0A0A0]'}`}>{f}</button>
          ))}
        </div>
      </div>
      <div className="border border-[#2A2A2A] overflow-x-auto mb-4">
        <table className="w-full text-xs">
          <thead className="bg-[#1A1A1A]">
            <tr>
              <th className="text-left p-2 font-mono text-[#A0A0A0]">Name</th>
              <th className="text-left p-2 font-mono text-[#A0A0A0]">Slug</th>
              <th className="text-left p-2 font-mono text-[#A0A0A0]">Category</th>
              <th className="text-left p-2 font-mono text-[#A0A0A0]">Domain</th>
              <th className="text-left p-2 font-mono text-[#A0A0A0]">Status</th>
              <th className="text-right p-2 font-mono text-[#A0A0A0]">Credits</th>
              <th className="text-right p-2 font-mono text-[#A0A0A0]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {playbooks.map(pb => (
              <tr key={pb.slug || pb.id} className="border-t border-[#2A2A2A] hover:bg-[#1A1A1A]/50">
                <td className="p-2 font-medium">{pb.name}</td>
                <td className="p-2 font-mono text-[#A0A0A0]">{pb.slug}</td>
                <td className="p-2"><span className="text-[8px] px-1.5 py-0.5 border border-[#2A2A2A] text-[#A0A0A0] font-mono">{pb.category || '-'}</span></td>
                <td className="p-2 font-mono text-[#A0A0A0]">{pb.domain || '-'}</td>
                <td className="p-2"><span className={`text-[8px] px-1.5 py-0.5 border font-mono ${pb.is_active !== false ? 'text-[#22C55E] border-[#22C55E]/30' : 'text-[#EF4444] border-[#EF4444]/30'}`}>{pb.is_active !== false ? 'ACTIVE' : 'INACTIVE'}</span></td>
                <td className="p-2 text-right font-mono">{pb.credit_cost ?? '-'}</td>
                <td className="p-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(pb)} className="p-1.5 text-[#A0A0A0] hover:text-[#FF4D00]" title="Edit" aria-label={`Edit playbook ${pb.name}`}><Pencil className="w-3 h-3" /></button>
                    <button onClick={() => toggleActive(pb.slug)} className={`p-1.5 ${pb.is_active !== false ? 'text-[#22C55E] hover:text-[#A0A0A0]' : 'text-[#A0A0A0] hover:text-[#22C55E]'}`} title="Toggle Active" aria-label={`${pb.is_active !== false ? 'Deactivate' : 'Activate'} playbook ${pb.name}`}>{pb.is_active !== false ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}</button>
                    <button onClick={() => duplicatePlaybook(pb.slug)} className="p-1.5 text-[#A0A0A0] hover:text-[#A855F7]" title="Duplicate" aria-label={`Duplicate playbook ${pb.name}`}><Copy className="w-3 h-3" /></button>
                    <button onClick={() => setDeleteConfirm(pb.slug)} className="p-1.5 text-[#A0A0A0] hover:text-[#EF4444]" title="Delete" aria-label={`Delete playbook ${pb.name}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {playbooks.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-[#A0A0A0]">No playbooks found.</td></tr>}
          </tbody>
        </table>
      </div>
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" role="dialog" aria-label="Delete playbook confirmation">
          <div className="w-full max-w-sm mx-4 bg-[#1A1A1A] border border-[#2A2A2A] p-6">
            <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-2">Delete Playbook</h3>
            <p className="text-xs text-[#A0A0A0] mb-2">Are you sure you want to delete <span className="text-white font-mono">{deleteConfirm}</span>?</p>
            <p className="text-[10px] text-[#F59E0B] mb-4">Warning: Existing frays using this playbook may be affected.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-1.5 border border-[#2A2A2A] text-xs text-[#A0A0A0]">Cancel</button>
              <button onClick={() => deletePlaybook(deleteConfirm)} className="px-4 py-1.5 bg-[#EF4444] text-white text-xs font-medium">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ModerationTab() {
  const [pending, setPending] = useState({ persona: [], playbook: [] });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({});
  const [expanded, setExpanded] = useState(null);

  const loadPending = async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/admin/community/pending');
      setPending(res.data.pending || { persona: [], playbook: [] });
    } catch (err) { console.error('Load pending error:', err); }
    setLoading(false);
  };

  useEffect(() => { loadPending(); }, []);

  const handleReview = async (contentType, contentId, action) => {
    const key = `${contentType}-${contentId}`;
    setActing(prev => ({ ...prev, [key]: action }));
    try {
      await API.post(`/api/admin/community/review/${contentType}/${contentId}`, { action });
      await loadPending();
    } catch (err) {
      alert(err.response?.data?.detail || `${action} failed`);
    }
    setActing(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const allItems = [
    ...(pending.persona || []).map(p => ({ ...p, _type: 'persona' })),
    ...(pending.playbook || []).map(p => ({ ...p, _type: 'playbook' })),
  ].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));

  if (loading) return <div className="flex justify-center py-10"><div className="w-5 h-5 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="moderation-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Community Moderation Queue</h3>
        <span className="text-xs font-mono text-[#A0A0A0]">{allItems.length} pending</span>
      </div>

      {allItems.length === 0 ? (
        <div className="text-center py-10">
          <CheckCircle className="w-8 h-8 text-[#22C55E] mx-auto mb-2" />
          <p className="text-sm text-[#A0A0A0]">All caught up! No pending submissions.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {allItems.map(item => {
            const key = `${item._type}-${item.id}`;
            const isExpanded = expanded === key;
            return (
              <div key={key} className="bg-[#1A1A1A] border border-[#2A2A2A] overflow-hidden" data-testid={`moderation-item-${item.id}`}>
                <div className="p-3 flex items-center gap-3">
                  <span className={`text-[9px] font-mono px-1.5 py-0.5 border shrink-0 ${
                    item._type === 'persona' ? 'text-[#3B82F6] border-[#3B82F6]/30' : 'text-[#EC4899] border-[#EC4899]/30'
                  }`}>{item._type.toUpperCase()}</span>
                  <div className="flex-1 min-w-0">
                    <button onClick={() => setExpanded(isExpanded ? null : key)} className="text-left w-full" aria-expanded={isExpanded} aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for ${item.name}`}>
                      <span className="text-sm font-['Barlow_Condensed'] font-medium block truncate">{item.name}</span>
                      <span className="text-[10px] font-mono text-[#555]">
                        {item.author_name || 'Unknown'} · {item.domain || item.category || 'Uncategorized'} · {new Date(item.created_at).toLocaleDateString()}
                      </span>
                    </button>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleReview(item._type, item.id, 'approve')}
                      disabled={!!acting[key]}
                      className="px-3 py-1.5 min-h-[44px] text-xs font-mono bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/30 hover:bg-[#22C55E]/20 disabled:opacity-50"
                      data-testid={`approve-${item.id}`}
                    >
                      {acting[key] === 'approve' ? '...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => handleReview(item._type, item.id, 'reject')}
                      disabled={!!acting[key]}
                      className="px-3 py-1.5 min-h-[44px] text-xs font-mono bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30 hover:bg-[#EF4444]/20 disabled:opacity-50"
                      data-testid={`reject-${item.id}`}
                    >
                      {acting[key] === 'reject' ? '...' : 'Reject'}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-3 pb-3 border-t border-[#2A2A2A] pt-3">
                    <p className="text-xs text-[#A0A0A0] mb-2">{item.description || 'No description'}</p>
                    {item.voices && Array.isArray(item.voices) && item.voices.length > 0 && (
                      <div className="mb-2">
                        <span className="text-[9px] font-mono text-[#555] block mb-1">VOICES ({item.voices.length})</span>
                        <div className="flex flex-wrap gap-1">
                          {item.voices.map((v, i) => (
                            <span key={i} className="text-[9px] font-mono px-1.5 py-0.5 bg-[#0F0F0F] border border-[#2A2A2A]">
                              {v.name || v.persona_slug || `Voice ${i + 1}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {item.system_prompt && (
                      <div>
                        <span className="text-[9px] font-mono text-[#555] block mb-1">SYSTEM PROMPT</span>
                        <pre className="text-[10px] text-[#A0A0A0] bg-[#0F0F0F] p-2 border border-[#2A2A2A] max-h-32 overflow-y-auto whitespace-pre-wrap">{item.system_prompt.slice(0, 500)}</pre>
                      </div>
                    )}
                    {item.mandate && (
                      <div>
                        <span className="text-[9px] font-mono text-[#555] block mb-1">MANDATE</span>
                        <pre className="text-[10px] text-[#A0A0A0] bg-[#0F0F0F] p-2 border border-[#2A2A2A] max-h-32 overflow-y-auto whitespace-pre-wrap">{item.mandate.slice(0, 500)}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function TrendingTopicGenerator({ onRefresh }) {
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState([]);
  const [pending, setPending] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);

  useEffect(() => { loadPending(); }, []);

  const loadPending = async () => {
    try {
      const res = await API.get('/api/admin/cat-fight-topics/pending');
      setPending(res.data.topics || []);
    } catch {}
    setLoadingPending(false);
  };

  const generateTrending = async () => {
    setGenerating(true);
    try {
      const res = await API.post('/api/admin/cat-fight-topics/generate-trending', { category: 'all' });
      setGenerated(res.data.topics || []);
      await loadPending();
    } catch (err) {
      console.error('Generate trending failed:', err);
    }
    setGenerating(false);
  };

  const reviewTopic = async (topicId, action, editedTopic) => {
    try {
      await API.put(`/api/admin/cat-fight-topics/${topicId}/review`, { action, topic: editedTopic });
      await loadPending();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Review failed:', err);
    }
  };

  return (
    <div data-testid="trending-generator">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Trending Topics AI</h3>
        <button onClick={generateTrending} disabled={generating}
          className="px-3 py-1.5 bg-[#A855F7] text-white text-xs font-medium disabled:opacity-50 flex items-center gap-1">
          {generating ? 'Generating...' : 'Generate from Current Events'}
        </button>
      </div>

      {generated.length > 0 && (
        <div className="p-3 bg-[#A855F7]/5 border border-[#A855F7]/30 mb-4 text-xs text-[#A855F7]">
          Generated {generated.length} trending topics — they're in the review queue below.
        </div>
      )}

      {/* Pending review queue */}
      {pending.length > 0 && (
        <div className="space-y-2 mb-4">
          <h4 className="text-[10px] font-mono text-[#A0A0A0]">PENDING REVIEW ({pending.length})</h4>
          {pending.map(t => (
            <div key={t.id} className="p-3 bg-[#1A1A1A] border border-[#F59E0B]/30">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 mr-3">
                  <p className="text-sm">{t.topic}</p>
                  <div className="flex gap-2 mt-1 text-[9px] font-mono text-[#A0A0A0]">
                    <span>{t.category}</span>
                    <span style={{ color: t.heat_level === 'nuclear' ? '#EF4444' : t.heat_level === 'hot' ? '#FF6B00' : '#F59E0B' }}>{t.heat_level}</span>
                    {t.trending_source && <span>Source: {t.trending_source.slice(0, 60)}...</span>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => reviewTopic(t.id, 'approve')}
                    className="px-2 py-1 text-[9px] font-mono bg-[#22C55E] text-black">Approve</button>
                  <button onClick={() => reviewTopic(t.id, 'reject')}
                    className="px-2 py-1 text-[9px] font-mono border border-[#EF4444]/30 text-[#EF4444]">Reject</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loadingPending && pending.length === 0 && (
        <p className="text-[10px] text-[#A0A0A0] font-mono">No topics pending review. Click "Generate from Current Events" to create trending topics.</p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   AdminMcpSetup — Admin self-service MCP key + config (#356)
   ═══════════════════════════════════════════════════════════════ */
const ADMIN_MCP_PLATFORMS = [
  { id: 'claude-code', label: 'Claude Code' },
  { id: 'claude-desktop', label: 'Claude Desktop' },
  { id: 'cursor', label: 'Cursor' },
  { id: 'windsurf', label: 'Windsurf' },
];

function getAdminMcpConfig(platform, os, apiKey, mcpUrl) {
  const key = apiKey || 'YOUR_API_KEY';
  const url = mcpUrl || 'https://www.getfray.ai/mcp';
  if (platform === 'claude-code') {
    return `claude mcp add fray --transport sse --url ${url} --header "Authorization: Bearer ${key}"`;
  }
  if (platform === 'claude-desktop' && os === 'windows') {
    return JSON.stringify({
      mcpServers: {
        fray: {
          command: "C:\\\\Users\\\\USERNAME\\\\AppData\\\\Roaming\\\\npm\\\\mcp-remote.cmd",
          args: [url, "--transport", "sse-only", "--header", `Authorization: Bearer ${key}`]
        }
      }
    }, null, 2);
  }
  return JSON.stringify({
    mcpServers: {
      fray: { type: "url", url, headers: { Authorization: `Bearer ${key}` } }
    }
  }, null, 2);
}

function AdminMcpSetup({ user }) {
  const [platform, setPlatform] = useState('claude-code');
  const [os, setOs] = useState('macos');
  const [apiKeys, setApiKeys] = useState([]);
  const [showKey, setShowKey] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState('');
  const [expiration, setExpiration] = useState('90d');

  useEffect(() => {
    API.get('/api/user/mcp-key')
      .then(res => setApiKeys(res.data.keys || []))
      .catch(() => API.get('/api/developer/keys').then(r => setApiKeys(r.data.keys || [])).catch(() => {}));
  }, []);

  const generateKey = async () => {
    setGenerating(true);
    try {
      const res = await API.post('/api/user/mcp-key/regenerate', { expiration });
      setShowKey(res.data.key);
      const keysRes = await API.get('/api/user/mcp-key').catch(() => API.get('/api/developer/keys'));
      setApiKeys(keysRes.data.keys || []);
    } catch {
      try {
        const res = await API.post('/api/developer/keys', { name: 'MCP' });
        setShowKey(res.data.key);
        const keysRes = await API.get('/api/developer/keys');
        setApiKeys(keysRes.data.keys || []);
      } catch { /* ignore */ }
    }
    setGenerating(false);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const activeKey = apiKeys.find(k => k.is_active);
  const currentKey = showKey || (activeKey ? `${activeKey.key_prefix}...` : null);
  const config = getAdminMcpConfig(platform, os, currentKey, 'https://www.getfray.ai/mcp');
  const magicPrompt = `I need to connect to Fray.ai for AI debate committees. Add this MCP server to my configuration:\n\nURL: https://www.getfray.ai/mcp\nAuthorization: Bearer ${currentKey || 'YOUR_API_KEY'}\n\nPlease add this to my MCP settings and then test the connection by running fray_test_connection.`;

  return (
    <div data-testid="admin-mcp-setup">
      <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-1">My MCP Connection</h3>
      <p className="text-[10px] text-[#A0A0A0] font-mono mb-4">
        Generate your personal MCP key to connect Claude Desktop, Claude Code, or other MCP clients.
        Your admin role ({user?.role}) grants access to admin MCP tools automatically.
      </p>

      {/* API Key Section */}
      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Key className="w-4 h-4 text-[#FF4D00]" />
          <span className="text-xs font-['Barlow_Condensed'] font-bold uppercase tracking-wider">Your API Key</span>
        </div>

        {showKey && (
          <div className="mb-3 p-3 bg-[#22C55E]/10 border border-[#22C55E]/30">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-[#22C55E] shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-[10px] text-[#22C55E] font-bold mb-1">Key Generated — Copy Now</p>
                <code className="text-xs font-mono text-[#22C55E] break-all block">{showKey}</code>
                <p className="text-[9px] text-[#A0A0A0] mt-1">This key will not be shown in full again.</p>
              </div>
              <button onClick={() => copyToClipboard(showKey, 'new-key')}
                className="px-2 py-1 text-[10px] font-mono bg-[#22C55E]/20 text-[#22C55E] border border-[#22C55E]/30">
                {copied === 'new-key' ? <><Check className="w-3 h-3 inline" /> Copied</> : <><Copy className="w-3 h-3 inline" /> Copy</>}
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 mb-3">
          <code className={`flex-1 px-3 py-2 text-xs font-mono bg-[#0A0A0A] border border-[#2A2A2A] ${currentKey ? 'text-[#F5F5F5]' : 'text-[#A0A0A0]'}`}>
            {currentKey || 'No active API key'}
          </code>
          {currentKey && (
            <button onClick={() => copyToClipboard(currentKey, 'key')}
              className="px-2 py-1.5 text-[10px] font-mono bg-[#2A2A2A] text-[#F5F5F5]">
              {copied === 'key' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            </button>
          )}
        </div>

        {activeKey && (
          <div className="flex items-center justify-between text-[9px] font-mono text-[#A0A0A0] mb-3 px-1">
            <span>Created {new Date(activeKey.created_at).toLocaleDateString()}</span>
            {activeKey.expires_at && <span className="text-[#F2A900]">Expires {new Date(activeKey.expires_at).toLocaleDateString()}</span>}
          </div>
        )}

        <div className="flex items-center gap-2">
          <select value={expiration} onChange={e => setExpiration(e.target.value)}
            className="px-2 py-1.5 bg-[#0A0A0A] border border-[#2A2A2A] text-xs font-mono text-[#F5F5F5] focus:border-[#FF4D00] focus:outline-none">
            <option value="7d">7 days</option>
            <option value="30d">30 days</option>
            <option value="90d">90 days</option>
            <option value="1y">1 year</option>
            <option value="never">Never expires</option>
          </select>
          <button onClick={generateKey} disabled={generating}
            className="px-3 py-1.5 bg-[#FF4D00] text-black text-xs font-bold disabled:opacity-50"
            data-testid="admin-mcp-generate-key">
            {generating ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <RefreshCw className="w-3 h-3 inline mr-1" />}
            {activeKey ? 'Regenerate Key' : 'Generate Key'}
          </button>
        </div>
      </div>

      {/* Magic Prompt */}
      <div className="p-4 bg-gradient-to-r from-[#FF4D00]/10 to-[#1A1A1A] border border-[#FF4D00]/30 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-['Barlow_Condensed'] font-bold">Skip the Steps — Prompt Your Agent</span>
        </div>
        <p className="text-[10px] text-[#A0A0A0] mb-2">Copy this prompt and paste it to Claude. It will set everything up.</p>
        <div className="relative">
          <pre className="text-[10px] font-mono text-[#E0E0E0] bg-[#0A0A0A] p-3 border border-[#2A2A2A] whitespace-pre-wrap max-h-28 overflow-y-auto">{magicPrompt}</pre>
          <button onClick={() => copyToClipboard(magicPrompt, 'prompt')}
            className="absolute top-1 right-1 px-2 py-1 text-[9px] font-mono bg-[#FF4D00] text-black">
            {copied === 'prompt' ? 'Copied' : 'Copy Prompt'}
          </button>
        </div>
      </div>

      {/* Platform Config */}
      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-['Barlow_Condensed'] font-bold uppercase tracking-wider">Platform Configuration</span>
        </div>

        {/* Platform tabs */}
        <div className="flex gap-2 mb-3 flex-wrap">
          {ADMIN_MCP_PLATFORMS.map(p => (
            <button key={p.id} onClick={() => setPlatform(p.id)}
              className={`px-3 py-1.5 text-[10px] font-mono border ${platform === p.id ? 'bg-[#FF4D00]/10 border-[#FF4D00] text-[#FF4D00]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#555]'}`}>
              {p.label}
            </button>
          ))}
        </div>

        {/* OS toggle for Claude Desktop */}
        {platform === 'claude-desktop' && (
          <div className="flex gap-2 mb-3">
            {['macos', 'windows'].map(o => (
              <button key={o} onClick={() => setOs(o)}
                className={`px-2 py-1 text-[9px] font-mono border ${os === o ? 'bg-[#00D4FF]/10 border-[#00D4FF] text-[#00D4FF]' : 'border-[#2A2A2A] text-[#A0A0A0]'}`}>
                {o === 'macos' ? 'macOS' : 'Windows'}
              </button>
            ))}
          </div>
        )}

        {/* Config snippet */}
        <div className="relative">
          <pre className="text-[10px] font-mono text-[#E0E0E0] bg-[#0A0A0A] p-3 border border-[#2A2A2A] whitespace-pre-wrap overflow-x-auto max-h-48">{config}</pre>
          <button onClick={() => copyToClipboard(config, 'config')}
            className="absolute top-1 right-1 px-2 py-1 text-[9px] font-mono bg-[#2A2A2A] text-[#F5F5F5]">
            {copied === 'config' ? <><Check className="w-3 h-3 inline" /> Copied</> : <><Copy className="w-3 h-3 inline" /> Copy</>}
          </button>
        </div>

        {platform === 'claude-code' && (
          <p className="text-[9px] text-[#A0A0A0] font-mono mt-2">Run this command in your terminal. The key is scoped to this project.</p>
        )}
        {platform !== 'claude-code' && (
          <p className="text-[9px] text-[#A0A0A0] font-mono mt-2">
            Paste this into your {platform === 'claude-desktop' ? (os === 'windows' ? '%APPDATA%\\Claude\\claude_desktop_config.json' : '~/Library/Application Support/Claude/claude_desktop_config.json') : platform === 'cursor' ? '.cursor/mcp.json' : '~/.codeium/windsurf/mcp_config.json'}
          </p>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   McpTab — MCP customer, tier tools, version history, and skill management (#323, #325)
   ═══════════════════════════════════════════════════════════════ */
function McpTab() {
  const { user } = useAuth();
  const [section, setSection] = useState('my-mcp');
  const [customers, setCustomers] = useState([]);
  const [versions, setVersions] = useState([]);
  const [skills, setSkills] = useState([]);
  const [tierTools, setTierTools] = useState([]);
  const [toolGroups, setToolGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [showRevokeModal, setShowRevokeModal] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [rollingBack, setRollingBack] = useState(null);
  const [savingTier, setSavingTier] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);
  // Setup templates state
  const [setupTemplates, setSetupTemplates] = useState({});
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateDraft, setTemplateDraft] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [previewKey, setPreviewKey] = useState('fray_xxxxxxxxxxxx');

  useEffect(() => { loadMcpData(); }, []);

  const loadMcpData = async () => {
    setLoading(true);
    try {
      const [custRes, verRes, skillRes, tierRes, tmplRes] = await Promise.all([
        API.get('/api/admin/mcp/customers').catch(() => ({ data: { customers: [] } })),
        API.get('/api/admin/mcp/versions').catch(() => ({ data: { versions: [] } })),
        API.get('/api/skills').catch(() => ({ data: { skills: [] } })),
        API.get('/api/admin/mcp/tier-tools').catch(() => ({ data: { tiers: [], groups: {} } })),
        API.get('/api/admin/mcp/setup-templates').catch(() => ({ data: { templates: {} } })),
      ]);
      setCustomers(custRes.data.customers || []);
      setVersions(verRes.data.versions || []);
      setSkills(skillRes.data.skills || []);
      setTierTools(tierRes.data.tiers || []);
      setToolGroups(tierRes.data.groups || {});
      setSetupTemplates(tmplRes.data.templates || {});
    } catch (err) {
      console.error('MCP load error:', err);
    }
    setLoading(false);
  };

  const revokeKey = async (userId) => {
    setRevoking(userId);
    try {
      await API.post('/api/admin/mcp/revoke', { user_id: userId, reason: revokeReason || 'Admin revocation' });
      setShowRevokeModal(null);
      setRevokeReason('');
      setStatusMsg({ type: 'success', text: 'Key revoked successfully' });
      await loadMcpData();
    } catch (err) {
      setStatusMsg({ type: 'error', text: err.response?.data?.detail || 'Revoke failed' });
    }
    setRevoking(null);
  };

  const exportAudit = async (userId) => {
    try {
      const res = await API.get(`/api/admin/mcp/audit-export?user_id=${userId}`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mcp-audit-${userId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Audit export failed' });
    }
  };

  const publishVersion = async (versionId) => {
    if (!window.confirm('Deploy this version to live? This will replace the current live version.')) return;
    setPublishing(versionId);
    try {
      const res = await API.post('/api/admin/mcp/publish', { version_id: versionId });
      setStatusMsg({ type: 'success', text: res.data.message });
      await loadMcpData();
    } catch (err) {
      setStatusMsg({ type: 'error', text: err.response?.data?.detail || 'Publish failed' });
    }
    setPublishing(null);
  };

  const rollbackVersion = async (versionId) => {
    if (!window.confirm('Rollback to this version? The current live version will be archived.')) return;
    setRollingBack(versionId);
    try {
      const res = await API.put(`/api/admin/mcp/rollback/${versionId}`);
      setStatusMsg({ type: 'success', text: res.data.message });
      await loadMcpData();
    } catch (err) {
      setStatusMsg({ type: 'error', text: err.response?.data?.detail || 'Rollback failed' });
    }
    setRollingBack(null);
  };

  const toggleSkillMcp = async (slug) => {
    try {
      const res = await API.put(`/api/admin/skills/${slug}/mcp-toggle`);
      setSkills(prev => prev.map(s => s.slug === slug ? { ...s, approved_for_mcp: res.data.approved_for_mcp } : s));
    } catch (err) {
      setStatusMsg({ type: 'error', text: err.response?.data?.detail || 'Toggle failed' });
    }
  };

  const updateTierAccess = async (tierSlug, mcpAccess) => {
    setSavingTier(tierSlug);
    try {
      await API.put('/api/admin/mcp/tier-tools', { tier_slug: tierSlug, mcp_access: mcpAccess });
      setStatusMsg({ type: 'success', text: `${tierSlug} tier updated to ${mcpAccess}` });
      await loadMcpData();
    } catch (err) {
      setStatusMsg({ type: 'error', text: err.response?.data?.detail || 'Update failed' });
    }
    setSavingTier(null);
  };

  const saveTemplate = async (key) => {
    setSavingTemplate(true);
    try {
      await API.put('/api/admin/mcp/setup-templates', { [key]: templateDraft });
      setSetupTemplates(prev => ({ ...prev, [key]: templateDraft }));
      setEditingTemplate(null);
      setStatusMsg({ type: 'success', text: `Template "${key}" saved` });
    } catch (err) {
      setStatusMsg({ type: 'error', text: err.response?.data?.detail || 'Save failed' });
    }
    setSavingTemplate(false);
  };

  const renderPreview = (template) => {
    if (!template) return '';
    return template
      .replace(/\{\{API_KEY\}\}/g, previewKey)
      .replace(/\{\{MCP_URL\}\}/g, setupTemplates.mcp_server_url || 'https://www.getfray.ai/mcp')
      .replace(/\{\{USER_NAME\}\}/g, 'Preview User');
  };

  if (loading) return <p className="text-[#A0A0A0] text-sm">Loading MCP data...</p>;

  return (
    <div data-testid="mcp-tab">
      {/* Status message */}
      {statusMsg && (
        <div className={`p-2 mb-4 text-xs border ${statusMsg.type === 'success' ? 'bg-[#22C55E]/10 border-[#22C55E]/30 text-[#22C55E]' : 'bg-[#EF4444]/10 border-[#EF4444]/30 text-[#EF4444]'}`}>
          {statusMsg.text}
          <button onClick={() => setStatusMsg(null)} className="ml-2 opacity-60 hover:opacity-100">&times;</button>
        </div>
      )}

      {/* Section tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { id: 'my-mcp', label: 'My MCP Connection', icon: Terminal },
          { id: 'customers', label: 'Customers', icon: Users },
          { id: 'tier-tools', label: 'Tool Access by Tier', icon: Shield },
          { id: 'server', label: 'Version History', icon: Server },
          { id: 'skills', label: 'Skill Visibility', icon: ToggleRight },
          { id: 'setup', label: 'Setup Content', icon: BookOpen },
          { id: 'models', label: 'Model Roster', icon: Cpu },
        ].map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            className={`px-3 py-1.5 text-xs font-mono border flex items-center gap-1.5 ${section === s.id ? 'bg-[#00D4FF]/10 border-[#00D4FF] text-[#00D4FF]' : 'border-[#2A2A2A] text-[#A0A0A0]'}`}
            data-testid={`mcp-section-${s.id}`}>
            <s.icon className="w-3 h-3" /> {s.label}
          </button>
        ))}
      </div>

      {/* My MCP Connection — admin self-service setup (#356) */}
      {section === 'my-mcp' && <AdminMcpSetup user={user} />}

      {/* Customer Management */}
      {section === 'customers' && (
        <div>
          <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-3">MCP Customers ({customers.length})</h3>
          {customers.length === 0 ? (
            <p className="text-[#A0A0A0] text-xs font-mono">No active MCP customers.</p>
          ) : (
            <div className="space-y-2">
              {customers.map((c, i) => (
                <div key={i} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium">{c.name || c.email}</span>
                    <div className="flex gap-3 mt-1 text-[10px] text-[#A0A0A0] font-mono">
                      <span>{c.email}</span>
                      <span>Plan: {c.plan || 'free'}</span>
                      <span className={c.key_active ? 'text-[#22C55E]' : 'text-[#EF4444]'}>
                        Key: {c.key_active ? 'Active' : 'Revoked'}
                      </span>
                      <span>Prefix: {c.key_prefix || '\u2014'}</span>
                      {c.key_last_used && <span>Last used: {new Date(c.key_last_used).toLocaleDateString()}</span>}
                      <span>Created: {new Date(c.key_created).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    <button onClick={() => exportAudit(c.id)}
                      className="px-2 py-0.5 text-[8px] font-mono border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#00D4FF] hover:border-[#00D4FF]/30">
                      <ScrollText className="w-3 h-3 inline mr-1" />Audit
                    </button>
                    {c.key_active && (
                      <button onClick={() => { setShowRevokeModal(c); setRevokeReason(''); }}
                        className="px-2 py-0.5 text-[8px] font-mono border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10">
                        <ShieldOff className="w-3 h-3 inline mr-1" />Revoke
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tool Access by Tier */}
      {section === 'tier-tools' && (
        <div>
          <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-1">Tool Access by Tier</h3>
          <p className="text-[10px] text-[#A0A0A0] font-mono mb-4">
            Configure which MCP feature group each tier receives. Changes take effect immediately.
          </p>

          <div className="bg-[#1A1A1A] border border-[#2A2A2A] overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#2A2A2A] bg-[#0F0F0F]">
                  <th className="text-left p-3 font-mono text-[#FF4D00]">Tier</th>
                  <th className="text-left p-3 text-[#A0A0A0]">Feature Group</th>
                  <th className="text-right p-3 font-mono text-[#A0A0A0]">Tools</th>
                </tr>
              </thead>
              <tbody>
                {tierTools.map(t => (
                  <tr key={t.slug} className="border-b border-[#2A2A2A] last:border-0">
                    <td className="p-3 font-mono text-[#F5F1E8] font-medium">{t.name}</td>
                    <td className="p-3">
                      <select
                        value={t.mcp_access || 'none'}
                        onChange={e => updateTierAccess(t.slug, e.target.value)}
                        disabled={savingTier === t.slug}
                        className="bg-[#0F0F0F] border border-[#2A2A2A] text-xs text-[#F5F1E8] px-2 py-1.5 focus:outline-none focus:border-[#00D4FF] disabled:opacity-50"
                      >
                        {Object.entries(toolGroups).map(([gKey, gVal]) => (
                          <option key={gKey} value={gKey}>
                            {gKey === 'none' ? 'None (no MCP access)' : `${gKey} (${gVal.tool_count} tools)`}
                          </option>
                        ))}
                      </select>
                      {savingTier === t.slug && <Loader2 className="w-3 h-3 inline ml-2 animate-spin text-[#00D4FF]" />}
                    </td>
                    <td className="p-3 text-right font-mono text-[#00D4FF]">{t.tool_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Group legend */}
          <div className="mt-4">
            <h4 className="text-xs font-semibold font-['Barlow_Condensed'] mb-2 text-[#A0A0A0]">Feature Group Reference</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(toolGroups).filter(([k]) => k !== 'none').map(([gKey, gVal]) => (
                <div key={gKey} className="p-2 bg-[#1A1A1A] border border-[#2A2A2A] text-left">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-mono text-[#F5F1E8]">{gKey}</span>
                    <span className="text-[9px] font-mono px-1.5 py-0.5 bg-[#0F0F0F] text-[#00D4FF]">{gVal.tool_count} tools</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Server Version History (upload removed — tools deploy with main app) */}
      {section === 'server' && (
        <div>
          <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-1">MCP Server Version History</h3>
          <p className="text-[10px] text-[#A0A0A0] font-mono mb-3">
            MCP tools deploy with the main app. Use version history for rollback only.
          </p>
          {versions.length === 0 ? (
            <p className="text-[#A0A0A0] text-xs font-mono">No versions recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {versions.map(v => (
                <div key={v.id} className={`p-3 bg-[#1A1A1A] border ${v.status === 'live' ? 'border-[#22C55E]/50' : 'border-[#2A2A2A]'} flex items-center justify-between`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono">{v.version}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 border font-mono ${
                        v.status === 'live' ? 'text-[#22C55E] border-[#22C55E]/30 bg-[#22C55E]/10' :
                        v.status === 'tested' ? 'text-[#00D4FF] border-[#00D4FF]/30' :
                        'text-[#A0A0A0] border-[#2A2A2A]'
                      }`}>{v.status?.toUpperCase()}</span>
                    </div>
                    <span className="text-[10px] text-[#A0A0A0] font-mono">
                      {new Date(v.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {v.status === 'tested' && (
                      <button onClick={() => publishVersion(v.id)} disabled={publishing === v.id}
                        className="px-2 py-0.5 text-[8px] font-mono border border-[#22C55E]/30 text-[#22C55E] hover:bg-[#22C55E]/10 disabled:opacity-50">
                        {publishing === v.id ? 'Publishing...' : 'Publish'}
                      </button>
                    )}
                    {v.status === 'archived' && (
                      <button onClick={() => rollbackVersion(v.id)} disabled={rollingBack === v.id}
                        className="px-2 py-0.5 text-[8px] font-mono border border-[#F2A900]/30 text-[#F2A900] hover:bg-[#F2A900]/10 disabled:opacity-50">
                        <ArrowDownCircle className="w-3 h-3 inline mr-1" />{rollingBack === v.id ? 'Rolling back...' : 'Rollback'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Skill MCP Visibility */}
      {section === 'skills' && (
        <div>
          <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-3">Skill MCP Visibility</h3>
          <p className="text-[10px] text-[#A0A0A0] font-mono mb-3">Toggle which skills are available through the MCP API.</p>
          <div className="space-y-1">
            {skills.map(s => (
              <div key={s.slug} className="p-2 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between">
                <div>
                  <span className="text-sm">{s.name || s.slug}</span>
                  <span className="text-[10px] text-[#A0A0A0] font-mono ml-2">{s.category}</span>
                </div>
                <button onClick={() => toggleSkillMcp(s.slug)}
                  className={`flex items-center gap-1 px-2 py-0.5 text-[8px] font-mono border ${s.approved_for_mcp ? 'text-[#22C55E] border-[#22C55E]/30' : 'text-[#A0A0A0] border-[#2A2A2A]'}`}>
                  {s.approved_for_mcp ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                  {s.approved_for_mcp ? 'MCP ON' : 'MCP OFF'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Setup Content — editable templates for customer MCP onboarding */}
      {section === 'setup' && (
        <div data-testid="mcp-setup-section">
          <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-1">Customer Setup Content</h3>
          <p className="text-[10px] text-[#A0A0A0] font-mono mb-4">
            Edit the templates customers see on the MCP setup page. Variables: {'{{API_KEY}}'}, {'{{MCP_URL}}'}, {'{{USER_NAME}}'}
          </p>

          {/* Preview key input */}
          <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] mb-4">
            <label className="text-[10px] text-[#A0A0A0] font-mono mb-1 block">Preview with API key:</label>
            <input type="text" value={previewKey} onChange={e => setPreviewKey(e.target.value)}
              className="w-full px-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#00D4FF]"
              placeholder="fray_xxxxxxxxxxxx" />
          </div>

          {/* Template cards */}
          <div className="space-y-4">
            {[
              { key: 'mcp_server_url', label: 'Server URL', rows: 1 },
              { key: 'mcp_prompt_template', label: 'Setup Prompt (Copy Prompt button)', rows: 10 },
              { key: 'mcp_verify_prompt', label: 'Verify Prompt (after restart)', rows: 6 },
              { key: 'mcp_config_claude_desktop', label: 'Claude Desktop Config', rows: 6 },
              { key: 'mcp_config_claude_code', label: 'Claude Code Command', rows: 2 },
              { key: 'mcp_config_cursor', label: 'Cursor Config', rows: 6 },
              { key: 'mcp_config_windsurf', label: 'Windsurf Config', rows: 6 },
            ].map(tmpl => (
              <div key={tmpl.key} className="bg-[#1A1A1A] border border-[#2A2A2A] overflow-hidden">
                <div className="flex items-center justify-between p-3 border-b border-[#2A2A2A]">
                  <span className="text-xs font-semibold font-['Barlow_Condensed']">{tmpl.label}</span>
                  <div className="flex gap-2">
                    {editingTemplate === tmpl.key ? (
                      <>
                        <button onClick={() => saveTemplate(tmpl.key)} disabled={savingTemplate}
                          className="px-2 py-0.5 text-[8px] font-mono bg-[#22C55E] text-black disabled:opacity-50">
                          {savingTemplate ? 'Saving...' : 'Save'}
                        </button>
                        <button onClick={() => setEditingTemplate(null)}
                          className="px-2 py-0.5 text-[8px] font-mono border border-[#2A2A2A] text-[#A0A0A0]">Cancel</button>
                      </>
                    ) : (
                      <button onClick={() => { setEditingTemplate(tmpl.key); setTemplateDraft(setupTemplates[tmpl.key] || ''); }}
                        className="px-2 py-0.5 text-[8px] font-mono border border-[#FF4D00]/30 text-[#FF4D00] hover:bg-[#FF4D00]/10">
                        <Pencil className="w-3 h-3 inline mr-1" />Edit
                      </button>
                    )}
                  </div>
                </div>
                <div className="p-3">
                  {editingTemplate === tmpl.key ? (
                    <textarea value={templateDraft} onChange={e => setTemplateDraft(e.target.value)}
                      rows={tmpl.rows}
                      className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#00D4FF]/30 text-xs font-mono text-[#F5F1E8] focus:outline-none resize-y" />
                  ) : (
                    <div>
                      <p className="text-[9px] text-[#A0A0A0] font-mono mb-1">TEMPLATE:</p>
                      <pre className="text-[10px] font-mono text-[#F5F1E8] bg-[#0F0F0F] p-2 border border-[#2A2A2A] whitespace-pre-wrap mb-2 max-h-32 overflow-y-auto">
                        {setupTemplates[tmpl.key] || '(not set)'}
                      </pre>
                      <p className="text-[9px] text-[#00D4FF] font-mono mb-1">PREVIEW:</p>
                      <pre className="text-[10px] font-mono text-[#00D4FF] bg-[#0F0F0F] p-2 border border-[#00D4FF]/20 whitespace-pre-wrap max-h-32 overflow-y-auto">
                        {renderPreview(setupTemplates[tmpl.key])}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Model Roster — sync + overview (#458) */}
      {section === 'models' && (
        <ModelRosterAdmin />
      )}

      {/* Revoke Modal */}
      {showRevokeModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" role="dialog" aria-label="Revoke MCP key">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md mx-4 bg-[#1A1A1A] border border-[#2A2A2A] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-[#2A2A2A]">
              <div>
                <h3 className="text-sm font-semibold font-['Barlow_Condensed'] text-[#EF4444]">Revoke MCP Key</h3>
                <p className="text-[10px] text-[#A0A0A0] font-mono mt-0.5">{showRevokeModal.email}</p>
              </div>
              <button onClick={() => setShowRevokeModal(null)} className="text-[#A0A0A0] hover:text-[#F5F5F5]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-[#A0A0A0]">This will immediately revoke all active API keys for this user. They will lose MCP access within 30 seconds.</p>
              <div>
                <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Reason (optional)</label>
                <input type="text" value={revokeReason} onChange={e => setRevokeReason(e.target.value)}
                  className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#EF4444]"
                  placeholder="e.g. Abuse detected" />
              </div>
              <button onClick={() => revokeKey(showRevokeModal.id)} disabled={revoking === showRevokeModal.id}
                className="w-full px-4 py-2.5 bg-[#EF4444] text-white text-sm font-medium disabled:opacity-50">
                {revoking === showRevokeModal.id ? 'Revoking...' : 'Revoke Key'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

